// Blog content as DATA, not components.
//
// Deliberate departure from form720-ui, where every post is its own .jsx file
// plus a route plus a hand-added entry in package.json's react-snap list. Here a
// post is one object in this array: no new component, no new route, no build
// config to touch. `BlogPost.jsx` renders any of them, and
// `scripts/generate-sitemap.mjs` picks them up automatically.
//
// This shape is the interim step to the DB-backed CMS in SEO_CMS_SPEC.md - the
// field names mirror `cms_page` so these rows import directly and the SEO team
// takes over without a developer. Keep it serialisable: no JSX, no imports of
// components, no functions.
//
// BODY BLOCKS: { type: "p" | "h2" | "h3" | "ul" | "ol" | "table" | "callout" | "quote" | "cta" }
//
// CALLS TO ACTION: a post may carry `cta: { heading, text, label, to }`. It is
// shown compact under the intro and in full as the closing panel (posts without
// one get the site default). A `{ type: "cta", heading, text, label, to }` body
// block places one mid-article. Filing-audience posts (fleets, preparers) should
// always have one, worded for the job the reader came to do. `to` is checked by
// scripts/check-internal-links.mjs like any other link.
//
// INLINE LINKS: write them in the text itself as [visible label](/path) — in
// paragraphs, list items, callouts, table cells, and FAQ answers. Not in h2/h3
// (a heading is also an anchor id and a table-of-contents label), and not in
// `title`, `excerpt`, `metaTitle`, or `metaDescription`, which are plain text.
// See src/seo/richText.js for the parser and src/seo/schema.js for how the
// markup is stripped back out before it reaches JSON-LD.
//
// Contextual in-body links are the ones that pass relevance — the "Related
// guides" cards at the foot of an article are navigation, not internal linking.
// `scripts/check-internal-links.mjs` fails the build if a link points at a route
// that does not exist, or if a post has fewer than two internal body links.
//
// IRS FACTS: every figure below traces to an already-verified source in this
// repo - the TY2026 rate tables in src/data/rates.js (Form 2290 page 2), the
// due-date chart in HowItWorks.jsx, and the FAQ answers sourced from the IRS
// Instructions for Form 2290 (Rev. July 2026). `sources` records which. Nothing
// here introduces a figure that wasn't already vetted and shipped in this app.
// Per form2290-api/CLAUDE.md: a plausible-looking number is a defect, not a
// draft. Do not add a rate, threshold, or penalty amount to this file without
// tracing it to one of the four IRS sources first.

import { RATES, WEIGHTS } from "./rates.js";
import { dueDateRows, nextDueDate } from "./taxPeriods.js";

const CATEGORY_ORDER = Object.keys(RATES);

// Rendered as the rate table in the weight-categories post. Built from the same
// module the filing wizard computes tax from, so the article can never quote a
// rate the product doesn't charge.
const rateTableRows = CATEGORY_ORDER.map((c) => [
  c,
  WEIGHTS[c],
  `$${RATES[c][0].toFixed(2)}`,
  `$${RATES[c][1].toFixed(2)}`,
]);

// Built from src/data/taxPeriods.js — the single source of truth. Never
// hand-copy deadlines into an article: this file used to carry its own literal
// copy of the table, which meant the blog and /how-it-works could disagree about
// a federal filing deadline. taxPeriods.js is transcribed from the IRS
// instructions and cross-checked at build time by scripts/check-tax-period.mjs.
const DUE_DATE_ROWS = dueDateRows().map((d) => [d.monthLabel, d.dueLabel]);

// The next deadline that hasn't passed, from the same IRS-transcribed table.
// Used where an article says "the next deadline is ..." - that line used to be
// a hardcoded "August 31, 2026" and was wrong from September on. Undefined once
// the period's last deadline has passed, and the callout is then left out.
const NEXT_DUE = nextDueDate();

// The tax period every post was last verified against (all figures and rules
// trace to the Rev. July 2026 IRS instructions). BlogPost shows "Checked for
// the 2026–2027 tax period" ONLY while this equals the current period in
// taxPeriods.js - so when CURRENT_TAX_YEAR rolls to TY2027 the label
// disappears until the posts are re-verified and this is bumped. A label that
// rolled forward on its own would be a stale claim, which is the competitors'
// failure ("Your 2024-25 Form 2290 is Expired!") this is meant to avoid.
export const CONTENT_VERIFIED_FOR = "TY2026";

export const CATEGORIES = {
  deadlines: "Deadlines & penalties",
  schedule1: "Schedule 1",
  amendments: "Amendments & corrections",
  credits: "Credits & refunds",
  basics: "Form 2290 basics",
  rates: "Rates & weight categories",
  fleets: "Fleets & tax preparers",
};

export const BLOG_POSTS = [
  // ─────────────────────────────────────────────────────────────────────────
  {
    slug: "form-2290-due-date-2026-2027",
    category: "deadlines",
    title: "Form 2290 Due Dates for 2026–2027: Every Deadline by First-Used Month",
    metaTitle: "Form 2290 Due Date 2026-2027 | Deadline by First-Used Month",
    metaDescription:
      "Form 2290 is due August 31, 2026 for trucks first used in July. See every deadline for the 2026-2027 tax period, by the month your vehicle first hit the road.",
    keywords: [
      "form 2290 due date",
      "form 2290 due date 2026",
      "hvut deadline 2026",
      "when is form 2290 due",
      "form 2290 deadline",
      "first used month form 2290",
    ],
    excerpt:
      "Form 2290 is not due on one fixed date for everyone. Your deadline depends entirely on the month your vehicle was first used on a public highway.",
    publishedAt: "2026-08-07",
    updatedAt: "2026-08-07",
    readingMinutes: 5,
    irsVerified: true,
    sources: "IRS Instructions for Form 2290 (Rev. July 2026) — When To File",
    body: [
      {
        type: "p",
        text: "Most people believe Form 2290 is due on August 31. That is true for the majority of filers, but it is not the rule. The actual rule is that Form 2290 is due by the last day of the month following the month in which the vehicle was first used on a public highway during the tax period.",
      },
      {
        type: "p",
        text: "August 31 is simply the deadline that applies to everyone whose truck was already running in July, which is the first month of the tax period — and that describes most of the fleet on the road. If you put a truck into service in November, your deadline is not August 31. It is December 31.",
      },
      { type: "h2", text: "The 2026–2027 tax period" },
      {
        type: "p",
        text: "The Form 2290 tax period runs from July 1, 2026 through June 30, 2027. It does not follow the calendar year, and it does not follow your business's fiscal year. Every vehicle on the road during that window is reported against that period — including [vehicles you expect to run 5,000 miles or less](/blog/suspended-vehicles-category-w), which are reported as suspended and owe no tax.",
      },
      ...(NEXT_DUE
        ? [
            {
              type: "callout",
              tone: "warn",
              text: `The next deadline is ${NEXT_DUE.dueLabel} — for any vehicle first used on a public highway in ${NEXT_DUE.monthLabel}. Find yours with the [Form 2290 due date calculator](/form-2290-due-date-calculator).`,
            },
          ]
        : []),
      { type: "h2", text: "Every due date for the 2026–2027 period" },
      {
        type: "p",
        text: "Find the month your vehicle was first used on a public highway in the left column. The right column is your filing deadline. Where the last day of the month falls on a weekend or a legal holiday, the deadline rolls to the next business day — those rolls are already applied below.",
      },
      {
        type: "table",
        headers: ["Month first used", "Form 2290 due by"],
        rows: DUE_DATE_ROWS,
      },
      { type: "h2", text: "What \"first used\" actually means" },
      {
        type: "p",
        text: "This is the single most misunderstood field on the return, and getting it wrong changes both your deadline and your tax.",
      },
      {
        type: "p",
        text: "First-used month is the month the vehicle was first driven on a public highway during this tax period. It is not the month you bought the truck, not the month you registered it, and not the month you started the business. A truck purchased in May but not driven on a public road until September has a first-used month of September.",
      },
      {
        type: "p",
        text: "For a vehicle you already owned and ran through the previous period, the first-used month is July — the first month of the new period — because that is when it was first used during this period.",
      },
      { type: "h2", text: "Why the first-used month changes your tax, not just your deadline" },
      {
        type: "p",
        text: "Form 2290 tax is prorated. A vehicle first used in July is taxed for the full 12-month period. A vehicle first used later pays only for the months remaining in the period — this is the [partial-period tax](/blog/form-2290-new-vehicle-partial-period).",
      },
      {
        type: "p",
        text: "So a truck first used in January is taxed for six months, not twelve. The annual figure it is prorated from is set by the vehicle's taxable gross weight — see the [full TY2026 rate table](/rates). Reporting the wrong first-used month means either overpaying the IRS or underpaying and owing the difference later.",
      },
      { type: "h2", text: "Filing early" },
      {
        type: "p",
        text: "The filing window for a tax period opens on July 1. You cannot file a return for a period before that period has begun, but you can [file as soon as it opens](/wizard/1) rather than waiting for the deadline.",
      },
      {
        type: "p",
        text: "There is a practical reason to file early: your [stamped Schedule 1](/blog/stamped-schedule-1-proof-of-payment) is what state DMVs require to register or renew a heavy vehicle. If your registration is due in August and you wait until August 31 to file, you have left yourself no margin. Filing in July means the Schedule 1 is in hand before you need it.",
      },
      { type: "h2", text: "If you miss the deadline" },
      {
        type: "p",
        text: "File as soon as you can. The IRS can charge a [penalty plus interest](/blog/form-2290-late-filing-penalties) on top of the tax owed for filing late or paying late, and the amount grows the longer the return sits unfiled. There is no version of waiting that improves the outcome.",
      },
      {
        type: "p",
        text: "What the IRS charges, how to request penalty relief, and what to do first are covered in [filing Form 2290 late](/blog/form-2290-late-filing-penalties). This guide stays on the deadlines themselves.",
      },
      {
        type: "p",
        text: "There is also a second consequence that has nothing to do with the IRS: without a current stamped Schedule 1, you cannot renew your state registration. For most operators that is the more immediate problem.",
      },
    ],
    faqs: [
      {
        q: "Is Form 2290 always due August 31?",
        a: "No. August 31 is the deadline for vehicles first used in July, which covers most filers. If your vehicle was first used in a later month, your deadline is the last day of the month following that first use.",
      },
      {
        q: "What is the Form 2290 deadline for the 2026–2027 tax period?",
        a: "For vehicles first used in July 2026, the deadline is August 31, 2026. The tax period runs July 1, 2026 through June 30, 2027, and later first-used months have later deadlines.",
      },
      {
        q: "Can I file Form 2290 before July?",
        a: "No. The filing window for a tax period opens on July 1. You can file any time from that date onward, and filing early is recommended if your state registration renewal is due soon.",
      },
      {
        q: "What happens if I file Form 2290 late?",
        a: "The IRS can charge a [penalty plus interest](/blog/form-2290-late-filing-penalties) on the tax owed. You also will not have a current stamped Schedule 1, which most state DMVs require before they will register or renew a heavy vehicle.",
      },
    ],
  },

  // ─────────────────────────────────────────────────────────────────────────
  {
    slug: "stamped-schedule-1-proof-of-payment",
    category: "schedule1",
    title: "Stamped Schedule 1: Your IRS Proof of Payment Explained",
    metaTitle: "Stamped Schedule 1 | Form 2290 Proof of Payment for DMV",
    metaDescription:
      "Your stamped Schedule 1 is the proof of payment every state DMV asks for. Learn what it is, how fast you get it, and what to do if you lose your copy.",
    keywords: [
      "stamped schedule 1",
      "schedule 1 form 2290",
      "form 2290 proof of payment",
      "schedule 1 for dmv registration",
      "irs stamped schedule 1",
      "2290 proof of payment",
    ],
    excerpt:
      "The stamped Schedule 1 is the entire practical point of filing Form 2290 — it is what your state DMV requires before it will register or renew a heavy vehicle.",
    publishedAt: "2026-08-07",
    updatedAt: "2026-08-07",
    readingMinutes: 5,
    irsVerified: true,
    sources: "IRS Instructions for Form 2290 (Rev. July 2026) — Schedule 1, proof of payment",
    body: [
      {
        type: "p",
        text: "Most people do not file Form 2290 because they want to pay the [Heavy Vehicle Use Tax](/blog/what-is-form-2290). They file because they need the stamped Schedule 1 — and without it, the truck does not get registered.",
      },
      { type: "h2", text: "What Schedule 1 actually is" },
      {
        type: "p",
        text: "Schedule 1 is part of Form 2290 itself. It lists the vehicle identification numbers you are reporting and the weight category each one falls into. When the IRS accepts your return, it stamps that schedule and returns it to you. That stamped copy is your receipt — the government's confirmation that this specific VIN has been reported and the tax paid.",
      },
      {
        type: "p",
        text: "The stamp is what makes it proof. An unstamped Schedule 1 is just a page of your return; it proves nothing on its own.",
      },
      { type: "h2", text: "Why you need it" },
      {
        type: "p",
        text: "Two situations require it:",
      },
      {
        type: "ul",
        items: [
          "State vehicle registration. State DMVs require proof that the Heavy Vehicle Use Tax has been paid before they will register or renew a heavy vehicle. This is the reason most operators file at all.",
          "Bringing a Canadian or Mexican-registered vehicle into the United States. Customs and Border Protection requires the same proof of payment.",
        ],
      },
      { type: "h2", text: "How fast you get it" },
      {
        type: "p",
        text: "This is where filing method matters enormously.",
      },
      {
        type: "p",
        text: "E-filed, the stamped Schedule 1 is typically available within seconds of IRS acceptance. You download it immediately and can hand it to the DMV the same day — [start a return](/wizard/1) and it lands in your account as soon as the IRS accepts it.",
      },
      {
        type: "p",
        text: "Filed on paper, the IRS mails the stamped Schedule 1 to the address on the return. That takes weeks. If your registration renewal is close, paper filing is not a realistic option.",
      },
      {
        type: "callout",
        tone: "info",
        text: "E-filing is mandatory if you are reporting and paying tax on [25 or more vehicles on a single return](/blog/bulk-form-2290-filing-for-fleets). [Suspended category W vehicles](/blog/suspended-vehicles-category-w) do not count toward that 25, because no tax is being paid on them.",
      },
      { type: "h2", text: "What the electronic stamp looks like" },
      {
        type: "p",
        text: "An e-filed Schedule 1 does not carry an ink stamp. It carries a digital watermark showing the IRS received and accepted the return. This is the accepted equivalent, and state DMVs are set up to recognise it.",
      },
      {
        type: "p",
        text: "If a clerk is unfamiliar with the electronic format, the watermark and the receipt date on the document are what identify it as IRS-accepted.",
      },
      { type: "h2", text: "If you lose your Schedule 1" },
      {
        type: "p",
        text: "You do not need to refile, and you should not — filing a duplicate return for the same VIN and period creates a problem rather than solving one.",
      },
      {
        type: "p",
        text: "If you e-filed, the copy is stored in your filing account and you can download it again at any time. That is one of the underrated advantages of e-filing: the document is not something you can misplace permanently.",
      },
      {
        type: "p",
        text: "If you filed on paper and lost the copy, you will need to contact the IRS to request another.",
      },
      { type: "h2", text: "Check it before you file" },
      {
        type: "p",
        text: "The VINs on your Schedule 1 come from what you entered. The IRS stamps what you submitted; it does not correct it for you. The [review step](/how-it-works) lists every VIN back to you before you transmit.",
      },
      {
        type: "p",
        text: "A single wrong character in a VIN produces a stamped Schedule 1 that does not match the vehicle, and the DMV will reject it. That is fixable — see our guide to [VIN corrections](/blog/form-2290-vin-correction) — but checking every VIN before you transmit is faster than fixing it afterwards.",
      },
    ],
    faqs: [
      {
        q: "What is a stamped Schedule 1?",
        a: "It is the page of Form 2290 listing your vehicle VINs and weight categories, stamped by the IRS after it accepts your return. It serves as your proof that the Heavy Vehicle Use Tax has been reported and paid.",
      },
      {
        q: "How long does it take to get a stamped Schedule 1?",
        a: "E-filed, it is typically available within seconds of IRS acceptance. Filed on paper, the IRS mails it to the address on the return, which takes considerably longer.",
      },
      {
        q: "Do state DMVs accept an electronic Schedule 1?",
        a: "Yes. An e-filed Schedule 1 carries a digital watermark showing IRS acceptance rather than an ink stamp, and state registration offices are set up to accept it as proof of payment.",
      },
      {
        q: "What if I lose my stamped Schedule 1?",
        a: "Do not refile. If you e-filed, download another copy from your filing account. If you filed on paper, contact the IRS to request a replacement.",
      },
    ],
  },

  // ─────────────────────────────────────────────────────────────────────────
  {
    slug: "form-2290-vin-correction",
    category: "amendments",
    title: "Form 2290 VIN Correction: How to Fix a Wrong VIN",
    metaTitle: "Form 2290 VIN Correction | Fix a Wrong VIN on Schedule 1",
    metaDescription:
      "Entered the wrong VIN on Form 2290? A VIN correction fixes it and gets you a corrected stamped Schedule 1. Here is when you need one and how it works.",
    keywords: [
      "form 2290 vin correction",
      "2290 vin correction",
      "wrong vin form 2290",
      "correct vin schedule 1",
      "vin correction irs 2290",
    ],
    excerpt:
      "A VIN is 17 characters and one wrong one makes your stamped Schedule 1 useless at the DMV. A VIN correction fixes it without refiling the whole return.",
    publishedAt: "2026-08-07",
    updatedAt: "2026-08-07",
    readingMinutes: 4,
    irsVerified: true,
    sources: "IRS Instructions for Form 2290 (Rev. July 2026) — VIN correction",
    body: [
      {
        type: "p",
        text: "A vehicle identification number is 17 characters. Transposing two of them, or reading an 8 as a B, produces a [stamped Schedule 1](/blog/stamped-schedule-1-proof-of-payment) that does not match your truck. The IRS stamps exactly what you submitted — it does not check the VIN against the vehicle.",
      },
      {
        type: "p",
        text: "The DMV does check. That is usually where the mistake surfaces, at the counter, when the registration is refused.",
      },
      { type: "h2", text: "What a VIN correction is" },
      {
        type: "p",
        text: "A VIN correction is a specific type of Form 2290 filing that exists solely to fix a wrong VIN on a return the IRS has already accepted. You are not refiling the return and not paying the tax again — the tax was already paid on that vehicle. You are correcting the identifier so the stamped Schedule 1 matches the truck.",
      },
      {
        type: "p",
        text: "The result is a new stamped Schedule 1 carrying the correct VIN, which is the document the DMV will accept.",
      },
      { type: "h2", text: "When you need one" },
      {
        type: "ul",
        items: [
          "The VIN on your accepted return has a typo — a wrong, missing, or transposed character.",
          "The DMV rejected your Schedule 1 because the VIN does not match the vehicle.",
          "You caught the error yourself after the return was accepted.",
        ],
      },
      { type: "h2", text: "When you need something else instead" },
      {
        type: "p",
        text: "A VIN correction is narrow. It fixes the VIN and nothing else. These situations need a different filing:",
      },
      {
        type: "ul",
        items: [
          "The taxable gross weight was wrong or has increased — that is a [gross weight increase amendment](/blog/form-2290-amendments), and additional tax is likely due.",
          "A [suspended vehicle](/blog/suspended-vehicles-category-w) went over its mileage limit — that is a [mileage exceeded amendment](/blog/form-2290-amendments).",
          "You reported the wrong vehicle entirely, not a mistyped VIN — that is a different problem, and the tax paid on the wrong vehicle may need a [credit claim](/blog/form-2290-credits-and-refunds).",
          "Your return was rejected rather than accepted — nothing needs correcting, because nothing was filed. Fix the VIN and transmit again.",
        ],
      },
      {
        type: "callout",
        tone: "info",
        text: "Rejected and accepted are different states. If the IRS rejected your return, there is no accepted filing to correct — you simply fix the error and retransmit.",
      },
      { type: "h2", text: "What you need to file one" },
      {
        type: "p",
        text: "The incorrect VIN as it appears on the accepted return, the correct VIN, and the details of the original filing — the business EIN, the tax period, and the first-used month. The correction has to point at the original return, so those details have to match it. You can [start a VIN correction](/wizard/1) directly — there is no need to refile the return.",
      },
      { type: "h2", text: "How to avoid needing one" },
      {
        type: "p",
        text: "VIN errors are almost entirely a data-entry problem, and they concentrate in predictable places:",
      },
      {
        type: "ul",
        items: [
          "Read the VIN off the vehicle or the title, not from memory or a previous year's paperwork.",
          "Watch the characters that look alike in print: 8 and B, 0 and D, 5 and S, 2 and Z. Note that VINs never contain the letters I, O, or Q — if you have typed one, it is wrong.",
          "Check the length. A heavy vehicle VIN is 17 characters. Sixteen or eighteen means something was dropped or doubled.",
          "Review the VIN list on the summary screen before transmitting, not after.",
        ],
      },
      {
        type: "p",
        text: "Two minutes of checking before you transmit avoids a correction, a second trip to the DMV, and the delay in between. If several VINs need fixing at once, [bulk VIN correction](/blog/bulk-form-2290-filing-for-fleets) handles the batch rather than one correction at a time.",
      },
    ],
    faqs: [
      {
        q: "Can I correct a VIN on Form 2290 after filing?",
        a: "Yes. A VIN correction is a specific Form 2290 filing that fixes a wrong VIN on an already-accepted return and produces a corrected stamped Schedule 1.",
      },
      {
        q: "Do I pay the tax again when I file a VIN correction?",
        a: "No. The tax was already paid on that vehicle. A VIN correction fixes the identifier, not the tax.",
      },
      {
        q: "What if my return was rejected rather than accepted?",
        a: "Then there is nothing to correct — no return was filed. Fix the VIN and transmit the return again.",
      },
      {
        q: "Can a VIN correction fix the weight category too?",
        a: "No. A VIN correction changes only the VIN. A wrong or increased taxable gross weight requires a gross weight increase amendment, which may carry additional tax.",
      },
    ],
  },

  // ─────────────────────────────────────────────────────────────────────────
  {
    slug: "form-2290-amendments",
    category: "amendments",
    title: "Form 2290 Amendments: Weight Increases and Exceeded Mileage",
    metaTitle: "Form 2290 Amendment | Weight Increase & Mileage Exceeded",
    metaDescription:
      "Two things trigger a Form 2290 amendment: a taxable gross weight increase, or a suspended vehicle exceeding its mileage limit. Here is how each one works.",
    keywords: [
      "form 2290 amendment",
      "2290 amendment",
      "taxable gross weight increase",
      "mileage exceeded amendment",
      "amend form 2290",
      "form 2290 weight increase",
    ],
    excerpt:
      "An amendment is not a correction. It reports something that changed after you filed — and in both cases, additional tax is due.",
    publishedAt: "2026-08-07",
    updatedAt: "2026-08-07",
    readingMinutes: 5,
    irsVerified: true,
    sources:
      "IRS Instructions for Form 2290 (Rev. July 2026) — amended returns, additional tax from increase in taxable gross weight, suspended vehicles exceeding the mileage use limit",
    body: [
      {
        type: "p",
        text: "People use \"amendment\" loosely to mean any change to a filed return. On Form 2290 it means something narrower and worth being precise about, because the wrong filing type will not accomplish what you need.",
      },
      {
        type: "p",
        text: "An amendment reports a change in circumstances that happened after you filed and that increases the tax owed. A correction fixes information that was wrong when you filed. Different filings, different outcomes.",
      },
      { type: "h2", text: "The two amendment types" },
      {
        type: "p",
        text: "Only two situations call for a Form 2290 amendment, and both involve additional tax:",
      },
      {
        type: "ul",
        items: [
          "The vehicle's taxable gross weight increased during the period, moving it into a higher category.",
          "A vehicle you reported as suspended exceeded its mileage use limit.",
        ],
      },
      {
        type: "p",
        text: "A wrong VIN is not an amendment — it is a [VIN correction](/blog/form-2290-vin-correction), and no additional tax is involved.",
      },
      { type: "h2", text: "Taxable gross weight increase" },
      {
        type: "p",
        text: "Taxable gross weight determines which [category a vehicle falls into](/blog/form-2290-weight-categories-and-rates) and therefore what it is taxed. If the weight increases during the period — typically because the vehicle is now carrying heavier loads or its configuration changed — and the increase moves it into a higher category, the difference in tax becomes due.",
      },
      {
        type: "p",
        text: "You report the new taxable gross weight and the month in which the increase occurred. The additional tax is the difference between what the higher category owes for the remaining months and what you already paid — the [TY2026 rate table](/rates) shows the annual figure for every category.",
      },
      {
        type: "callout",
        tone: "warn",
        text: "The amendment is due by the last day of the month following the month in which the taxable gross weight increased — not at the end of the tax period.",
      },
      { type: "h2", text: "Suspended vehicle exceeded the mileage limit" },
      {
        type: "p",
        text: "A vehicle expected to run 5,000 miles or less during the period — 7,500 miles or less for agricultural vehicles — can be [reported as suspended under category W](/blog/suspended-vehicles-category-w), with no tax due.",
      },
      {
        type: "p",
        text: "Suspension is a forecast, not a guarantee. If the vehicle later goes over that limit, the suspension no longer applies and the tax becomes due for the period.",
      },
      {
        type: "p",
        text: "You report the VIN of the vehicle that exceeded the limit and the month in which it happened. The tax is then calculated from that vehicle's weight category.",
      },
      {
        type: "callout",
        tone: "warn",
        text: "This amendment is due by the last day of the month following the month in which the mileage limit was exceeded.",
      },
      { type: "h2", text: "Which filing do I actually need?" },
      {
        type: "table",
        headers: ["Situation", "What to file"],
        rows: [
          ["Wrong VIN on an accepted return", "[VIN correction](/blog/form-2290-vin-correction)"],
          ["Taxable gross weight increased mid-period", "Gross weight increase amendment"],
          ["Suspended vehicle went over its mileage limit", "Mileage exceeded amendment"],
          ["Vehicle sold, destroyed, or stolen", "[Credit claim](/blog/form-2290-credits-and-refunds), not an amendment"],
          ["Vehicle ran under the mileage limit after you paid tax", "[Credit claim](/blog/form-2290-credits-and-refunds), not an amendment"],
          [
            "A new vehicle went into service after you filed",
            "[A new return for its own first-used month](/blog/form-2290-new-vehicle-partial-period)",
          ],
          ["The IRS rejected the return", "[Fix the error and retransmit](/wizard/1) — nothing to amend"],
        ],
      },
      { type: "h2", text: "What you get back" },
      {
        type: "p",
        text: "An accepted amendment produces a new [stamped Schedule 1](/blog/stamped-schedule-1-proof-of-payment) reflecting the change. Where the amendment moved a vehicle out of suspended status or into a higher weight category, that updated Schedule 1 is the current proof of payment for the vehicle.",
      },
      { type: "h2", text: "Do not ignore one" },
      {
        type: "p",
        text: "Both amendment types have deadlines tied to the month the change occurred — the same last-day-of-the-following-month rule that sets your [original filing deadline](/blog/form-2290-due-date-2026-2027) — and both carry tax that is genuinely owed. [Late filing and late payment](/blog/form-2290-late-filing-penalties) can both draw a penalty plus interest.",
      },
      {
        type: "p",
        text: "The mileage case is the one that catches people out, because nothing external prompts it. Nobody sends a notice when a suspended truck crosses 5,000 miles — tracking that is on the operator. It is worth checking the odometer against the limit periodically rather than discovering it at the end of the period. Fleets amending several vehicles at once can [upload the changes from a spreadsheet](/blog/bulk-form-2290-filing-for-fleets).",
      },
    ],
    faqs: [
      {
        q: "What is a Form 2290 amendment?",
        a: "A filing that reports a change after your original return was accepted: either the taxable gross weight increased into a higher category, or a suspended vehicle exceeded its mileage use limit. Both result in additional tax.",
      },
      {
        q: "When is a gross weight increase amendment due?",
        a: "By the last day of the month following the month in which the taxable gross weight increased.",
      },
      {
        q: "When is a mileage exceeded amendment due?",
        a: "By the last day of the month following the month in which the vehicle exceeded its mileage use limit — 5,000 miles, or 7,500 for agricultural vehicles.",
      },
      {
        q: "Is a wrong VIN fixed with an amendment?",
        a: "No. A wrong VIN is fixed with a VIN correction, which involves no additional tax. Amendments are only for weight increases and exceeded mileage.",
      },
    ],
  },

  // ─────────────────────────────────────────────────────────────────────────
  {
    slug: "suspended-vehicles-category-w",
    category: "basics",
    title: "Suspended Vehicles and Category W: The 5,000-Mile Rule",
    metaTitle: "Form 2290 Suspended Vehicles | Category W & 5,000-Mile Limit",
    metaDescription:
      "A truck running 5,000 miles or less can be reported as suspended under category W with no tax due — but it still has to be reported. Here is how it works.",
    keywords: [
      "form 2290 suspended vehicle",
      "category w form 2290",
      "5000 mile limit form 2290",
      "hvut mileage limit",
      "agricultural vehicle 7500 miles",
      "suspended vehicle 2290",
    ],
    excerpt:
      "Suspended does not mean exempt from filing. Category W vehicles owe no tax, but leaving them off the return entirely is a mistake.",
    publishedAt: "2026-08-07",
    updatedAt: "2026-08-07",
    readingMinutes: 4,
    irsVerified: true,
    sources:
      "IRS Instructions for Form 2290 (Rev. July 2026) — suspended vehicles, mileage use limit, agricultural vehicles",
    body: [
      {
        type: "p",
        text: "Not every heavy vehicle [owes the Heavy Vehicle Use Tax](/faq). A truck that barely runs during the period can be reported as suspended — category W — and owe nothing. But the word people latch onto is \"nothing,\" and that leads to the most common mistake in this whole area.",
      },
      {
        type: "callout",
        tone: "warn",
        text: "Suspended means no tax due. It does not mean no filing due. A category W vehicle still has to be [reported on Form 2290](/wizard/1).",
      },
      { type: "h2", text: "The mileage use limit" },
      {
        type: "p",
        text: "A vehicle expected to be used 5,000 miles or less on public highways during the tax period can be reported as suspended. For agricultural vehicles the limit is higher: 7,500 miles or less.",
      },
      {
        type: "p",
        text: "The limit applies to the whole tax period — July 1 through June 30 — not to a calendar year and not per month, and it is not prorated for a [vehicle that joins partway through](/blog/form-2290-new-vehicle-partial-period).",
      },
      { type: "h2", text: "It is a forecast, and you are accountable for it" },
      {
        type: "p",
        text: "You claim suspension at filing time based on what you expect the vehicle to do. Nobody knows in July exactly how many miles a truck will run by June.",
      },
      {
        type: "p",
        text: "If the vehicle stays under the limit, nothing further is required. If it goes over, the suspension no longer applies and the [tax becomes due for the period](/blog/form-2290-weight-categories-and-rates). That is reported through a [mileage exceeded amendment](/blog/form-2290-amendments), due by the [last day of the month following](/blog/form-2290-due-date-2026-2027) the month the limit was crossed.",
      },
      {
        type: "p",
        text: "Nothing prompts this. No notice arrives when a suspended truck crosses 5,000 miles — tracking it is on the operator. If you have vehicles running near the line, check the odometers periodically rather than at the end of the period.",
      },
      { type: "h2", text: "Agricultural vehicles" },
      {
        type: "p",
        text: "The 7,500-mile limit applies to agricultural vehicles specifically, not to any truck used on a farm. The classification carries its own conditions around the vehicle's use and registration, and it is worth confirming a vehicle genuinely qualifies before relying on the higher limit — the difference between the two limits is 2,500 miles, and getting it wrong means an amendment and unexpected tax.",
      },
      { type: "h2", text: "Category W and the 25-vehicle e-file threshold" },
      {
        type: "p",
        text: "E-filing is mandatory when you report and pay tax on [25 or more vehicles on a single return](/blog/bulk-form-2290-filing-for-fleets). Suspended vehicles do not count toward that 25, because no tax is being paid on them.",
      },
      {
        type: "p",
        text: "So a fleet with 20 taxable trucks and 10 suspended ones is at 20 for this purpose, not 30 — e-filing is not mandatory, though it is still by far the faster route to a stamped Schedule 1.",
      },
      { type: "h2", text: "Suspended vehicles appear on your Schedule 1" },
      {
        type: "p",
        text: "Because they are reported, category W vehicles are listed on your [stamped Schedule 1](/blog/stamped-schedule-1-proof-of-payment) alongside the taxable ones. That matters practically: the Schedule 1 is your proof of payment for state registration, and a suspended vehicle that was never reported will not appear on it — which can hold up the registration of a truck that owed no tax at all.",
      },
      { type: "h2", text: "If a suspended vehicle stays under the limit" },
      {
        type: "p",
        text: "Nothing more is owed. Separately, if you already paid tax on a vehicle that then ran 5,000 miles or less during the period — 7,500 for agricultural — you may be able to [claim a credit for the tax paid](/blog/form-2290-credits-and-refunds). That is a credit claim rather than an amendment, and it goes in the opposite direction: money back rather than tax due.",
      },
    ],
    faqs: [
      {
        q: "What is a suspended vehicle on Form 2290?",
        a: "A vehicle expected to run 5,000 miles or less on public highways during the tax period — 7,500 miles or less for agricultural vehicles. It is reported under category W and no tax is due.",
      },
      {
        q: "Do I still have to file if all my vehicles are suspended?",
        a: "Yes. Suspended means no tax due, not no filing due. Category W vehicles must still be reported on Form 2290, and they appear on your stamped Schedule 1.",
      },
      {
        q: "What happens if a suspended vehicle exceeds 5,000 miles?",
        a: "The suspension no longer applies and the tax becomes due for the period. You report it with a mileage exceeded amendment, due by the last day of the month following the month the limit was crossed.",
      },
      {
        q: "Do suspended vehicles count toward the 25-vehicle e-filing requirement?",
        a: "No. The mandatory e-file threshold counts vehicles you are reporting and paying tax on. Suspended vehicles carry no tax and do not count.",
      },
    ],
  },

  // ─────────────────────────────────────────────────────────────────────────
  {
    slug: "form-2290-credits-and-refunds",
    category: "credits",
    title: "Form 2290 Credits and Refunds: Getting Money Back",
    metaTitle: "Form 2290 Credits & Refunds | Sold, Destroyed & Low-Mileage",
    metaDescription:
      "Sold, destroyed, or barely drove a truck you already paid tax on? You may be able to claim a Form 2290 credit. Here is what qualifies and how to claim it.",
    keywords: [
      "form 2290 credit",
      "form 2290 refund",
      "sold vehicle credit 2290",
      "form 8849 schedule 6",
      "hvut refund",
      "destroyed vehicle credit form 2290",
    ],
    excerpt:
      "The Heavy Vehicle Use Tax is paid up front for the whole period. When a truck leaves service partway through, some of that tax may come back.",
    publishedAt: "2026-08-07",
    updatedAt: "2026-08-07",
    readingMinutes: 4,
    irsVerified: true,
    sources: "IRS Instructions for Form 2290 (Rev. July 2026) — credits, Form 8849 Schedule 6",
    body: [
      {
        type: "p",
        text: "[Form 2290 tax](/blog/what-is-form-2290) is paid up front for the full tax period. If a vehicle leaves service partway through — sold, destroyed, stolen — or turns out to have barely run, you have paid for months of road use that never happened.",
      },
      {
        type: "p",
        text: "That money is not automatically written off. It can often be recovered. This is also the part of Form 2290 that gets the thinnest coverage anywhere, which means it is the part most often left unclaimed.",
      },
      { type: "h2", text: "What qualifies" },
      {
        type: "p",
        text: "You may be able to claim a credit for tax already paid on a vehicle that was:",
      },
      {
        type: "ul",
        items: [
          "Destroyed, stolen, or sold before June 1 and not used for the remainder of the period.",
          "Used 5,000 miles or less during the period — 7,500 miles or less for agricultural vehicles — despite having had tax paid on it.",
        ],
      },
      {
        type: "callout",
        tone: "info",
        text: "The low-mileage credit is the one people miss. If you paid the tax rather than [claiming suspension](/blog/suspended-vehicles-category-w), and the truck then barely ran, the tax may be recoverable.",
      },
      { type: "h2", text: "The June 1 condition" },
      {
        type: "p",
        text: "For a sold, destroyed, or stolen vehicle, the event must have happened before June 1 and the vehicle must not have been used for the rest of the period. June 1 is near the end of the period, which runs to June 30, so this is not a narrow window — but it is a real condition and it is date-specific.",
      },
      {
        type: "p",
        text: "\"Not used for the remainder of the period\" is the other half. A vehicle sold in March that you kept driving until May does not meet the condition.",
      },
      { type: "h2", text: "Two ways to claim" },
      {
        type: "p",
        text: "There are two routes, and which one applies depends on whether you are filing a return anyway:",
      },
      {
        type: "ul",
        items: [
          "As a credit on Form 2290 — if you are [filing a return for the period](/wizard/1), the credit can be applied against the tax owed on that return. This is the simpler path when you have other vehicles to report.",
          "On Form 8849, Schedule 6 — used to claim a refund where there is no Form 2290 tax to offset it against. This is the route when the credit exceeds what you owe, or when you are not filing a 2290 at all.",
        ],
      },
      {
        type: "p",
        text: "A credit reduces what you pay now. A refund sends money back. Most operators with a mixed fleet end up using the credit route because they have tax to offset.",
      },
      { type: "h2", text: "What you need" },
      {
        type: "p",
        text: "A credit claim has to identify the specific vehicle and the specific event: the VIN, the [tax period](/blog/form-2290-due-date-2026-2027) the tax was paid for, the date of the sale, destruction, theft, or the end of the period for a low-mileage claim, and an explanation of why the credit is due.",
      },
      {
        type: "p",
        text: "Keep the supporting documentation — the bill of sale, the insurance or police report, the mileage records. You are not usually attaching it to the claim, but you are asserting something the IRS may ask you to substantiate.",
      },
      { type: "h2", text: "Credit or amendment?" },
      {
        type: "p",
        text: "These get confused constantly, and they run in opposite directions.",
      },
      {
        type: "table",
        headers: ["Situation", "Direction", "Filing"],
        rows: [
          ["Vehicle sold, destroyed, or stolen before June 1", "Money back", "Credit or Form 8849 Schedule 6"],
          ["Paid tax, vehicle ran 5,000 miles or less", "Money back", "Credit or Form 8849 Schedule 6"],
          ["Suspended vehicle exceeded its mileage limit", "Tax due", "[Mileage exceeded amendment](/blog/form-2290-amendments)"],
          ["Taxable gross weight increased", "Tax due", "[Gross weight increase amendment](/blog/form-2290-amendments)"],
        ],
      },
      { type: "h2", text: "Worth checking every year" },
      {
        type: "p",
        text: "Fleets turn over. Trucks get sold, wrecked, and parked, and the tax paid on them in July is easy to forget by the following June. Reviewing what left the fleet during the period before filing the next return is a habit that pays for itself — and the credit is claimed against the return you are about to file anyway. If you are [filing for a whole fleet](/blog/bulk-form-2290-filing-for-fleets), last year's vehicle list is where to start.",
      },
    ],
    faqs: [
      {
        q: "Can I get a refund on Form 2290 if I sell my truck?",
        a: "You may be able to claim a credit for tax paid on a vehicle sold before June 1 and not used for the remainder of the period. It can be claimed as a credit on Form 2290 or as a refund on Form 8849, Schedule 6.",
      },
      {
        q: "What is Form 8849 Schedule 6 used for?",
        a: "Claiming a refund of Heavy Vehicle Use Tax where there is no Form 2290 tax to offset the credit against — for example when the credit exceeds what you owe.",
      },
      {
        q: "Can I claim a credit if my truck barely ran?",
        a: "If you paid tax on a vehicle that was then used 5,000 miles or less during the period — 7,500 for agricultural vehicles — you may be able to claim a credit for the tax paid.",
      },
      {
        q: "Is a credit the same as an amendment?",
        a: "No, they run in opposite directions. A credit recovers tax you overpaid. An amendment reports a change that means you owe more.",
      },
    ],
  },

  // ─────────────────────────────────────────────────────────────────────────
  {
    slug: "form-2290-weight-categories-and-rates",
    category: "rates",
    title: "Form 2290 Weight Categories and Tax Rates for 2026–2027",
    metaTitle: "Form 2290 Weight Categories & Rates TY2026 | Full Table",
    metaDescription:
      "The complete Form 2290 rate table for 2026-2027: all 21 weight categories from 55,000 lbs up, standard and logging rates, plus suspended category W.",
    keywords: [
      "form 2290 weight categories",
      "form 2290 tax rates 2026",
      "taxable gross weight",
      "hvut rate table",
      "logging vehicle rate",
      "form 2290 category a",
    ],
    excerpt:
      "Your Form 2290 tax is set by one thing: taxable gross weight. Here is the full rate table, and how to work out which category a vehicle falls into.",
    publishedAt: "2026-08-07",
    updatedAt: "2026-08-07",
    readingMinutes: 5,
    irsVerified: true,
    sources:
      "IRS Form 2290 (Rev. July 2026) page 2 annual rate table; Instructions for Form 2290 — determining taxable gross weight",
    body: [
      {
        type: "p",
        text: "Form 2290 tax is not negotiable and not estimated. It is set by the vehicle's taxable gross weight and the number of months it is used during the period. Get the weight category right and the tax follows automatically.",
      },
      { type: "h2", text: "What taxable gross weight means" },
      {
        type: "p",
        text: "Taxable gross weight is not the weight of the empty truck, and it is not what the vehicle happens to weigh on a given day. It is the total of:",
      },
      {
        type: "ul",
        items: [
          "The actual unloaded weight of the vehicle fully equipped for service.",
          "The actual unloaded weight of any trailers or semitrailers fully equipped for service, customarily used in combination with the vehicle.",
          "The weight of the maximum load customarily carried on the vehicle and on those trailers or semitrailers.",
        ],
      },
      {
        type: "p",
        text: "The phrase doing the work is \"customarily carried.\" This is about how the vehicle is normally operated, not its heaviest-ever load and not its lightest.",
      },
      { type: "h2", text: "The 55,000 lb threshold" },
      {
        type: "p",
        text: "The Heavy Vehicle Use Tax applies to highway motor vehicles with a taxable gross weight of 55,000 pounds or more. Below that, the vehicle is not taxable — which is why vans, pickups, and panel trucks generally fall outside it entirely.",
      },
      {
        type: "p",
        text: "Category A starts exactly at 55,000 lb. Each subsequent category covers a 1,000 lb band, up to category V for anything over 75,000 lb.",
      },
      { type: "h2", text: "The full 2026–2027 rate table" },
      {
        type: "p",
        text: "These are the annual rates for a vehicle used the full 12-month period. A vehicle [first used after July](/blog/form-2290-due-date-2026-2027) pays a prorated partial-period amount instead. The same table is on the [weight categories and rates page](/rates).",
      },
      {
        type: "table",
        headers: ["Category", "Taxable gross weight (lb)", "Standard (annual)", "Logging (annual)"],
        rows: rateTableRows,
      },
      { type: "h2", text: "Logging vehicles pay less" },
      {
        type: "p",
        text: "A vehicle used exclusively to transport products harvested from a forested site, and registered as a logging vehicle under state law, is taxed at a reduced rate in every weight category — the right-hand column above.",
      },
      {
        type: "p",
        text: "Both conditions matter. Exclusive use for forest products, and the state registration. A truck that hauls logs sometimes does not qualify.",
      },
      { type: "h2", text: "Category W: suspended" },
      {
        type: "p",
        text: "There is a twenty-second category that carries no rate. Category W is for [suspended vehicles](/blog/suspended-vehicles-category-w) — those expected to run 5,000 miles or less during the period, or 7,500 miles or less for agricultural vehicles. No tax is due, but the vehicle still has to be reported and it still appears on your [Schedule 1](/blog/stamped-schedule-1-proof-of-payment).",
      },
      { type: "h2", text: "Partial-period tax" },
      {
        type: "p",
        text: "The table above is the full-period tax. [A vehicle first used in a later month](/blog/form-2290-new-vehicle-partial-period) is taxed only for the months remaining in the period, so a truck first used in January is taxed for six months rather than twelve.",
      },
      {
        type: "p",
        text: "This is why the first-used month matters as much as the weight category. Two identical trucks in the same weight category can owe very different amounts if one started in July and the other in March. [Start a return](/wizard/1) and the partial-period amount is figured from the first-used month you enter.",
      },
      { type: "h2", text: "Getting the category wrong" },
      {
        type: "p",
        text: "Understating the weight underpays the tax, and if the weight is later corrected upward the difference becomes due. If the taxable gross weight genuinely increases mid-period into a higher category, that is reported through a [gross weight increase amendment](/blog/form-2290-amendments), with the additional tax due by the last day of the following month.",
      },
      {
        type: "p",
        text: "Overstating it simply means paying more than you owed. The IRS will not flag that for you.",
      },
    ],
    faqs: [
      {
        q: "What is taxable gross weight on Form 2290?",
        a: "The total of the unloaded weight of the vehicle fully equipped for service, the unloaded weight of any trailers customarily used with it, and the maximum load customarily carried on that combination.",
      },
      {
        q: "What is the minimum weight for Form 2290?",
        a: "55,000 pounds. Vehicles with a taxable gross weight below that are not subject to the Heavy Vehicle Use Tax, which is why most vans, pickups, and panel trucks are not taxable.",
      },
      {
        q: "How many weight categories does Form 2290 have?",
        a: "Twenty-one taxable categories, A through V, covering 55,000 lb up to over 75,000 lb — plus category W for suspended vehicles, which carries no tax.",
      },
      {
        q: "Why do logging vehicles pay a lower rate?",
        a: "A vehicle used exclusively to transport products harvested from a forested site, and registered as a logging vehicle under state law, is taxed at a reduced rate in every weight category.",
      },
    ],
  },
  // ─────────────────────────────────────────────────────────────────────────
  {
    slug: "bulk-form-2290-filing-for-fleets",
    category: "fleets",
    title: "Bulk Form 2290 Filing for Fleets and Tax Preparers",
    metaTitle: "Bulk Form 2290 Filing | Upload a Fleet From Excel",
    metaDescription:
      "Filing 2290 for a fleet? Import vehicles from an Excel template instead of typing each VIN. Bulk upload works for filings, VIN corrections, and amendments.",
    keywords: [
      "bulk 2290 filing",
      "form 2290 bulk upload",
      "file form 2290 for multiple vehicles",
      "form 2290 for fleets",
      "form 2290 for tax preparers",
      "2290 excel upload",
    ],
    excerpt:
      "Typing 200 VINs by hand is where fleet filings go wrong. Bulk upload turns the vehicle list into a spreadsheet import — and it covers corrections and amendments too.",
    publishedAt: "2026-08-07",
    updatedAt: "2026-08-07",
    readingMinutes: 5,
    irsVerified: true,
    sources:
      "IRS Instructions for Form 2290 (Rev. July 2026) — electronic filing requirement for 25 or more vehicles",
    body: [
      {
        type: "p",
        text: "For a single truck, [Form 2290](/blog/what-is-form-2290) is a short form. For a fleet, the form is not the hard part — the data entry is. Two hundred vehicles means two hundred 17-character VINs, each with a weight category, each with a logging or suspended flag, every one of them typed by hand.",
      },
      {
        type: "p",
        text: "That is where fleet filings go wrong. Not in the tax, in the transcription.",
      },
      { type: "h2", text: "E-filing is mandatory at 25 vehicles" },
      {
        type: "p",
        text: "If you are reporting and paying tax on 25 or more vehicles on a single return, e-filing is required — it is not a preference.",
      },
      {
        type: "p",
        text: "One detail that catches people out: [suspended category W vehicles](/blog/suspended-vehicles-category-w) do not count toward that 25, because no tax is being paid on them. A fleet with 20 taxable trucks and 10 suspended ones is at 20 for this purpose, not 30.",
      },
      { type: "h2", text: "How bulk upload works" },
      {
        type: "p",
        text: "Rather than adding vehicles one at a time, you work from a spreadsheet:",
      },
      {
        type: "ol",
        items: [
          "Download the Excel template. It comes with the column headers already set up, an Instructions sheet documenting every column with an example, and dropdowns on the fields that have fixed options — weight category, logging, suspended.",
          "Fill in one row per vehicle. Because the dropdowns are applied to the whole column up front, you can use Excel's fill handle or select-and-Ctrl+Enter to set the same weight category across fifty rows at once instead of picking it fifty times.",
          "[Upload the file](/wizard/1). Rows are validated on import and problems are reported per row, so a single bad VIN doesn't sink the whole file.",
        ],
      },
      {
        type: "callout",
        tone: "info",
        text: "Up to 500 vehicles per file. The template is an .xlsx and uploads accept .xlsx or .xls.",
      },
      { type: "h2", text: "It is not just for new filings" },
      {
        type: "p",
        text: "This is the part most fleet operators do not expect, and it is where the time actually goes. The same bulk import works for:",
      },
      {
        type: "ul",
        items: [
          "Original filings — the full vehicle list for the period.",
          "[VIN corrections](/blog/form-2290-vin-correction) — fixing multiple mistyped VINs at once rather than one correction at a time.",
          "[Taxable gross weight increase amendments](/blog/form-2290-amendments).",
          "Amended returns.",
        ],
      },
      {
        type: "p",
        text: "If a spreadsheet error put the same wrong character into a batch of VINs, correcting them one by one is the slow, error-prone path. Bulk correction handles the batch.",
      },
      { type: "h2", text: "Getting the spreadsheet right" },
      {
        type: "p",
        text: "The import is only as good as the file. A few things worth checking before you upload:",
      },
      {
        type: "ul",
        items: [
          "VIN length. A heavy vehicle VIN is 17 characters. Excel will happily hold 16 or 18.",
          "Excel dropping leading zeros or reformatting a VIN as a number or a date. Format VIN columns as Text before pasting.",
          "Look-alike characters: 8 and B, 0 and D, 5 and S, 2 and Z. VINs never contain I, O, or Q — if one appears, the row is wrong.",
          "The [weight category](/blog/form-2290-weight-categories-and-rates) must reflect taxable gross weight — the vehicle, its customary trailers, and the maximum load customarily carried — not the empty weight.",
          "Mark suspended vehicles as suspended. They still have to be reported and they still appear on your Schedule 1.",
        ],
      },
      { type: "h2", text: "One Schedule 1 for the fleet" },
      {
        type: "p",
        text: "An accepted return produces a single [stamped Schedule 1](/blog/stamped-schedule-1-proof-of-payment) listing every VIN on it — taxable and suspended alike. That one document is the proof of payment your registrations depend on, so it is worth checking the VIN list carefully before transmitting rather than discovering an error at the DMV counter.",
      },
      { type: "h2", text: "For tax preparers" },
      {
        type: "p",
        text: "If you file on behalf of clients, the same import applies per business. Each business keeps its own vehicles, returns, and filing history, so last year's vehicle list is available as a starting point rather than something to retype every July.",
      },
      {
        type: "p",
        text: "Given the season is compressed into July and August, the difference between typing and importing is most of the work. See [pricing by vehicle count](/pricing) for what a fleet return costs.",
      },
    ],
    faqs: [
      {
        q: "Do I have to e-file Form 2290 for a fleet?",
        a: "Yes, if you are reporting and paying tax on 25 or more vehicles on a single return. Suspended category W vehicles do not count toward that threshold because no tax is paid on them.",
      },
      {
        q: "How many vehicles can I upload at once?",
        a: "Up to 500 per file. Download the Excel template, fill in one row per vehicle, and upload it as .xlsx or .xls.",
      },
      {
        q: "Can I bulk upload VIN corrections and amendments?",
        a: "Yes. The same import works for original filings, VIN corrections, taxable gross weight increase amendments, and amended returns.",
      },
      {
        q: "Do I get one Schedule 1 or one per vehicle?",
        a: "One stamped Schedule 1 for the return, listing every VIN reported on it, including suspended vehicles.",
      },
    ],
  },
  // ─────────────────────────────────────────────────────────────────────────
  // CANONICAL TARGET: informational "what is form 2290" / "heavy vehicle use
  // tax". It does NOT compete with the homepage, which owns the transactional
  // "file form 2290 online", or with /faq, which owns the question-list intent.
  // Three URLs chasing one query is how a small site splits its own authority -
  // every article below states the query it owns before it states anything else.
  {
    slug: "what-is-form-2290",
    category: "basics",
    title: "What Is Form 2290 in Trucking? The IRS Heavy Vehicle Use Tax, and How to File It",
    metaTitle: "What Is Form 2290? IRS Heavy Use Tax for Trucks, Explained",
    metaDescription:
      "Form 2290 is the IRS return for the Heavy Vehicle Use Tax on trucks of 55,000 lbs or more. Who files, what it costs, when it's due, and how to e-file it today.",
    keywords: [
      "what is form 2290",
      "what is 2290 in trucking",
      "what is a 2290 form",
      "irs heavy use tax",
      "heavy vehicle use tax",
      "heavy highway vehicle use tax",
      "hvut",
      "irs form 2290",
    ],
    excerpt:
      "Form 2290 is the IRS return for the Heavy Vehicle Use Tax. If you run a truck at 55,000 lbs or more, it is the form standing between you and a license plate — and it can be filed online in one sitting.",
    // Rewritten 2026-10-07: Search Console showed this page collecting the most
    // impressions on the site for "what is 2290 in trucking", "irs heavy use
    // tax" and "heavy vehicle tax" at an average position of ~85. Added a
    // direct answer up top, a how-to-file section, and a filing call to action.
    // Every figure is unchanged in source: RATES (Form 2290 p.2) and i2290.
    cta: {
      heading: "Ready to file your Form 2290?",
      text: "E-file in one sitting. The tax is figured from the IRS rate tables, and your stamped Schedule 1 comes back once the IRS accepts the return.",
      label: "Start your Form 2290",
      to: "/wizard/1",
    },
    publishedAt: "2026-08-14",
    updatedAt: "2026-10-07",
    readingMinutes: 7,
    irsVerified: true,
    sources:
      "IRS Instructions for Form 2290 (Rev. July 2026) — Purpose of Form, Who Must File, When To File, Getting Started, taxable gross weight, Schedule 1; Form 2290 page 2 tax computation table",
    body: [
      {
        type: "callout",
        tone: "info",
        text: `The short answer: Form 2290 is the IRS return for the Heavy Vehicle Use Tax (HVUT), an annual federal tax on trucks and other highway vehicles with a taxable gross weight of 55,000 pounds or more. The tax period runs July 1 to June 30, the tax tops out at $${RATES.V[0].toFixed(2)} per vehicle for a full period, and the accepted return gives you a stamped Schedule 1, the proof of payment your state needs to register the truck.`,
      },
      {
        type: "p",
        text: "Form 2290 is the federal return that reports the Heavy Vehicle Use Tax — an annual tax on heavy trucks that use public highways. If the truck is registered in your name and it is heavy enough, filing is not optional.",
      },
      {
        type: "p",
        text: "Most operators meet the form for a practical reason rather than a tax one. State DMVs will not register or renew a heavy vehicle without proof the tax has been reported, and that proof only exists once the return has been filed and accepted.",
      },
      { type: "h2", text: "What the tax actually is" },
      {
        type: "p",
        text: "The Heavy Vehicle Use Tax is a federal tax on the use of public highways by heavy vehicles. It is charged once for a tax period rather than per trip or per mile, and it is paid up front for the whole period rather than billed at the end.",
      },
      {
        type: "p",
        text: "That up-front structure is why so much of Form 2290 is about getting the details right the first time. You are paying for months that have not happened yet, based on a weight category and a first-used month you declare.",
      },
      { type: "h2", text: "Who has to file" },
      {
        type: "p",
        text: "The obligation follows registration, not ownership and not who drives. If a taxable heavy vehicle is registered in your name — as an individual, an LLC, a corporation, or a partnership — you are the one who files.",
      },
      {
        type: "ul",
        items: [
          "Owner-operators with a single truck.",
          "Fleets, however many vehicles — and [fleets can file the whole list from a spreadsheet](/blog/bulk-form-2290-filing-for-fleets) rather than one truck at a time.",
          "Tax preparers filing on behalf of clients.",
          "Anyone bringing a Canadian or Mexican-registered heavy vehicle into the United States, who needs the same proof of payment at the border.",
        ],
      },
      {
        type: "p",
        text: "You also need an EIN. The IRS will not accept a Form 2290 filed under a Social Security number, and a newly issued EIN takes time to become usable for e-filing — which is a reason to sort it out well before a deadline rather than on the day.",
      },
      { type: "h2", text: "What makes a vehicle taxable" },
      {
        type: "p",
        text: "A vehicle is taxable if it is a highway motor vehicle with a taxable gross weight of 55,000 pounds or more. Below that threshold the tax does not apply, which is why vans, pickups, and panel trucks generally fall outside it entirely.",
      },
      {
        type: "p",
        text: "Taxable gross weight is not the empty weight of the truck. It is the vehicle fully equipped for service, plus any trailers customarily used with it, plus the maximum load customarily carried on that combination. The [weight categories and rate table](/blog/form-2290-weight-categories-and-rates) set out every band, and the same table is on the [rates page](/rates).",
      },
      { type: "h2", text: "The tax period is not the calendar year" },
      {
        type: "p",
        text: "The Form 2290 tax period runs from July 1 to June 30. It does not follow the calendar year and it does not follow your business's fiscal year.",
      },
      {
        type: "p",
        text: "Nor is there a single deadline for everyone. Your return is due by the last day of the month following the month the vehicle was first used on a public highway during the period — so a truck already running in July is due by August 31, and a truck first used in November is due by December 31. The [full due-date chart](/blog/form-2290-due-date-2026-2027) lists every month.",
      },
      { type: "h2", text: "What it costs" },
      {
        type: "p",
        text: "The tax is set by two things: the vehicle's weight category, and how many months of the period it is used for. A vehicle running the full period pays the annual rate for its category. A vehicle [first used partway through the period](/blog/form-2290-new-vehicle-partial-period) pays a prorated partial-period amount instead.",
      },
      {
        type: "p",
        text: "Logging vehicles — used exclusively to haul products harvested from a forested site and registered as such under state law — are taxed at a reduced rate in every category.",
      },
      { type: "h2", text: "What you get for filing" },
      {
        type: "p",
        text: "A [stamped Schedule 1](/blog/stamped-schedule-1-proof-of-payment). It lists the VINs you reported and carries the IRS stamp confirming the return was accepted, and it is the document your state DMV asks for at registration.",
      },
      {
        type: "callout",
        tone: "info",
        text: "E-filed, the stamped Schedule 1 typically arrives within seconds of IRS acceptance. Filed on paper, the IRS mails it, which takes weeks — a real problem if a registration renewal is close.",
      },
      { type: "h2", text: "You can owe nothing and still have to file" },
      {
        type: "p",
        text: "A vehicle expected to run 5,000 miles or less during the period — 7,500 for agricultural vehicles — can be reported as [suspended under category W](/blog/suspended-vehicles-category-w), with no tax due.",
      },
      {
        type: "p",
        text: "Suspended means no tax, not no filing. The vehicle still has to be reported, and it still appears on your Schedule 1 — which matters, because a truck missing from the Schedule 1 can hold up a registration even though it owed nothing.",
      },
      { type: "h2", text: "What can happen after you file" },
      {
        type: "p",
        text: "Three things change a return after the fact, and they are genuinely different filings:",
      },
      {
        type: "table",
        headers: ["What changed", "What you file"],
        rows: [
          ["A VIN was mistyped", "[VIN correction](/blog/form-2290-vin-correction)"],
          [
            "Weight increased, or a suspended vehicle went over its mileage limit",
            "[Amendment](/blog/form-2290-amendments) — additional tax due",
          ],
          [
            "Vehicle sold, destroyed, stolen, or barely driven",
            "[Credit or refund claim](/blog/form-2290-credits-and-refunds) — money back",
          ],
        ],
      },
      { type: "h2", text: "Paper or e-file" },
      {
        type: "p",
        text: "E-filing is mandatory if you are reporting and paying tax on 25 or more vehicles on a single return. Below that it is a choice, but the difference in how fast the stamped Schedule 1 comes back makes it a fairly one-sided one.",
      },
      { type: "h2", text: "How to file Form 2290 online" },
      {
        type: "p",
        text: "The IRS asks you to have three things ready before you start: your EIN, the VIN of each vehicle, and each vehicle's taxable gross weight. With those in hand, an e-filed return takes one sitting:",
      },
      {
        type: "ol",
        items: [
          "Enter your business details — the legal name, address, and EIN the IRS has on file. A Social Security number can't be used.",
          "Choose the return type: an original return, an [amendment](/blog/form-2290-amendments), or a [VIN correction](/blog/form-2290-vin-correction).",
          "Add each truck with its VIN, weight category, and first-used month. A fleet can [import the vehicle list from a spreadsheet](/blog/bulk-form-2290-filing-for-fleets) instead of typing it.",
          "Claim any [credit for a truck sold, destroyed, or stolen](/blog/form-2290-credit-sold-destroyed-stolen-truck) since your last return.",
          "Review the tax, which is figured from the IRS rate tables, then pay by electronic funds withdrawal, EFTPS, or credit/debit card.",
          "E-sign and transmit. Once the IRS accepts the return, your stamped Schedule 1 is ready to download.",
        ],
      },
      {
        type: "p",
        text: "Filing for clients rather than your own trucks? The same return carries a paid-preparer section — see [Form 2290 for tax preparers](/blog/form-2290-for-tax-preparers).",
      },
      {
        type: "cta",
        heading: "File Form 2290 now",
        text: "Start with your EIN, VINs, and weights. You can save and come back before you submit.",
        label: "Start filing",
        to: "/wizard/1",
      },
    ],
    faqs: [
      {
        q: "What is Form 2290 used for?",
        a: "It reports and pays the federal Heavy Vehicle Use Tax on highway vehicles with a taxable gross weight of 55,000 pounds or more, and it produces the stamped Schedule 1 that state DMVs require before registering a heavy vehicle.",
      },
      {
        q: "Who has to file Form 2290?",
        a: "Anyone in whose name a taxable heavy vehicle is registered — owner-operators, fleets, corporations, and preparers filing for clients. An EIN is required; the IRS will not accept a Form 2290 filed under a Social Security number.",
      },
      {
        q: "How much does Form 2290 cost?",
        a: "The tax itself depends on the vehicle's taxable gross weight category and how many months of the July-to-June period it is used. A vehicle first used partway through the period pays a prorated partial-period amount rather than the full annual rate.",
      },
      {
        q: "Do I have to file if my truck barely gets driven?",
        a: "Yes. A vehicle expected to run 5,000 miles or less during the period (7,500 for agricultural vehicles) is reported as suspended under category W and owes no tax, but it still has to be reported and it still appears on your Schedule 1.",
      },
      {
        q: "Is Form 2290 the same as the Heavy Vehicle Use Tax?",
        a: "Form 2290 is the return; the Heavy Vehicle Use Tax, or HVUT, is the tax it reports. In everyday use the two names are used interchangeably.",
      },
    ],
  },

  // ─────────────────────────────────────────────────────────────────────────
  // CANONICAL TARGET: "form 2290 late filing" / "form 2290 penalty". The
  // due-date article keeps "form 2290 due date" and now points its short
  // consequences section here rather than covering the same ground.
  //
  // NO PENALTY PERCENTAGE APPEARS BELOW, AND THAT IS DELIBERATE. Checked
  // 2026-08-14: the Instructions for Form 2290 publish no rate at all, and the
  // IRS failure-to-file page that does publish 5% per month / 25% cap
  // enumerates Forms 1040, 1120, 1065 and 1120-S — Form 2290 is not among them.
  // So the widely-repeated "4.5% per month" figure cannot be traced to a
  // primary source from here. Per the file header, a plausible-looking number
  // is a defect. If the owner or an EA can source a 2290-specific figure, it
  // goes in with the citation; until then the article says why it is absent,
  // which is more useful to a worried reader than a number that might be wrong.
  {
    slug: "form-2290-late-filing-penalties",
    category: "deadlines",
    title: "Filing Form 2290 Late: Penalties, Interest, and What to Do Next",
    metaTitle: "Form 2290 Late Filing | Penalties, Interest & Next Steps",
    metaDescription:
      "Missed the Form 2290 deadline? What the IRS charges for filing or paying late, why your registration is the more urgent problem, and how to request penalty relief.",
    keywords: [
      "form 2290 late filing",
      "form 2290 penalty",
      "form 2290 late payment penalty",
      "missed form 2290 deadline",
      "hvut penalty",
      "form 2290 penalty relief",
    ],
    excerpt:
      "Two separate things go wrong when a Form 2290 is late — and the one that stops you working is not the one the IRS sends you a bill for.",
    publishedAt: "2026-08-14",
    updatedAt: "2026-08-14",
    readingMinutes: 6,
    irsVerified: true,
    sources:
      "IRS Instructions for Form 2290 (Rev. July 2026) — When To File, penalty relief; IRS.gov/PenaltyRelief",
    body: [
      {
        type: "p",
        text: "If your Form 2290 is late, the useful thing to know is that two separate problems have started, on two different clocks, and only one of them involves the IRS.",
      },
      {
        type: "p",
        text: "The IRS can charge a penalty plus interest on the tax owed. Meanwhile you do not have a current stamped Schedule 1 — and without that, your state will not register or renew the truck. For most operators the second problem arrives first and hurts more.",
      },
      { type: "h2", text: "The two consequences, in the order they bite" },
      {
        type: "ul",
        items: [
          "No current [stamped Schedule 1](/blog/stamped-schedule-1-proof-of-payment). This stops registration, and registration is what keeps the truck legal. It is immediate and it is visible at the DMV counter.",
          "A penalty plus interest from the IRS on the tax owed, for filing late, paying late, or both. This arrives later, as a notice.",
        ],
      },
      {
        type: "p",
        text: "The order matters because it changes what you do first. Filing solves both — the Schedule 1 comes back on acceptance, and the penalty exposure stops growing.",
      },
      { type: "h2", text: "Why this guide does not quote you a percentage" },
      {
        type: "p",
        text: "Search for this and you will find a specific monthly percentage repeated confidently across a dozen filing sites. We are not going to repeat it, and it is worth explaining why.",
      },
      {
        type: "p",
        text: "The IRS Instructions for Form 2290 do not publish a penalty rate. What they say is that if you receive a penalty for filing or paying late and believe you have reasonable cause, you should write to the IRS explaining why.",
      },
      {
        type: "p",
        text: "The IRS does publish a failure-to-file rate elsewhere — but the page it appears on enumerates the returns it applies to, and Form 2290 is not among them. Applying a figure from a different return to this one produces a number that looks authoritative and may simply be wrong.",
      },
      {
        type: "callout",
        tone: "warn",
        text: "The amount that actually applies to you is stated on the notice the IRS sends. That figure is the real one — not the percentage on a filing site, and not an estimate from a calculator that does not know your tax, your months, or your history.",
      },
      { type: "h2", text: "Filing late and paying late are different failures" },
      {
        type: "p",
        text: "They are two obligations and they are assessed separately. A return filed on time with the payment short is not the same situation as a return never filed at all, and the two can be charged independently.",
      },
      {
        type: "p",
        text: "Interest is a third thing again. It runs on what is owed and it keeps running until the balance is paid, which is the mechanical reason a late 2290 gets more expensive the longer it sits rather than costing a flat amount.",
      },
      { type: "h2", text: "If you cannot pay the full tax" },
      {
        type: "p",
        text: "The Instructions are explicit that the tax is due in full with the return. Where that is not possible, the IRS publishes its payment options at [IRS.gov](https://www.irs.gov/payments) — but the return itself is a separate obligation, and leaving it unfiled while you sort out the money does not help either problem.",
      },
      { type: "h2", text: "How to request penalty relief" },
      {
        type: "p",
        text: "If you have a genuine reason the return or the payment was late, you can ask the IRS to abate the penalty. Send a letter explaining why you believe you have reasonable cause, or follow the process at [IRS.gov/PenaltyRelief](https://www.irs.gov/payments/penalty-relief). If a notice has already arrived, the number on it is the fastest route.",
      },
      {
        type: "callout",
        tone: "warn",
        text: "Do not attach your explanation to the Form 2290 itself. That is not where the IRS processes relief requests, and a return with an unexpected attachment is a return that gets held up.",
      },
      { type: "h2", text: "What to do right now" },
      {
        type: "ol",
        items: [
          "[File the return](/wizard/1). Nothing improves while it is unfiled, and the stamped Schedule 1 comes back on acceptance.",
          "Check your first-used month before you transmit. It sets both the deadline you missed and the tax you owe — the [due-date chart](/blog/form-2290-due-date-2026-2027) shows which month applies to which deadline.",
          "Check every VIN on the summary screen. A late return with a wrong VIN needs a [VIN correction](/blog/form-2290-vin-correction) on top of everything else.",
          "Pay what you can, when you file.",
          "If a notice arrives and you have reasonable cause, request relief — do not simply pay it because it arrived.",
        ],
      },
      { type: "h2", text: "Late is not the same as wrong" },
      {
        type: "p",
        text: "A lot of people arrive here looking for a penalty when what they actually have is a different filing to make. If the return was filed on time and something changed afterwards, this is not a late-filing problem:",
      },
      {
        type: "table",
        headers: ["Situation", "What it actually is"],
        rows: [
          ["Return never filed for the period", "Late filing — file now"],
          [
            "Weight increased, or a suspended vehicle passed its mileage limit",
            "[Amendment](/blog/form-2290-amendments), with its own deadline",
          ],
          ["A VIN was mistyped on an accepted return", "[VIN correction](/blog/form-2290-vin-correction)"],
          [
            "Tax paid on a truck that was sold or barely driven",
            "[Credit or refund](/blog/form-2290-credits-and-refunds) — money back, not owed",
          ],
          ["The IRS rejected the return", "Nothing was filed. Fix the error and [retransmit](/wizard/1)"],
        ],
      },
      {
        type: "p",
        text: "The rejected case catches people out most often. A rejection is not a late filing and not a penalty — it means the return never landed, so the fix is to correct the error and send it again.",
      },
      { type: "h2", text: "Next year" },
      {
        type: "p",
        text: "The filing window opens on July 1 and the largest deadline — for any vehicle already running in July — is August 31. Filing in early July rather than late August is the entire fix, and it also means the Schedule 1 is in hand before a registration renewal needs it.",
      },
    ],
    faqs: [
      {
        q: "What is the penalty for filing Form 2290 late?",
        a: "The IRS can charge a penalty plus interest on the tax owed for filing late, paying late, or both. The Instructions for Form 2290 do not publish a rate, and the amount that applies to you is stated on the notice the IRS sends. Treat specific percentages quoted elsewhere with caution.",
      },
      {
        q: "What happens if I never file Form 2290?",
        a: "Two things. The IRS penalty and interest continue to accrue on the tax owed, and you have no current stamped Schedule 1 — so your state will not register or renew the vehicle. The second usually stops the truck working before the first arrives in the post.",
      },
      {
        q: "Can I get a Form 2290 penalty removed?",
        a: "You can request relief if you believe you have reasonable cause. Send the IRS a letter explaining why the return or payment was late, or follow the process at IRS.gov/PenaltyRelief. Do not attach the explanation to the return itself.",
      },
      {
        q: "Should I still file if I cannot pay the tax?",
        a: "The tax is due in full with the return, and where that is not possible the IRS publishes payment options at IRS.gov. Filing and paying are separate obligations, so leaving the return unfiled while you arrange the money does not improve either one.",
      },
      {
        q: "Is a rejected Form 2290 the same as filing late?",
        a: "No. A rejection means no return was filed at all, so there is nothing to amend or correct — fix whatever the IRS flagged and transmit it again.",
      },
    ],
  },

  // ─────────────────────────────────────────────────────────────────────────
  // CANONICAL TARGET: "form 2290 new vehicle" / "form 2290 partial period tax".
  // The due-date article keeps "form 2290 due date" and the weight-categories
  // article keeps the rate table; both now link their short partial-period
  // passages here instead of expanding them.
  {
    slug: "form-2290-new-vehicle-partial-period",
    category: "basics",
    title: "Form 2290 for a New Truck: First-Used Month and Partial-Period Tax",
    metaTitle: "Form 2290 for New Vehicles | Partial-Period Tax Explained",
    metaDescription:
      "Put a truck into service mid-year? You do not owe a full year of Heavy Vehicle Use Tax. How the first-used month sets both your deadline and your prorated tax.",
    keywords: [
      "form 2290 new vehicle",
      "form 2290 partial period tax",
      "prorated form 2290",
      "first used month",
      "form 2290 mid year purchase",
      "new truck form 2290",
    ],
    excerpt:
      "A truck put on the road in March does not owe a full year of Heavy Vehicle Use Tax. The month it first touches a public highway sets both the bill and the deadline.",
    publishedAt: "2026-08-14",
    updatedAt: "2026-08-14",
    readingMinutes: 5,
    irsVerified: true,
    sources:
      "IRS Instructions for Form 2290 (Rev. July 2026) — When To File, first use, partial-period tax; IRS Form 2290 (Rev. July 2026) page 2 rate table",
    body: [
      {
        type: "p",
        text: "Buying a truck in the middle of the tax period raises the same two questions every time: when is the return due, and do I owe a full year of tax on a vehicle I have had for four months?",
      },
      {
        type: "p",
        text: "One answer to both. The month the vehicle is first used on a public highway sets your deadline and prorates your tax, and it is the single field on the return that most changes what you pay.",
      },
      { type: "h2", text: "First use is not purchase, registration, or delivery" },
      {
        type: "p",
        text: "The first-used month is the month the vehicle was first driven on a public highway during this tax period. Not the month you bought it, not the month the title came through, not the month it was registered, and not the month the business started.",
      },
      {
        type: "p",
        text: "A truck bought in May but not driven on a public road until September has a first-used month of September. A truck you already owned and ran through the previous period has a first-used month of July, because July is the first month of the new period.",
      },
      {
        type: "callout",
        tone: "warn",
        text: "This is the most misreported field on Form 2290. Getting it wrong changes your deadline and your tax at the same time, in the same direction, so the error compounds rather than cancelling out.",
      },
      { type: "h2", text: "Your deadline moves with it" },
      {
        type: "p",
        text: "The return is due by the last day of the month following the month of first use. Put a truck on the road in November and the return is due by December 31 — not the following August, and not August 31 of the year you bought it.",
      },
      {
        type: "p",
        text: "The [full due-date chart](/blog/form-2290-due-date-2026-2027) lists every first-used month against its deadline for the current period, including the rolls where the last day of a month lands on a weekend or a federal holiday.",
      },
      { type: "h2", text: "And so does the tax: the partial-period rule" },
      {
        type: "p",
        text: "Form 2290 tax is prorated. A vehicle used for the full period pays the annual rate for its weight category. A vehicle first used later pays only for the months remaining in the period, so a truck first used in January is taxed for six months rather than twelve.",
      },
      {
        type: "p",
        text: "The annual figure it is prorated from comes from the vehicle's taxable gross weight — the [weight categories and rate table](/blog/form-2290-weight-categories-and-rates) sets out all 21 bands, and the same table is on the [rates page](/rates).",
      },
      {
        type: "p",
        text: "We are deliberately not printing a worked dollar example here, because the amount depends on the category, the month, and whether the vehicle is a logging vehicle. [Start a return](/wizard/1) and the partial-period tax is figured from the published IRS tables using the first-used month you enter.",
      },
      { type: "h2", text: "Adding a truck to a period you have already filed for" },
      {
        type: "p",
        text: "This is not an amendment. Amendments cover exactly two situations — a taxable gross weight increase, and a suspended vehicle exceeding its mileage limit — and a new vehicle is neither.",
      },
      {
        type: "p",
        text: "A vehicle put into service after you filed is reported on its own return for its own first-used month, with its own deadline and its own prorated tax. The [amendments guide](/blog/form-2290-amendments) covers what genuinely does require one.",
      },
      { type: "h2", text: "Buying a truck someone else already reported" },
      {
        type: "p",
        text: "A stamped Schedule 1 belongs to the filer, not to the vehicle. The seller's Schedule 1 will not register the truck in your name, however recent it is.",
      },
      {
        type: "p",
        text: "Your own first-used month is the month you first use the vehicle on a public highway. The seller's side of the transaction is separate and may entitle them to a [credit for the months they paid for and did not use](/blog/form-2290-credits-and-refunds). The detailed rules for vehicles sold during a period are in the IRS Instructions, and are worth reading before a mid-period purchase rather than after.",
      },
      { type: "h2", text: "If the new truck will barely be driven" },
      {
        type: "p",
        text: "A vehicle expected to run 5,000 miles or less during the period — 7,500 for agricultural vehicles — is reported as [suspended under category W](/blog/suspended-vehicles-category-w) and owes no tax.",
      },
      {
        type: "p",
        text: "The mileage limit applies to the whole tax period, and it is not prorated for a vehicle that joins partway through. Suspension is still a forecast: if the truck goes over the limit later, the tax becomes due and an amendment reports it.",
      },
      { type: "h2", text: "Putting several vehicles on at once" },
      {
        type: "p",
        text: "Vehicles sharing a first-used month go on the same return. Where a fleet takes delivery of a batch, [bulk upload](/blog/bulk-form-2290-filing-for-fleets) imports the vehicle list from a spreadsheet rather than one VIN at a time — which is also where the transcription errors that lead to VIN corrections come from.",
      },
      { type: "h2", text: "Before you transmit" },
      {
        type: "ul",
        items: [
          "Confirm the first-used month against when the truck actually went on a public road, not when it was bought.",
          "Confirm the weight category reflects taxable gross weight — the vehicle, its customary trailers, and the maximum load customarily carried.",
          "Check the VIN against the vehicle or the title. A wrong VIN produces a Schedule 1 the DMV will reject, and fixing it needs a [VIN correction](/blog/form-2290-vin-correction).",
        ],
      },
    ],
    faqs: [
      {
        q: "Do I pay a full year of Form 2290 tax on a truck bought mid-year?",
        a: "No. The tax is prorated from the first-used month, so a vehicle first used in January is taxed for six months of the period rather than twelve.",
      },
      {
        q: "When is Form 2290 due for a new vehicle?",
        a: "By the last day of the month following the month the vehicle was first used on a public highway. A truck first used in November is due by December 31.",
      },
      {
        q: "What counts as the first-used month?",
        a: "The month the vehicle was first driven on a public highway during the tax period — not the month it was purchased, titled, registered, or delivered.",
      },
      {
        q: "Do I file an amendment to add a new truck?",
        a: "No. Amendments cover only a taxable gross weight increase or a suspended vehicle exceeding its mileage limit. A new vehicle is reported on its own return for its own first-used month.",
      },
      {
        q: "Can I use the seller's stamped Schedule 1 to register a truck I bought?",
        a: "No. A stamped Schedule 1 is issued to the filer, not to the vehicle, so it will not register the truck in your name. You report the vehicle yourself from the month you first use it.",
      },
    ],
  },

  // ─────────────────────────────────────────────────────────────────────────
  // BATCH 2026-10-07 — filing-oriented posts for fleets and tax preparers.
  // Every IRS fact below is from the Instructions for Form 2290 (Rev. July
  // 2026); rates come from RATES / Table I; deadlines from taxPeriods.js;
  // the reject rule from the 2290 Business Rules Publication TY2026 v1.1.
  // Product claims were checked against the app (wizard steps, credits step,
  // Businesses + Tax Preparers dashboards, bulk import) on release/1.1.
  // ─────────────────────────────────────────────────────────────────────────

  // CANONICAL TARGET: "form 2290 for fleets" / "file 2290 for multiple trucks".
  // The bulk-upload post owns the data-entry how-to; this one owns the
  // year-round schedule of returns a fleet actually has to file.
  {
    slug: "form-2290-fleet-filing-calendar",
    category: "fleets",
    title: "Form 2290 for Fleets: Every Return You'll File This Year, Month by Month",
    metaTitle: "Form 2290 for Fleets | Year-Round HVUT Filing Calendar",
    metaDescription:
      "A fleet files more than one Form 2290 a year. The July renewal, a return for every month you add trucks, credits, and amendments — and how to file them fast.",
    keywords: [
      "form 2290 for fleets",
      "file 2290 for multiple trucks",
      "fleet hvut filing",
      "form 2290 fleet",
      "2290 for trucking companies",
      "form 2290 new trucks added",
    ],
    excerpt:
      "One August deadline is the owner-operator's view of Form 2290. A fleet that adds, sells, and reweighs trucks files all year — here is the calendar, and how to clear each return in one sitting.",
    cta: {
      heading: "File your fleet's Form 2290",
      text: "Import the whole vehicle list from Excel, pay the tax, and get one stamped Schedule 1 for every VIN on the return.",
      label: "Start a fleet return",
      to: "/wizard/1",
    },
    publishedAt: "2026-10-07",
    updatedAt: "2026-10-07",
    readingMinutes: 7,
    irsVerified: true,
    sources:
      "IRS Instructions for Form 2290 (Rev. July 2026) — When To File (incl. Example 3), How To File, Line 3, Line 5, Line 8, Suspended vehicles exceeding the mileage use limit, Recordkeeping",
    body: [
      {
        type: "p",
        text: "For an owner-operator, [Form 2290](/blog/what-is-form-2290) is one return a year, due August 31. For a fleet it is a recurring job. Trucks are bought in October, sold in February, and moved to heavier trailers in March, and almost every one of those events has its own filing deadline.",
      },
      {
        type: "p",
        text: "The rule underneath all of it is short: a return is due by the last day of the month after the month a vehicle is first used on a public highway during the July-to-June period. Everything below follows from that.",
      },
      { type: "h2", text: "July: the fleet renewal return" },
      {
        type: "p",
        text: "Every taxable vehicle in service in July goes on one return, due August 31. That includes trucks you expect to run 5,000 miles or less (7,500 for agricultural vehicles): they are reported as [suspended under category W](/blog/suspended-vehicles-category-w) with no tax, but they still go on the return and on the Schedule 1.",
      },
      {
        type: "p",
        text: "Two things make the July return the big one for a fleet:",
      },
      {
        type: "ul",
        items: [
          "E-filing is required for any return reporting and paying tax on 25 or more vehicles. Suspended category W trucks don't count toward the 25, because no tax is paid on them.",
          "The new period's return is also where you confirm last period's suspended trucks really stayed under the mileage limit, and list any that didn't. Those owe tax on a separate return for the prior period.",
        ],
      },
      {
        type: "cta",
        heading: "Renewing the whole fleet?",
        text: "Upload up to 500 vehicles per file from the Excel template instead of typing each VIN.",
        label: "Start the July return",
        to: "/wizard/1",
      },
      { type: "h2", text: "Any month: a return for each month you add trucks" },
      {
        type: "p",
        text: "A truck first used in October isn't added to the July return. It goes on a new return, due by the end of November, and it is taxed only for the months left in the period — see [partial-period tax for trucks first used after July](/blog/form-2290-new-vehicle-partial-period).",
      },
      {
        type: "p",
        text: "If you add trucks in more than one month, the IRS requires a separate Form 2290 for each month of first use. Its own example is a filer who first uses vehicles in July and August: the July trucks are due by August 31, and the August trucks go on a separate return due by September 30.",
      },
      {
        type: "p",
        text: "For a fleet that buys steadily, that is up to twelve returns a period. Here is when each is due:",
      },
      {
        type: "table",
        headers: ["Trucks first used in", "File and pay by"],
        rows: DUE_DATE_ROWS,
      },
      {
        type: "p",
        text: "The deadline is not tied to the registration renewal date. A truck whose plates renew in March is still due by the month after it was first used.",
      },
      { type: "h2", text: "When a truck is sold, destroyed, or stolen" },
      {
        type: "p",
        text: "Tax already paid for the months after the event can come back as a credit. For a vehicle sold, destroyed, or stolen before June 1 and not used again that period, the credit is claimed on the next Form 2290 you file — which for a fleet is usually the next month's new-truck return. The full rules, with a worked example, are in [claiming the Form 2290 credit for a sold, destroyed, or stolen truck](/blog/form-2290-credit-sold-destroyed-stolen-truck).",
      },
      { type: "h2", text: "When a truck gets heavier, or a suspended truck runs too far" },
      {
        type: "p",
        text: "Two events create additional tax mid-period, and both are filed as an [amended return](/blog/form-2290-amendments) due by the last day of the month after the month they happened:",
      },
      {
        type: "ul",
        items: [
          "A taxable gross weight increase that moves a truck into a higher category — for example, a heavier maximum load customarily carried. You pay the difference for the rest of the period.",
          "A suspended truck that goes over 5,000 miles (7,500 for agricultural vehicles). The full tax for the period becomes due, figured from the month it was first used.",
        ],
      },
      { type: "h2", text: "Records to keep for every truck" },
      {
        type: "p",
        text: "The IRS asks you to keep records for every taxable vehicle for at least 3 years after the tax is due or paid, whichever is later, and to keep actual highway mileage for every suspended vehicle. For each truck that means its VIN and description, the date and source of acquisition, the first month of taxable use each period, and the date and buyer if it was sold.",
      },
      { type: "h2", text: "How a fleet files each of these in one sitting" },
      {
        type: "ol",
        items: [
          "Add the business once — legal name, EIN, and address. Every later return for the period starts from it.",
          "Pick the return type: original (July renewal or a new month's trucks), [VIN correction](/blog/form-2290-vin-correction), or amendment.",
          "Import the trucks from the Excel template, or add them one by one for a small month.",
          "Confirm last period's suspended trucks and claim any credits; the credit is figured for you from the IRS partial-period table.",
          "Pay, e-sign, and transmit. One stamped [Schedule 1](/blog/stamped-schedule-1-proof-of-payment) comes back listing every VIN on the return.",
        ],
      },
    ],
    faqs: [
      {
        q: "Does a fleet file one Form 2290 a year?",
        a: "Not usually. Trucks in use in July go on one return due August 31, but trucks first used in any later month need their own return, due by the end of the following month. The IRS requires a separate Form 2290 for each month of first use.",
      },
      {
        q: "Do suspended trucks count toward the 25-vehicle e-file requirement?",
        a: "No. E-filing is required when a return reports and pays tax on 25 or more vehicles. Suspended category W trucks aren't counted because no tax is paid on them, but they are still listed on the return.",
      },
      {
        q: "Where do I claim the credit for a truck we sold?",
        a: "On the next Form 2290 you file for the period, if the truck was sold before June 1 and not used again that period. You can also claim a refund on Form 8849 instead.",
      },
      {
        q: "How long should a fleet keep Form 2290 records?",
        a: "At least 3 years after the tax is due or paid, whichever is later. For suspended vehicles, keep highway mileage records for at least 3 years after the end of the period the suspension covers.",
      },
    ],
  },

  // CANONICAL TARGET: "2290 refund sold truck" / "form 2290 credit sold vehicle"
  // (Search Console 2026-10-07 shows "2290 refund sold truck" at position ~36).
  // The credits-and-refunds post stays the overview; this one owns the
  // sold/destroyed/stolen scenario end to end.
  {
    slug: "form-2290-credit-sold-destroyed-stolen-truck",
    category: "credits",
    title: "Sold, Destroyed, or Stolen Truck? How to Claim Your Form 2290 Credit",
    metaTitle: "Form 2290 Credit for a Sold, Destroyed or Stolen Truck",
    metaDescription:
      "Sold or lost a truck you already paid 2290 tax on? Claim the unused months back on your next Form 2290. Who qualifies, what to include, and a worked example.",
    keywords: [
      "2290 refund sold truck",
      "form 2290 credit sold vehicle",
      "form 2290 stolen truck",
      "form 2290 destroyed vehicle credit",
      "hvut credit sold truck",
      "irs form 2290 refund",
    ],
    excerpt:
      "If a truck you paid the full year on was sold, wrecked, or stolen, the tax for the months it wasn't yours comes back. Here is who qualifies, how the credit is figured, and how to claim it on your next return.",
    cta: {
      heading: "Claim your credit on your next Form 2290",
      text: "Add the truck, the reason, and the date. The credit is figured from the IRS partial-period table and applied against the tax due.",
      label: "Start a return with a credit",
      to: "/wizard/1",
    },
    publishedAt: "2026-10-07",
    updatedAt: "2026-10-07",
    readingMinutes: 6,
    irsVerified: true,
    sources:
      "IRS Instructions for Form 2290 (Rev. July 2026) — Line 5 (credits), Figuring the credit, When to make a claim, used-vehicle example; Table I and Table II partial-period tax tables",
    body: [
      {
        type: "p",
        text: "Form 2290 tax is paid up front for the whole July-to-June period. When a truck leaves your hands partway through, the IRS lets you take back the tax for the months after it was gone — as a credit on your next return, or as a refund.",
      },
      { type: "h2", text: "Who qualifies" },
      {
        type: "p",
        text: "You can claim a credit for tax you paid on a vehicle that was:",
      },
      {
        type: "ul",
        items: [
          "Sold before June 1 and not used during the rest of the period.",
          "Destroyed — damaged by accident or other casualty so badly it isn't economical to rebuild — before June 1 and not used during the rest of the period.",
          "Stolen before June 1 and not used during the rest of the period.",
        ],
      },
      {
        type: "callout",
        tone: "warn",
        text: "Lighter loads don't count. The IRS allows no credit, lower tax, or refund for an occasional light or decreased load, or for a discontinued or changed use of the vehicle.",
      },
      {
        type: "p",
        text: "A truck that ran 5,000 miles or less (7,500 for agricultural vehicles) also earns a credit, but on a different timetable — it can only be claimed after the period ends. That case is covered in [Form 2290 credits and refunds](/blog/form-2290-credits-and-refunds).",
      },
      { type: "h2", text: "How the credit is figured" },
      {
        type: "p",
        text: "The credit is the tax you paid, minus the tax for the months you actually had the truck. Count the months of use from the first day of the month it was first used in the period through the last day of the month it was sold, destroyed, or stolen, then look up that many months in the IRS partial-period table for its [weight category](/blog/form-2290-weight-categories-and-rates).",
      },
      {
        type: "p",
        text: "A worked example using the IRS's own figures: a category V truck (over 75,000 lb) was first used in July, so the full-period tax of $550.00 was paid. It was sold in September and not used again by the seller.",
      },
      {
        type: "table",
        headers: ["Step", "Amount"],
        rows: [
          ["Tax paid on the truck (full period, category V)", "$550.00"],
          ["Months of use: July, August, September", "3 months"],
          ["Partial-period tax for 3 months, category V (IRS Table I)", "$137.50"],
          ["Credit: $550.00 − $137.50", "$412.50"],
        ],
      },
      {
        type: "p",
        text: "Each truck's credit is figured separately. Logging vehicles use the logging column of the partial-period table (Table II) instead.",
      },
      { type: "h2", text: "What you have to include" },
      {
        type: "p",
        text: "The IRS wants an explanation of the facts for each credit. For a vehicle destroyed, stolen, or sold, include:",
      },
      {
        type: "ol",
        items: [
          "The VIN.",
          "The taxable gross weight category.",
          "The date of the destruction, theft, or sale.",
          "The credit worksheet.",
          "For a sale, the buyer's name and address.",
        ],
      },
      {
        type: "p",
        text: "Leave something out and the IRS may disallow the claim — the buyer's name and address on a sale is the item most often missed.",
      },
      { type: "h2", text: "Where and when to claim it" },
      {
        type: "p",
        text: "Claim the credit on the next Form 2290 you file, or claim a refund on Form 8849 instead. For a fleet, the next return is often the one for trucks added the following month, so the credit simply reduces that return's tax.",
      },
      {
        type: "p",
        text: "One limit: the credit can't be more than the tax on the return you claim it on. If it is, the excess has to be claimed as a refund on Form 8849 with Schedule 6.",
      },
      {
        type: "cta",
        heading: "Adding trucks this month?",
        text: "Claim the credit for the truck you sold on the same return and pay less today.",
        label: "Start the return",
        to: "/wizard/1",
      },
      { type: "h2", text: "If you bought the truck" },
      {
        type: "p",
        text: "The buyer files too. If you bought a used truck privately from a seller who already paid the tax for the period, and you first drove it in the month of the sale, your tax is prorated from the first day of the following month. The IRS example: a category V truck bought on September 9 owes 9/12 of $550, or $412.50, entered with October as the month on line 1. Ask the seller for a copy of their [stamped Schedule 1](/blog/stamped-schedule-1-proof-of-payment) — it is one way to confirm the tax was paid.",
      },
      { type: "h2", text: "Claiming it online" },
      {
        type: "ol",
        items: [
          "Start the return for the business that paid the tax.",
          "In the credits step, add the truck's VIN, its weight category, the reason (sold, destroyed, or stolen), and the date.",
          "Add the explanation, including the buyer's name and address for a sale.",
          "The credit is figured from the IRS partial-period table and capped at the tax on the return; review it with the rest of the return, then pay and submit.",
        ],
      },
    ],
    faqs: [
      {
        q: "Can I get my 2290 tax back if I sold my truck?",
        a: "Yes, if it was sold before June 1 and you didn't use it again that period. Claim a credit on your next Form 2290 or a refund on Form 8849 for the tax on the months after the sale.",
      },
      {
        q: "How do I calculate the credit for a sold truck?",
        a: "Subtract the partial-period tax for the months you used it (from the first month of use through the month of sale) from the tax you paid. A category V truck paid at $550.00 and sold in September has 3 months of use, $137.50, so the credit is $412.50.",
      },
      {
        q: "Is a truck that was stolen treated the same as a sale?",
        a: "Yes. A truck destroyed or stolen before June 1 and not used again that period earns the same credit, figured the same way. Include the date of the theft or destruction.",
      },
      {
        q: "What if my credit is bigger than the tax on my next return?",
        a: "The credit on Form 2290 can't exceed the tax reported on that return. Claim the excess as a refund on Form 8849, Schedule 6.",
      },
    ],
  },

  // CANONICAL TARGET: "form 2290 for tax preparers" / "file 2290 for clients".
  // Backlog #28 (preparers flagged UNDERSERVED: everyone targets
  // owner-operators). Converts to a preparer SIGN-UP, not a single filing.
  {
    slug: "form-2290-for-tax-preparers",
    category: "fleets",
    title: "Form 2290 for Tax Preparers and CPAs: How to File HVUT Returns for Trucking Clients",
    metaTitle: "Form 2290 for Tax Preparers & CPAs | File 2290 for Clients",
    metaDescription:
      "File Form 2290 for your trucking clients: the paid-preparer section, PTIN rules, payment, Schedule 1 delivery, and how to run many clients from one account.",
    keywords: [
      "form 2290 for tax preparers",
      "file 2290 for clients",
      "form 2290 paid preparer",
      "2290 for cpas",
      "hvut tax preparer",
      "e-file 2290 for clients",
    ],
    excerpt:
      "Every trucking client with a heavy truck needs a Form 2290 — and the deadline lands in late summer, not in tax season. Here is what the IRS requires of a paid preparer, and how to file for a book of clients without retyping anything.",
    cta: {
      heading: "Open a tax preparer account",
      text: "Save your PTIN and firm details once, add each client as a business, and file their returns from one dashboard.",
      label: "Create a preparer account",
      to: "/login?tab=up",
    },
    publishedAt: "2026-10-07",
    updatedAt: "2026-10-07",
    readingMinutes: 7,
    irsVerified: true,
    sources:
      "IRS Instructions for Form 2290 (Rev. July 2026) — Paid Preparer Use Only, Third Party Designee, Signature, Payment voucher, Schedule 1 (e-file), When To File, Recordkeeping",
    body: [
      {
        type: "p",
        text: "Most tax software competes for the owner-operator filing one truck. The trucking client with three, thirty, or three hundred trucks usually hands the job to their accountant — and Form 2290 is an odd return for an accounting practice. It is due by August 31 for every truck in service in July, it has nothing to do with the income tax calendar, and it comes back several times a year.",
      },
      { type: "h2", text: "What the IRS requires of a paid preparer" },
      {
        type: "p",
        text: "If you're paid to prepare a client's Form 2290 and you aren't the client's employee, the Paid Preparer Use Only section is yours:",
      },
      {
        type: "ul",
        items: [
          "You must sign the return and complete the paid-preparer section.",
          "Enter your own PTIN. If you work for a firm, also enter the firm's name and EIN — but you can't use the firm's PTIN in place of yours.",
          "Include your complete address.",
          "Give the client a copy of the return in addition to the one filed with the IRS.",
        ],
      },
      {
        type: "p",
        text: "Being the paid preparer doesn't by itself let you talk to the IRS about the return afterwards. For that, the client names you as [third-party designee on Form 2290](/blog/third-party-designee-form-2290).",
      },
      { type: "h2", text: "The filing calendar your clients actually have" },
      {
        type: "p",
        text: "The August 31 return is the busy week, but it is not the whole job. A client who adds trucks in October needs a return by the end of November; one who sells a truck in February has a credit to claim; a truck moved to heavier loads needs an amendment. The [fleet filing calendar](/blog/form-2290-fleet-filing-calendar) sets out every trigger — a client list with several fleets generates 2290 work in most months of the year.",
      },
      {
        type: "p",
        text: "Two dates are worth putting on every trucking client's file:",
      },
      {
        type: "table",
        headers: ["When", "What"],
        rows: [
          ["August 31", "Return for every truck in service in July, including suspended trucks"],
          ["End of each following month", "Return for trucks first used that month; amendments for weight increases or suspended trucks over the mileage limit"],
        ],
      },
      { type: "h2", text: "Payment: the client's money, not yours" },
      {
        type: "p",
        text: "The tax must be paid in full with the return. An e-filed return can be paid by electronic funds withdrawal from the client's bank account, through the client's EFTPS enrollment, or by credit or debit card. If the client pays by check, the IRS says the client should give the Form 2290-V payment voucher to the preparer.",
      },
      { type: "h2", text: "Getting the stamped Schedule 1 to the client" },
      {
        type: "p",
        text: "On an e-filed return, the IRS sends the watermarked Schedule 1 electronically to the e-file provider, and you download it from there. That is what the client needs at the DMV, so it is the deliverable that matters — send it the day the return is accepted. See [stamped Schedule 1, your proof of payment](/blog/stamped-schedule-1-proof-of-payment).",
      },
      {
        type: "cta",
        heading: "Filing for several trucking clients?",
        text: "One preparer account, one dashboard, every client's returns and Schedule 1s in one place.",
        label: "Create a preparer account",
        to: "/login?tab=up",
      },
      { type: "h2", text: "How filing for clients works here" },
      {
        type: "ol",
        items: [
          "Create an account and choose the tax preparer option at sign-up.",
          "Save your preparer profile once: PTIN, credential (CPA, EA, attorney, AFSP, or none), and firm name, EIN, and address. It fills the paid-preparer section of every client return.",
          "Add each client as a business — legal name, EIN, address, and the person who signs for them. Each client keeps its own trucks, returns, and history, so next July starts from last year's list.",
          "File: pick the client, choose the return type, add or [import their trucks from a spreadsheet](/blog/bulk-form-2290-filing-for-fleets), and pay from the client's account.",
          "Download the stamped Schedule 1 once the IRS accepts the return and send it to the client.",
        ],
      },
      {
        type: "p",
        text: "Before the first return for a new client, run through the [Form 2290 client checklist](/blog/form-2290-client-checklist) — most rejections trace back to something that was never collected. Prepaid return packages for higher volumes are on the [pricing page](/pricing).",
      },
      { type: "h2", text: "Records" },
      {
        type: "p",
        text: "The client must keep Form 2290 records for at least 3 years after the tax is due or paid, whichever is later — longer for suspended vehicles. Keeping each client's returns and Schedule 1s in one account makes that request easy to answer when it comes.",
      },
    ],
    faqs: [
      {
        q: "Does a tax preparer have to sign Form 2290?",
        a: "Yes, if you were paid to prepare it and aren't the client's employee. Sign the return and complete the Paid Preparer Use Only section with your PTIN, address, and — if you work for a firm — the firm's name and EIN.",
      },
      {
        q: "Can I use my firm's PTIN on Form 2290?",
        a: "No. Each preparer enters their own PTIN. The firm's name and EIN go in their own fields.",
      },
      {
        q: "Can I file Form 2290 for multiple clients from one account?",
        a: "Yes. Add each client as a separate business. Each keeps its own vehicles, returns, and Schedule 1s, and your preparer details fill every return.",
      },
      {
        q: "Who pays the Form 2290 tax when a preparer files?",
        a: "The client. The tax must be paid in full with the return — by electronic funds withdrawal from the client's account, the client's EFTPS, a credit or debit card, or a check with Form 2290-V.",
      },
    ],
  },

  // CANONICAL TARGET: "third party designee form 2290". Backlog #59. Short,
  // precise, sourced line-by-line from the i2290 Third Party Designee section.
  {
    slug: "third-party-designee-form-2290",
    category: "fleets",
    title: "Third-Party Designee on Form 2290: What It Allows and How to Set It Up",
    metaTitle: "Form 2290 Third Party Designee | What It Allows",
    metaDescription:
      "Naming a third-party designee on Form 2290 lets your preparer or employee talk to the IRS about the return. What it covers, what it doesn't, and how long it lasts.",
    keywords: [
      "third party designee form 2290",
      "form 2290 designee",
      "2290 designee pin",
      "form 2290 authorize preparer",
      "third party designee irs",
    ],
    excerpt:
      "One checkbox on Form 2290 decides whether your preparer or office manager can call the IRS about your return. Here is exactly what it allows, what it doesn't, and when it runs out.",
    cta: {
      heading: "Name your designee as you file",
      text: "Add the designee's name, phone, and 5-digit PIN on the business details step.",
      label: "Start your Form 2290",
      to: "/wizard/1",
    },
    publishedAt: "2026-10-07",
    updatedAt: "2026-10-07",
    readingMinutes: 4,
    irsVerified: true,
    sources:
      "IRS Instructions for Form 2290 (Rev. July 2026) — Third Party Designee; Paid Preparer Use Only; Pub. 947 referenced by the instructions",
    body: [
      {
        type: "p",
        text: "When the IRS has a question about a [Form 2290](/blog/what-is-form-2290), it talks to the taxpayer. The Third Party Designee section lets you name one more person it can talk to — an employee, your return preparer, or anyone else you choose.",
      },
      { type: "h2", text: "What a designee can do" },
      {
        type: "p",
        text: "By checking Yes, you authorize the IRS to speak with the designee to answer questions about the information on the return. The designee can also:",
      },
      {
        type: "ul",
        items: [
          "Exchange information about the Form 2290 with the IRS.",
          "Request and receive written information about the Form 2290, including copies of notices, correspondence, and account transcripts.",
        ],
      },
      { type: "h2", text: "What a designee can't do" },
      {
        type: "p",
        text: "The designee can't bind you to anything — including additional tax — and can't represent you before the IRS. If you want someone to act for you beyond answering questions, that is a power of attorney; the IRS points to Pub. 947, Practice Before the IRS and Power of Attorney.",
      },
      {
        type: "callout",
        tone: "info",
        text: "Designee and paid preparer are separate roles. A preparer signs the paid-preparer section because they prepared the return; they can only discuss it with the IRS afterwards if they are also named as designee. See [Form 2290 for tax preparers](/blog/form-2290-for-tax-preparers).",
      },
      { type: "h2", text: "What you need to name one" },
      {
        type: "ol",
        items: [
          "Check Yes in the Third Party Designee section.",
          "Enter the designee's name and phone number.",
          "Enter any five digits the designee chooses as their personal identification number (PIN). They'll use it to identify themselves to the IRS.",
        ],
      },
      { type: "h2", text: "How long it lasts" },
      {
        type: "p",
        text: "The authorization applies only to the return it appears on, so a fleet that files a new return for trucks added in October needs the designee on that return too. Here the designee is saved with the business details, so each new return for that business includes it unless you remove it. Each authorization expires automatically 1 year from that return's due date, without regard to extensions.",
      },
      {
        type: "p",
        text: "To revoke it sooner, you or the designee send a written statement of revocation to the IRS at 7940 Kentucky Drive, Florence, KY 41042-2915.",
      },
      {
        type: "cta",
        heading: "Filing for a client?",
        text: "Preparer accounts save your PTIN and firm details, so naming yourself as designee takes seconds on each return.",
        label: "Create a preparer account",
        to: "/login?tab=up",
      },
      { type: "h2", text: "When it's worth doing" },
      {
        type: "ul",
        items: [
          "A preparer files for you and you want them to handle any IRS follow-up on the return.",
          "An office manager or fleet administrator handles the [stamped Schedule 1](/blog/stamped-schedule-1-proof-of-payment) and any notices.",
          "You expect questions — for example on a return with credits or amendments — and want the person who prepared it to answer them.",
        ],
      },
    ],
    faqs: [
      {
        q: "What does a third-party designee on Form 2290 allow?",
        a: "It lets the IRS discuss the return with the person you name. The designee can exchange information about the Form 2290 with the IRS and request copies of notices, correspondence, and account transcripts for it.",
      },
      {
        q: "Can a third-party designee represent me before the IRS?",
        a: "No. A designee can't bind you to anything, including additional tax, and can't represent you. That requires a power of attorney — see IRS Pub. 947.",
      },
      {
        q: "How long does a Form 2290 third-party designee authorization last?",
        a: "It applies only to that return and expires 1 year from the return's due date, without regard to extensions. You can revoke it earlier in writing.",
      },
      {
        q: "What PIN does a Form 2290 designee use?",
        a: "Any five digits the designee chooses. You enter it on the return with their name and phone number.",
      },
    ],
  },

  // CANONICAL TARGET: "form 2290 checklist" / "what do I need to file form
  // 2290". For preparers onboarding a trucking client and fleet admins.
  {
    slug: "form-2290-client-checklist",
    category: "fleets",
    title: "Form 2290 Checklist: Everything to Collect Before You File",
    metaTitle: "Form 2290 Checklist | What You Need to File 2290",
    metaDescription:
      "What you need to file Form 2290: EIN, legal name, VINs, taxable gross weight, first-used month, suspended trucks, credits, and payment. A checklist for preparers and fleets.",
    keywords: [
      "form 2290 checklist",
      "what do i need to file form 2290",
      "form 2290 requirements",
      "documents needed for form 2290",
      "form 2290 client checklist",
      "2290 filing requirements",
    ],
    excerpt:
      "Most Form 2290 rejections and delays trace back to one missing fact. Collect these before you start — for your own trucks or a new client's — and the return is a single sitting.",
    cta: {
      heading: "Got everything on the list?",
      text: "Start the return now; you can save and come back before you submit.",
      label: "Start filing",
      to: "/wizard/1",
    },
    publishedAt: "2026-10-07",
    updatedAt: "2026-10-07",
    readingMinutes: 6,
    irsVerified: true,
    sources:
      "IRS Instructions for Form 2290 (Rev. July 2026) — Getting Started, EIN, VIN, Taxable Gross Weight, Determining Taxable Gross Weight, Lines 1, 5, 7–9, How To Pay the Tax, Third Party Designee, Recordkeeping; 2290 Business Rules Publication TY2026 v1.1 — R0000-922-01",
    body: [
      {
        type: "p",
        text: "The IRS lists three things you need before you start a [Form 2290](/blog/what-is-form-2290): your EIN, each vehicle's VIN, and each vehicle's taxable gross weight. That is the minimum. For a return that goes through first time — especially for a new client or a fleet — collect the rest of this list too.",
      },
      { type: "h2", text: "The business" },
      {
        type: "table",
        headers: ["Collect", "Why it matters"],
        rows: [
          ["EIN", "Required. Form 2290 can't be filed under a Social Security number."],
          ["Legal business name, exactly as the IRS has it for that EIN", "The IRS rejects an e-filed return when the EIN and name control don't match its records (rule R0000-922-01)."],
          ["Mailing address", "Include suite or unit number. Check Address Change if it moved since the last return."],
          ["Signing authority: name, title, phone", "The return must be signed; an unsigned return isn't considered filed."],
          ["Third-party designee, if any", "Name, phone, and a 5-digit PIN — see [third-party designee on Form 2290](/blog/third-party-designee-form-2290)."],
        ],
      },
      { type: "h2", text: "Each truck" },
      {
        type: "table",
        headers: ["Collect", "Where to get it"],
        rows: [
          ["VIN", "Registration, title, or the vehicle itself. Generally 17 characters. Use the truck's VIN, not the trailer's."],
          ["Taxable gross weight", "Empty weight fully equipped, plus customary trailers, plus the maximum load customarily carried. If a state registration declares a specific gross weight, it can't be less than the highest weight declared."],
          ["Month first used on a public highway this period", "Sets the deadline and, after July, the [partial-period tax](/blog/form-2290-new-vehicle-partial-period)."],
          ["Logging vehicle?", "Logging trucks pay reduced rates; it must be registered as such under state law."],
          ["Expected highway miles", "5,000 or less (7,500 for agricultural) means it can be reported as suspended, category W."],
          ["If bought used privately", "Purchase date, and proof the seller paid this period's tax (a copy of their stamped Schedule 1)."],
        ],
      },
      {
        type: "p",
        text: "For a fleet, put all of this in the Excel template and [import it in one upload](/blog/bulk-form-2290-filing-for-fleets) rather than typing it.",
      },
      {
        type: "cta",
        heading: "Have the VINs and weights?",
        text: "That's enough to start. Add trucks one by one or import the spreadsheet.",
        label: "Start filing",
        to: "/wizard/1",
      },
      { type: "h2", text: "Last period's suspended trucks" },
      {
        type: "p",
        text: "If the business reported suspended trucks last period, the new return asks you to confirm they stayed under the mileage limit, and to list any that didn't (those owe tax on a separate return for that period). If a suspended truck was sold, you need the sale statement: the seller's and buyer's names, addresses, and EINs, the VIN, the sale date, and the odometer readings at the start of the period and at the sale.",
      },
      { type: "h2", text: "Credits to claim" },
      {
        type: "p",
        text: "For each truck sold, destroyed, or stolen since tax was paid on it: the VIN, weight category, the date, and for a sale the buyer's name and address. See [claiming the credit for a sold, destroyed, or stolen truck](/blog/form-2290-credit-sold-destroyed-stolen-truck).",
      },
      { type: "h2", text: "Payment" },
      {
        type: "table",
        headers: ["Method", "Collect beforehand"],
        rows: [
          ["Electronic funds withdrawal", "Bank routing and account number, account type"],
          ["EFTPS", "An existing EFTPS enrollment — you must enroll before you can use it"],
          ["Credit or debit card", "The card (the card processor charges a convenience fee)"],
        ],
      },
      { type: "h2", text: "Afterwards: what to keep" },
      {
        type: "p",
        text: "Keep the return, the [stamped Schedule 1](/blog/stamped-schedule-1-proof-of-payment), and the vehicle records for at least 3 years after the tax is due or paid, whichever is later — and highway mileage logs for every suspended truck.",
      },
    ],
    faqs: [
      {
        q: "What do I need to file Form 2290?",
        a: "At minimum your EIN, each vehicle's VIN, and each vehicle's taxable gross weight. You'll also need the month each truck was first used in the period, your legal business name and address, and a way to pay the tax.",
      },
      {
        q: "Can I file Form 2290 with my Social Security number?",
        a: "No. Form 2290 requires an EIN. If you don't have one, apply at IRS.gov/EIN.",
      },
      {
        q: "Why was my Form 2290 rejected for my EIN?",
        a: "Usually because the business name or name control on the return doesn't match what the IRS has for that EIN. Use the legal name exactly as the IRS issued the EIN.",
      },
      {
        q: "Where do I find a truck's VIN for Form 2290?",
        a: "On the registration, the title, or the vehicle itself. It is generally 17 characters. Use the truck's VIN, not the trailer's.",
      },
    ],
  },

  // ─────────────────────────────────────────────────────────────────────────
  // BATCH 2026-10-07 (2) — 20 filing-oriented posts for every filer type.
  // Drafted in four groups, then reviewed line by line against i2290 (Rev.
  // July 2026), the 2290 Business Rules TY2026 v1.1, IRS2290.xsd, and the app.
  // ─────────────────────────────────────────────────────────────────────────
// CANONICAL TARGET: "how to file form 2290 online" / "e-file form 2290 step
  // by step". The homepage owns the bare head term "file form 2290 online";
  // this post owns the how-to / step-by-step intent.
  {
    slug: "how-to-file-form-2290-online",
    category: "basics",
    title: "How to File Form 2290 Online: Step-by-Step E-File Guide for 2026-2027",
    metaTitle: "How to File Form 2290 Online | Step-by-Step E-File Guide",
    metaDescription:
      "How to e-file Form 2290 for 2026-2027, step by step: what to have ready, each screen of the return, paying the tax, and getting your stamped Schedule 1.",
    keywords: [
      "how to file form 2290 online",
      "e-file form 2290",
      "file 2290 online",
      "form 2290 online filing",
      "e-file 2290 step by step",
      "how to e-file 2290",
    ],
    excerpt:
      "E-filing Form 2290 is one sitting if you have three things ready. Here is every step, in the order you'll meet it, from your EIN to the stamped Schedule 1.",
    cta: {
      heading: "E-file your Form 2290 now",
      text: "Follow the steps below as you go. The tax is figured from the IRS tables, and your stamped Schedule 1 comes back once the IRS accepts the return.",
      label: "Start your Form 2290",
      to: "/wizard/1",
    },
    publishedAt: "2026-10-07",
    updatedAt: "2026-10-07",
    readingMinutes: 6,
    irsVerified: true,
    sources:
      "IRS Instructions for Form 2290 (Rev. July 2026) — Electronic filing, How To File, Getting Started, EIN, VIN, Taxable Gross Weight, When To File, How To Pay the Tax, Schedule 1 (e-file), Signature",
    body: [
      {
        type: "p",
        text: "The IRS encourages every [Form 2290](/blog/what-is-form-2290) filer to e-file, and requires it for any return reporting and paying tax on 25 or more vehicles. The practical reason is speed: once an e-filed return is accepted, the stamped Schedule 1 can be available within minutes. A paper return means waiting for it in the mail.",
      },
      {
        type: "p",
        text: "Here is the whole process, in the order you'll do it.",
      },
      { type: "h2", text: "Before you start: three things to have ready" },
      {
        type: "p",
        text: "The IRS lists exactly three things you need to complete Form 2290:",
      },
      {
        type: "ol",
        items: [
          "Your employer identification number (EIN). A Social Security number can't be used — see [getting an EIN for Form 2290](/blog/ein-for-form-2290).",
          "The VIN of each vehicle — from the registration, the title, or the vehicle itself. Use the truck's VIN, not the trailer's.",
          "The taxable gross weight of each vehicle, which sets its category. It is the truck's empty weight fully equipped, plus its customary trailers, plus the maximum load customarily carried. See [Form 2290 weight categories](/blog/form-2290-weight-categories-and-rates).",
        ],
      },
      {
        type: "p",
        text: "You'll also need the month each truck was first used on a public highway this period. That month sets both the deadline and the tax. The [due date calculator](/form-2290-due-date-calculator) shows the deadline for any month, and the [tax calculator](/form-2290-tax-calculator) shows what you'll owe.",
      },
      { type: "h2", text: "Step 1: Create your account" },
      {
        type: "p",
        text: "Sign up with your email. If you're filing for clients rather than your own trucks, choose the tax preparer option at sign-up — see [Form 2290 for tax preparers](/blog/form-2290-for-tax-preparers).",
      },
      {
        type: "cta",
        heading: "Get set up in a minute",
        text: "Creating an account is free. You only pay when you file a return.",
        label: "Create your free account",
        to: "/login?tab=up",
      },
      { type: "h2", text: "Step 2: Business details" },
      {
        type: "p",
        text: "Enter the business's legal name, EIN, business type, and address, plus the name control from your IRS EIN letter. The name control and EIN must match the IRS's records, or the IRS rejects the return (rule R0000-922-01) — see [why Form 2290 returns get rejected](/blog/form-2290-rejected-common-reasons). This is also where you name the person who signs the return, and an optional [third-party designee](/blog/third-party-designee-form-2290).",
      },
      { type: "h2", text: "Step 3: Choose the return type" },
      {
        type: "ul",
        items: [
          "Form 2290 — the regular return for vehicles first used this period.",
          "Amended return — only for an increase in taxable gross weight or a suspended vehicle that went over its mileage limit. See [Form 2290 amendments](/blog/form-2290-amendments).",
          "VIN correction — to fix a VIN on a Schedule 1 you already filed. See [Form 2290 VIN correction](/blog/form-2290-vin-correction).",
        ],
      },
      { type: "h2", text: "Step 4: Add your trucks" },
      {
        type: "p",
        text: "Pick the month the vehicles were first used, then add each truck's VIN and weight category. Mark logging vehicles (reduced rate) and suspended vehicles — those expected to run 5,000 miles or less, or 7,500 for agricultural vehicles. If you have trucks first used in different months, the IRS requires a separate return for each month. A fleet can [import trucks from a spreadsheet](/blog/bulk-form-2290-filing-for-fleets) instead of typing them.",
      },
      { type: "h2", text: "Step 5: Prior-year suspended trucks and credits" },
      {
        type: "p",
        text: "If you reported suspended trucks last period, confirm they stayed under the mileage limit, and list any you sold. Then claim any credit for a truck that was sold, destroyed, or stolen after you paid tax on it — see [claiming the Form 2290 credit](/blog/form-2290-credit-sold-destroyed-stolen-truck).",
      },
      { type: "h2", text: "Step 6: Review the tax" },
      {
        type: "p",
        text: "The tax summary shows the tax on every truck from the IRS rate tables and the balance due. Read through the full return before you pay — a mistyped VIN is the most avoidable problem on a Schedule 1.",
      },
      { type: "h2", text: "Step 7: Pay the tax" },
      {
        type: "p",
        text: "The IRS requires the tax to be paid in full with the return. On an e-filed return you can choose:",
      },
      {
        type: "ul",
        items: [
          "Electronic funds withdrawal — the tax is debited from your bank account.",
          "EFTPS — you pay separately through EFTPS, which you must be enrolled in first.",
          "Credit or debit card — you pay through IRS.gov/PayByCard; the card processor charges a fee.",
        ],
      },
      {
        type: "p",
        text: "More detail in [how to pay Form 2290 tax](/blog/how-to-pay-form-2290).",
      },
      { type: "h2", text: "Step 8: Sign, submit, and download Schedule 1" },
      {
        type: "p",
        text: "E-sign and transmit. An unsigned return isn't considered filed. When the IRS accepts the return, the watermarked Schedule 1 comes back to us electronically and you download it — that is your [stamped Schedule 1](/blog/stamped-schedule-1-proof-of-payment), the proof of payment your state asks for at registration.",
      },
      { type: "h2", text: "When to file" },
      {
        type: "p",
        text: "Form 2290 is due by the last day of the month after the month the vehicle was first used. Trucks in service in July are due by August 31. If a due date falls on a weekend or legal holiday, file by the next business day. The full chart:",
      },
      {
        type: "table",
        headers: ["Vehicle first used in", "File and pay by"],
        rows: DUE_DATE_ROWS,
      },
    ],
    faqs: [
      {
        q: "How do I file Form 2290 online?",
        a: "Through an IRS-authorized e-file provider. Enter your business details and EIN, choose the return type, add each truck's VIN, weight category, and first-used month, review the tax, pay it, then e-sign and submit. Your stamped Schedule 1 comes back once the IRS accepts the return.",
      },
      {
        q: "What do I need to e-file Form 2290?",
        a: "Your EIN, the VIN of each vehicle, and each vehicle's taxable gross weight. You'll also need the month each truck was first used on a public highway this period and a way to pay the tax.",
      },
      {
        q: "Do I have to e-file Form 2290?",
        a: "Only if the return reports and pays tax on 25 or more vehicles. Suspended category W vehicles don't count toward the 25. Everyone else may file on paper, but the IRS encourages e-filing.",
      },
      {
        q: "How fast do I get Schedule 1 after e-filing?",
        a: "The IRS says a stamped Schedule 1 can be available within minutes after an e-filed return is accepted.",
      },
    ],
  },

  // CANONICAL TARGET: "form 2290 owner operator" / "owner operator 2290
  // filing" / "leased owner operator 2290". Who is liable when a truck is
  // leased on or dual-registered is the question competitors skip.
  {
    slug: "form-2290-for-owner-operators",
    category: "basics",
    title: "Form 2290 for Owner-Operators: Who Files, What You Owe, and How to E-File",
    metaTitle: "Form 2290 for Owner-Operators | File Your HVUT Online",
    metaDescription:
      "Owner-operator? Here's when you file Form 2290, what your truck owes, who pays when you're leased on, and how to e-file and get your stamped Schedule 1 fast.",
    keywords: [
      "form 2290 owner operator",
      "owner operator 2290 filing",
      "leased owner operator 2290",
      "owner operator heavy vehicle use tax",
      "who pays 2290 leased truck",
      "file 2290 owner operator",
    ],
    excerpt:
      "For an owner-operator, Form 2290 is one short return a year — as long as you know whose name the truck is registered in, what weight category it's in, and when it was first on the road.",
    cta: {
      heading: "File your truck's Form 2290",
      text: "One truck, one sitting. See the tax before you pay, and download your stamped Schedule 1 once the IRS accepts it.",
      label: "Start your Form 2290",
      to: "/wizard/1",
    },
    publishedAt: "2026-10-07",
    updatedAt: "2026-10-07",
    readingMinutes: 6,
    irsVerified: true,
    sources:
      "IRS Instructions for Form 2290 (Rev. July 2026) — Who Must File, Dual registration, Taxable Vehicles, When To File, Line 2 (column 1 annual tax), Part II suspension, How To Pay the Tax, Schedule 1, Recordkeeping; Form 2290 page 2 tax computation table",
    body: [
      {
        type: "p",
        text: "If you own and run your own truck, [Form 2290](/blog/what-is-form-2290) is a federal return you file every period — usually once, by August 31. It reports and pays the Heavy Vehicle Use Tax, and it produces the stamped Schedule 1 your state wants before it will register the truck.",
      },
      { type: "h2", text: "Do you have to file?" },
      {
        type: "p",
        text: "You must file if a taxable highway motor vehicle is registered, or required to be registered, in your name at the time of its first use during the period, and it has a taxable gross weight of 55,000 pounds or more. It doesn't matter whether you operate as an individual, an LLC, or a corporation.",
      },
      {
        type: "p",
        text: "Taxable gross weight isn't the empty weight. It's the truck fully equipped for service, plus the trailers you customarily pull with it, plus the maximum load you customarily carry. A tractor pulling loaded trailers usually lands in a high category. See [how to work out taxable gross weight](/blog/taxable-gross-weight-form-2290).",
      },
      { type: "h2", text: "Leased on to a carrier? Who pays" },
      {
        type: "p",
        text: "The filing obligation follows registration. The IRS rule on dual registration: if a taxable vehicle is registered in the name of both the owner and another person, the owner is liable for the tax — and the same rule applies to dual registration of a leased vehicle.",
      },
      {
        type: "p",
        text: "So if you own the truck and it's registered in both your name and the carrier's, the tax is yours. Whoever actually sends the payment, the owner is the one the IRS holds liable — keep your own stamped Schedule 1 on file.",
      },
      { type: "h2", text: "What your truck owes" },
      {
        type: "p",
        text: `The tax depends on the weight category and the month the truck was first used in the July-to-June period. A truck over 75,000 pounds (category V) in service in July owes $${RATES.V[0].toFixed(2)} for the full period — the highest annual amount on the form. A logging truck in the same category owes $${RATES.V[1].toFixed(2)}.`,
      },
      {
        type: "p",
        text: "A truck first used after July pays only for the months left in the period. Put your numbers into the [Form 2290 tax calculator](/form-2290-tax-calculator) to see the exact figure, or browse the full [rate table](/rates).",
      },
      {
        type: "callout",
        tone: "info",
        text: "Running 5,000 miles or less this period (7,500 for an agricultural vehicle)? Report the truck as [suspended under category W](/blog/suspended-vehicles-category-w). No tax is due, but you still file and the truck still appears on your Schedule 1.",
      },
      { type: "h2", text: "When it's due" },
      {
        type: "p",
        text: "By the last day of the month after the month the truck was first used on a public highway. A truck already running in July is due by August 31; a truck bought and first driven in November is due by December 31. The deadline isn't tied to your registration renewal date. Check yours with the [due date calculator](/form-2290-due-date-calculator).",
      },
      {
        type: "cta",
        heading: "Know your weight and month?",
        text: "That's all you need to see the tax. Creating an account is free; you only pay when you file.",
        label: "Create your free account",
        to: "/login?tab=up",
      },
      { type: "h2", text: "How an owner-operator files online" },
      {
        type: "ol",
        items: [
          "Enter your business details: legal name, EIN, address, and the name control from your IRS EIN letter. No EIN yet? See [EIN for Form 2290](/blog/ein-for-form-2290) — an SSN won't work.",
          "Choose Form 2290 as the return type.",
          "Add your truck: VIN, weight category, and the month it was first used. Mark it logging or suspended if that applies.",
          "If you sold a truck you'd already paid on, claim the credit — see [the credit for a sold, destroyed, or stolen truck](/blog/form-2290-credit-sold-destroyed-stolen-truck).",
          "Review the tax and pay it by electronic funds withdrawal, EFTPS, or credit/debit card.",
          "E-sign and submit, then download your stamped Schedule 1 once the IRS accepts the return.",
        ],
      },
      {
        type: "p",
        text: "The step-by-step version is in [how to file Form 2290 online](/blog/how-to-file-form-2290-online).",
      },
      { type: "h2", text: "After you file" },
      {
        type: "ul",
        items: [
          "Take the [stamped Schedule 1](/blog/stamped-schedule-1-proof-of-payment) to your state when you register or renew.",
          "Moved to heavier loads mid-year and into a higher category? That's an [amendment](/blog/form-2290-weight-increase-amendment), due by the end of the following month.",
          "Typed a VIN wrong? File a [VIN correction](/blog/form-2290-vin-correction).",
          "Keep your records for at least 3 years after the tax is due or paid, whichever is later.",
        ],
      },
    ],
    faqs: [
      {
        q: "Does an owner-operator have to file Form 2290?",
        a: "Yes, if the truck is registered (or required to be registered) in your name when it's first used in the period and its taxable gross weight is 55,000 pounds or more.",
      },
      {
        q: "Who pays Form 2290 on a leased truck?",
        a: "Under the IRS dual-registration rule, if a vehicle is registered in the name of both the owner and another person, the owner is liable for the tax. The rule also applies to a leased vehicle with dual registration.",
      },
      {
        q: "How much is Form 2290 for a semi truck?",
        a: `It depends on the taxable gross weight category and the month it was first used. A truck over 75,000 pounds in service from July owes $${RATES.V[0].toFixed(2)} for the full period; trucks first used later owe less.`,
      },
      {
        q: "Can an owner-operator file Form 2290 with a Social Security number?",
        a: "No. Form 2290 requires an employer identification number (EIN).",
      },
    ],
  },

  // CANONICAL TARGET: "first time filing form 2290" / "form 2290 new truck
  // owner" / "new trucking business 2290". Top-of-funnel hub for someone who
  // has just bought their first heavy truck.
  {
    slug: "first-time-form-2290-filer",
    category: "basics",
    title: "Filing Form 2290 for the First Time: A New Truck Owner's Guide",
    metaTitle: "First Time Filing Form 2290? New Truck Owner's Guide",
    metaDescription:
      "Just bought your first heavy truck? When your first Form 2290 is due, what you need before you file, what it will cost, and how to e-file and register.",
    keywords: [
      "first time filing form 2290",
      "form 2290 new truck owner",
      "new trucking business 2290",
      "first form 2290",
      "form 2290 new truck",
      "how to file 2290 first time",
    ],
    excerpt:
      "Your first Form 2290 is due sooner than most new owners expect — the clock starts the day you drive the truck home. Here's what to sort out first, and how to file it in one sitting.",
    cta: {
      heading: "File your first Form 2290",
      text: "We walk you through each part of the return in order, and figure the tax from the IRS tables.",
      label: "Start your Form 2290",
      to: "/wizard/1",
    },
    publishedAt: "2026-10-07",
    updatedAt: "2026-10-07",
    readingMinutes: 6,
    irsVerified: true,
    sources:
      "IRS Instructions for Form 2290 (Rev. July 2026) — Who Must File, Taxable Vehicles (use, first taxable use example), When To File (Examples 1 and 2), Getting Started, EIN, Proof of payment for state registration (60-day bill of sale), Recordkeeping",
    body: [
      {
        type: "p",
        text: "New heavy truck, new business, new paperwork. [Form 2290](/blog/what-is-form-2290) is the federal return that reports the Heavy Vehicle Use Tax on trucks with a taxable gross weight of 55,000 pounds or more, and it is one of the first filings a new trucking business faces.",
      },
      { type: "h2", text: "The clock starts on the drive home" },
      {
        type: "p",
        text: "Your first Form 2290 is due by the last day of the month after the month the truck is first used on a public highway. The IRS is specific about what counts: buying a truck from a dealer and driving it home over public roads is its first taxable use.",
      },
      {
        type: "p",
        text: "The IRS's own example: a trucker buys a new truck on November 3, 2026 and drives it home that day. The return for that truck is due by December 31, 2026, and the tax covers November through June. Find your date with the [Form 2290 due date calculator](/form-2290-due-date-calculator).",
      },
      {
        type: "callout",
        tone: "info",
        text: "Registering before you've filed? The IRS says no proof of payment is required for a vehicle bought in the last 60 days if you show the state a copy of the bill of sale. You still have to file the return and pay the tax by the deadline.",
      },
      { type: "h2", text: "What you need before you start" },
      {
        type: "ol",
        items: [
          "An EIN. Form 2290 can't be filed under a Social Security number. If you don't have one, apply first — see [EIN for Form 2290](/blog/ein-for-form-2290).",
          "The truck's VIN, from the registration, the title, or the truck itself. Use the truck's VIN, not the trailer's.",
          "The truck's taxable gross weight — empty weight fully equipped, plus customary trailers, plus the maximum load customarily carried. See [taxable gross weight](/blog/taxable-gross-weight-form-2290).",
          "The month it was first used on a public highway.",
          "A way to pay: your bank account for electronic funds withdrawal, an EFTPS enrollment, or a credit or debit card.",
        ],
      },
      { type: "h2", text: "What your first return will cost" },
      {
        type: "p",
        text: "A truck first used after July pays only for the months left in the July-to-June period, so a first return mid-year costs less than a full year. Enter the weight category and month in the [Form 2290 tax calculator](/form-2290-tax-calculator) to see the exact figure from the IRS tables. Our filing fee is separate and listed on [pricing](/pricing).",
      },
      {
        type: "cta",
        heading: "Set up your trucking business once",
        text: "Save your business details and EIN now; every later return starts from them. Creating an account is free.",
        label: "Create your free account",
        to: "/login?tab=up",
      },
      { type: "h2", text: "Filing it" },
      {
        type: "ol",
        items: [
          "Enter the business: legal name, EIN, business type, address, and the name control printed on your IRS EIN letter.",
          "Choose Form 2290 as the return type.",
          "Add the truck with its VIN, weight category, and first-used month.",
          "Review the tax, pay it, then e-sign and submit.",
          "Download the stamped Schedule 1 once the IRS accepts the return, and take it to your state for registration.",
        ],
      },
      {
        type: "p",
        text: "Every screen is covered in [how to file Form 2290 online](/blog/how-to-file-form-2290-online).",
      },
      { type: "h2", text: "What happens next year" },
      {
        type: "p",
        text: "The period resets every July 1. From your second year on, every truck in service in July goes on one return due August 31, and each truck you add later in the year goes on its own return for the month you first use it. Bought a truck that someone else already paid on this period? That has its own rules — see [Form 2290 for a used truck](/blog/form-2290-used-truck-purchase).",
      },
      {
        type: "p",
        text: "Keep your records — the truck's VIN and description, when and from whom you got it, and the first month of taxable use — for at least 3 years after the tax is due or paid, whichever is later.",
      },
    ],
    faqs: [
      {
        q: "When is my first Form 2290 due on a new truck?",
        a: "By the last day of the month after the month you first used it on a public highway. Driving it home from the dealer counts as first use. A truck first driven in November is due by December 31.",
      },
      {
        q: "Can I register my new truck before filing Form 2290?",
        a: "The IRS says no proof of payment is required for a vehicle bought within the last 60 days if you give the state a copy of the bill of sale. You still have to file and pay by the deadline.",
      },
      {
        q: "Do I pay a full year of 2290 tax on a truck bought mid-year?",
        a: "No. A vehicle first used after July is taxed only for the months remaining in the July-to-June period, using the IRS partial-period table.",
      },
      {
        q: "Do I need an EIN to file my first Form 2290?",
        a: "Yes. Form 2290 can't be filed with a Social Security number. Apply for an EIN at IRS.gov/EIN before you file.",
      },
    ],
  },

  // CANONICAL TARGET: "form 2290 used truck" / "2290 for used truck
  // purchased" / "buying a used truck 2290". Owns the buyer's side; the
  // seller's credit lives in form-2290-credit-sold-destroyed-stolen-truck.
  {
    slug: "form-2290-used-truck-purchase",
    category: "basics",
    title: "Bought a Used Truck? How to File Form 2290 for It",
    metaTitle: "Form 2290 for a Used Truck You Bought | How to File",
    metaDescription:
      "Bought a used heavy truck? When your Form 2290 is due, how the tax is prorated if the seller already paid, what to get from the seller, and how to file.",
    keywords: [
      "form 2290 used truck",
      "2290 for used truck purchased",
      "buying a used truck 2290",
      "form 2290 private purchase",
      "used truck heavy vehicle use tax",
      "2290 after buying truck",
    ],
    excerpt:
      "The seller's Schedule 1 doesn't cover you. A used truck needs your own Form 2290 — but if the seller already paid this period, you may owe less. Here's how it works.",
    cta: {
      heading: "File Form 2290 for your used truck",
      text: "Add the truck's VIN and weight, pick the month, and see the tax before you pay.",
      label: "Start your Form 2290",
      to: "/wizard/1",
    },
    publishedAt: "2026-10-07",
    updatedAt: "2026-10-07",
    readingMinutes: 6,
    irsVerified: true,
    sources:
      "IRS Instructions for Form 2290 (Rev. July 2026) — Line 2 Used vehicles; Tax computation for privately purchased used vehicles (incl. example); Line 9 Sales of suspended vehicles; Suspended vehicles exceeding the mileage use limit; Proof of payment (60-day bill of sale); Recordkeeping",
    body: [
      {
        type: "p",
        text: "When you buy a used heavy truck and register it in your name, you have your own [Form 2290](/blog/what-is-form-2290) to file. The seller's stamped Schedule 1 is the seller's proof of payment, not yours.",
      },
      { type: "h2", text: "When your return is due" },
      {
        type: "p",
        text: "Like any truck: by the last day of the month after the month you first use it on a public highway during the period. Driving it home from the seller counts as use. Check the date with the [Form 2290 due date calculator](/form-2290-due-date-calculator).",
      },
      { type: "h2", text: "If the seller already paid this period" },
      {
        type: "p",
        text: "The IRS has a special rule for a used truck bought from a seller who already paid the tax for the current period. If you buy it on or after July 1, 2026 but before June 1, 2027, and your first use is in the month of the sale, your tax doesn't include the month of the sale. It's prorated from the first day of the next month to the end of the period.",
      },
      {
        type: "p",
        text: "The IRS's own example, using a category V truck (over 75,000 pounds):",
      },
      {
        type: "table",
        headers: ["Step", "Detail"],
        rows: [
          ["Seller paid the full period on July 2, 2026", "$550.00"],
          ["Buyer purchases September 9, drives it home the next day", "First use in the month of sale"],
          ["Buyer's tax: October through June", "9/12 of $550 = $412.50"],
          ["Month entered on line 1", "October 2026 (\"202610\")"],
          ["Buyer's due date", "October 31 is a Saturday, so November 2, 2026"],
        ],
      },
      {
        type: "p",
        text: "Note the due date doesn't move just because you enter the following month on line 1 — it is still based on when you actually first used the truck.",
      },
      {
        type: "p",
        text: "To use this rule you must:",
      },
      {
        type: "ul",
        items: [
          "Determine that the seller paid the tax for the current period. A copy of the seller's [stamped Schedule 1](/blog/stamped-schedule-1-proof-of-payment) is one way.",
          "Enter the month after the sale as the month on line 1.",
          "Enter the prorated tax for the rest of the period.",
        ],
      },
      {
        type: "p",
        text: "The seller, meanwhile, can claim back the tax for the months after the sale — see [the credit for a sold truck](/blog/form-2290-credit-sold-destroyed-stolen-truck). If the seller didn't pay this period, your tax is simply figured from the month you first used the truck — run it through the [Form 2290 tax calculator](/form-2290-tax-calculator).",
      },
      {
        type: "cta",
        heading: "Just bought a truck?",
        text: "Set up your business and file before the deadline. Creating an account is free.",
        label: "Create your free account",
        to: "/login?tab=up",
      },
      { type: "h2", text: "If the truck was suspended when you bought it" },
      {
        type: "p",
        text: "If the seller reported the truck as suspended (expected to run 5,000 miles or less, or 7,500 for agricultural vehicles), the seller must give you a statement showing:",
      },
      {
        type: "ul",
        items: [
          "The seller's name, address, and EIN.",
          "The VIN.",
          "The date of the sale.",
          "The odometer reading at the beginning of the period and at the time of sale.",
          "Your name, address, and EIN.",
        ],
      },
      {
        type: "p",
        text: "You attach that statement to your Form 2290 and file by the normal deadline. The mileage limit counts the truck's total highway miles for the period, including the previous owner's. If it goes over after the sale and you received the statement, the tax is yours; if the seller never gave you one, the seller is also liable. See [suspended vehicles and category W](/blog/suspended-vehicles-category-w).",
      },
      { type: "h2", text: "Registering the truck" },
      {
        type: "p",
        text: "The IRS says no proof of payment is required for a vehicle bought within the last 60 days if you show the state a copy of the bill of sale. You still have to file and pay by the deadline.",
      },
      { type: "h2", text: "Keep this in your records" },
      {
        type: "p",
        text: "When you acquire a used taxable truck, keep proof of whether it was used, or the tax suspended, before it was registered in your name. A written statement signed and dated by the seller or dealer works. Keep it with the purchase date and the seller's name and address for at least 3 years.",
      },
    ],
    faqs: [
      {
        q: "Do I need to file Form 2290 for a used truck I bought?",
        a: "Yes. If it's registered in your name and has a taxable gross weight of 55,000 pounds or more, you file your own Form 2290 by the end of the month after the month you first use it.",
      },
      {
        q: "Does the seller's Form 2290 cover me?",
        a: "No. But if the seller already paid for the current period and you first used the truck in the month of the sale, your tax is prorated from the first day of the next month.",
      },
      {
        q: "What month do I enter on Form 2290 for a privately purchased used truck?",
        a: "Under the private-purchase rule, enter the month after the sale on line 1. The due date doesn't change — it's still based on when you first used the truck.",
      },
      {
        q: "What do I need from the seller of a suspended truck?",
        a: "A statement with the seller's name, address, and EIN; the VIN; the sale date; the odometer readings at the start of the period and at the sale; and your name, address, and EIN. Attach it to your Form 2290.",
      },
    ],
  },

  // CANONICAL TARGET: "ein for form 2290" / "form 2290 without ein" / "form
  // 2290 ssn". Getting and using the EIN; the name-control rejection itself is
  // owned by form-2290-ein-name-control-rejection.
  {
    slug: "ein-for-form-2290",
    category: "basics",
    title: "EIN for Form 2290: Why You Can't Use an SSN, and How to Get One",
    metaTitle: "EIN for Form 2290 | Can't File With an SSN",
    metaDescription:
      "Form 2290 requires an EIN — a Social Security number won't work. How to apply, which entities need their own EIN, and how to file once you have it.",
    keywords: [
      "ein for form 2290",
      "form 2290 without ein",
      "form 2290 ssn",
      "can i file 2290 with ssn",
      "form 2290 ein requirement",
      "get ein for trucking",
    ],
    excerpt:
      "The single most common blocker for a first Form 2290 isn't the tax — it's the ID number. Form 2290 needs an EIN, and the IRS won't take an SSN instead.",
    cta: {
      heading: "Have your EIN? File your Form 2290",
      text: "Enter the EIN and name control exactly as on your IRS letter, add your trucks, and e-file.",
      label: "Start your Form 2290",
      to: "/wizard/1",
    },
    publishedAt: "2026-10-07",
    updatedAt: "2026-10-07",
    readingMinutes: 5,
    irsVerified: true,
    sources:
      "IRS Instructions for Form 2290 (Rev. July 2026) — Getting Started, Employer Identification Number (EIN), Disregarded entities and qualified subchapter S subsidiaries, Schedule 1 name and address; 2290 Business Rules Publication TY2026 v1.1 — R0000-922-01",
    body: [
      {
        type: "p",
        text: "The IRS's instruction is short: you must have an employer identification number (EIN) to file [Form 2290](/blog/what-is-form-2290). You can't use your Social Security number. That applies to a one-truck sole proprietor exactly as it does to a fleet.",
      },
      { type: "h2", text: "How to get an EIN" },
      {
        type: "p",
        text: "The IRS gives these routes:",
      },
      {
        type: "ul",
        items: [
          "Online at IRS.gov/EIN.",
          "By fax or mail, using Form SS-4, Application for Employer Identification Number.",
          "By phone at 267-941-1099 (not toll-free) — but only for applicants with an address in a foreign country, such as Canada.",
        ],
      },
      {
        type: "callout",
        tone: "info",
        text: "Already have an EIN for payroll or another business purpose? If it belongs to the person or entity the truck is registered to, that's the EIN you file with. Make sure it's the right entity — see below.",
      },
      { type: "h2", text: "LLCs and S corporation subsidiaries: their own EIN, not the owner's" },
      {
        type: "p",
        text: "If your trucks are in a single-owner LLC or another eligible disregarded entity, or a qualified subchapter S subsidiary (QSub), the IRS treats that entity as separate for excise tax purposes. It must report and pay the tax, and claim any credits or refunds, under the entity's own EIN — not the owner's taxpayer identification number. If you aren't sure whether your entity already has an EIN, the IRS points you to its Business and Specialty Tax line at 800-829-4933.",
      },
      { type: "h2", text: "The EIN has to match the IRS's records" },
      {
        type: "p",
        text: "An e-filed Form 2290 is checked against the IRS's e-file database: the EIN and the name control in the return must match what the IRS has on file, or the return is rejected (business rule R0000-922-01). The name control is printed on the letter the IRS sent when it issued your EIN. Enter the legal name and EIN exactly as the IRS has them — the most common way this goes wrong is covered in [the EIN name control rejection](/blog/form-2290-ein-name-control-rejection).",
      },
      {
        type: "p",
        text: "Use the same name, address, and EIN on Schedule 1 as on the return itself.",
      },
      {
        type: "cta",
        heading: "Save your EIN once",
        text: "Add your business and EIN to a free account; every return you file after that starts from it.",
        label: "Create your free account",
        to: "/login?tab=up",
      },
      { type: "h2", text: "Filing once you have the EIN" },
      {
        type: "ol",
        items: [
          "Enter the business's legal name, EIN, business type, and address, plus the name control from your IRS EIN letter.",
          "Add each truck with its VIN, weight category, and the month it was first used — the [Form 2290 tax calculator](/form-2290-tax-calculator) shows the tax first if you want it.",
          "Pay the tax, e-sign, and submit. Download the stamped Schedule 1 once the IRS accepts the return.",
        ],
      },
      {
        type: "p",
        text: "Every step is in [how to file Form 2290 online](/blog/how-to-file-form-2290-online). And if you're waiting on an EIN as a deadline approaches, check your exact date with the [Form 2290 due date calculator](/form-2290-due-date-calculator) — the deadline doesn't move while the EIN is pending.",
      },
    ],
    faqs: [
      {
        q: "Can I file Form 2290 with my Social Security number?",
        a: "No. The IRS requires an employer identification number (EIN) for Form 2290. You can't use an SSN.",
      },
      {
        q: "How do I get an EIN for Form 2290?",
        a: "Apply online at IRS.gov/EIN, or fax or mail Form SS-4. Applicants with an address in a foreign country, such as Canada, can also apply by calling 267-941-1099.",
      },
      {
        q: "My trucks are in a single-member LLC. Whose EIN do I use?",
        a: "The LLC's. The IRS treats eligible single-owner disregarded entities and QSubs as separate entities for excise tax, so they report and pay under their own EIN, not the owner's.",
      },
      {
        q: "Why was my Form 2290 rejected for my EIN?",
        a: "The EIN and name control on an e-filed return must match the IRS e-file database (rule R0000-922-01). Enter the legal name and name control exactly as on your IRS EIN letter.",
      },
    ],
  },
// CANONICAL TARGET: "form 2290 amendment weight increase" / "amended 2290
  // return weight". The form-2290-amendments post stays the overview; this one
  // owns the Line 3 worksheet procedure end to end. Example figures are the
  // IRS's own printed Table I values (T and V, January column) - not computed.
  {
    slug: "form-2290-weight-increase-amendment",
    category: "amendments",
    title: "Form 2290 Weight Increase Amendment: How to File It and What You'll Owe",
    metaTitle: "Form 2290 Weight Increase Amendment | File Online",
    metaDescription:
      "Truck moved into a heavier weight category? File an amended Form 2290 by the end of the next month. The Line 3 worksheet, a worked example, and how to file it.",
    keywords: [
      "form 2290 amendment weight increase",
      "file 2290 amendment online",
      "amended 2290 return weight",
      "taxable gross weight increase 2290",
      "form 2290 line 3",
      "2290 additional tax weight",
    ],
    excerpt:
      "When a truck's taxable gross weight climbs into a higher category mid-period, you owe the difference for the months left. Here is the IRS worksheet, a worked example, and how to file the amendment in one sitting.",
    cta: {
      heading: "File your weight increase amendment",
      text: "Pick the month the weight went up, the old and new category, and the VIN. The additional tax is figured from the IRS partial-period table.",
      label: "Start the amendment",
      to: "/wizard/1",
    },
    publishedAt: "2026-10-07",
    updatedAt: "2026-10-07",
    readingMinutes: 6,
    irsVerified: true,
    sources:
      "IRS Instructions for Form 2290 (Rev. July 2026) — Purpose of Form, Amended return, Line 3 and its worksheet, When To File chart, Partial-Period Tax Tables (Table I); 2290 Business Rules Publication TY2026 v1.1 — F2290-002-01, F2290-003-01, F2290-018-01, F2290-033-01",
    body: [
      {
        type: "p",
        text: "A [Form 2290](/blog/what-is-form-2290) is filed for the weight category a truck is in when it's first used. If its taxable gross weight later goes up and it lands in a new, heavier category, the IRS wants the extra tax for the rest of the period. That is a taxable gross weight increase amendment, and it is one of only two reasons the IRS lets you check the Amended Return box — the other is a [suspended vehicle that went over its mileage limit](/blog/form-2290-suspended-vehicle-over-mileage).",
      },
      { type: "h2", text: "What counts as a weight increase" },
      {
        type: "p",
        text: "Taxable gross weight is the truck fully equipped for service, plus the trailers customarily used with it, plus the maximum load customarily carried. The IRS's own example of an increase is a change in the maximum load customarily carried. If a change like that moves the truck into a higher category, you file. If it stays in the same category, there is nothing to report. See [how to work out taxable gross weight](/blog/taxable-gross-weight-form-2290) and the [weight category table](/blog/form-2290-weight-categories-and-rates).",
      },
      {
        type: "callout",
        tone: "warn",
        text: "It only works one way. The IRS allows no credit, lower tax, or refund for an occasional light or decreased load, or for a discontinued or changed use of the vehicle.",
      },
      { type: "h2", text: "When the amendment is due" },
      {
        type: "p",
        text: "File the amended Form 2290 and Schedule 1 by the last day of the month following the month the taxable gross weight increased. As with every 2290 deadline, a date that falls on a Saturday, Sunday, or legal holiday moves to the next business day — the [Form 2290 due date calculator](/form-2290-due-date-calculator) shows the IRS dates for each month.",
      },
      { type: "h2", text: "How the additional tax is figured" },
      {
        type: "p",
        text: "The IRS gives a four-line worksheet, and you attach a copy for each vehicle:",
      },
      {
        type: "ol",
        items: [
          "Enter the month the taxable gross weight increased. Write the same month next to the Amended Return box on page 1.",
          "Find that month in the IRS Partial-Period Tax Tables and read down to the new category. That is the new tax.",
          "Under the same month, find the tax for the category you previously reported.",
          "Subtract line 3 from line 2. That is the additional tax, entered on Form 2290, line 3.",
        ],
      },
      {
        type: "p",
        text: "A worked example using the IRS's printed Table I figures: a truck reported in category T has its maximum load increased in January 2027, putting it in category V.",
      },
      {
        type: "table",
        headers: ["Worksheet line", "Amount"],
        rows: [
          ["1. Month the weight increased", "January 2027"],
          ["2. New category V, January column (Table I)", "$275.00"],
          ["3. Previous category T, January column (Table I)", "$259.00"],
          ["4. Additional tax (line 2 − line 3), to Form 2290 line 3", "$16.00"],
        ],
      },
      {
        type: "p",
        text: "That amendment is due by the end of February 2027 — and because February 28, 2027 is a Sunday, the IRS chart moves it to March 1, 2027. Logging vehicles use the logging table (Table II) instead.",
      },
      {
        type: "callout",
        tone: "info",
        text: `One exception: if the increase happens in July, after you've already filed, use the full-period amounts on Form 2290 page 2 for the new category instead of the partial-period tables. For example, moving from category T to V in July is $${RATES.V[0].toFixed(2)} − $${RATES.T[0].toFixed(2)}.`,
      },
      {
        type: "cta",
        heading: "Weight went up this month?",
        text: "The amendment is a short return: reason, month, VIN, old and new category. Create a free account to start it.",
        label: "Create your free account",
        to: "/login?tab=up",
      },
      { type: "h2", text: "Filling in the return" },
      {
        type: "ul",
        items: [
          "Report the additional tax on line 3, not line 2. Only put tax on line 2 if you're also reporting other taxable vehicles on the same return.",
          "Check the Amended Return box and write the month the weight increased next to it.",
          "List the vehicle on Schedule 1 — an amended return with no VIN on Schedule 1 is rejected (rule F2290-033-01).",
          "Attach the worksheet. The IRS rejects an e-filed return with an amount on line 3 but no worksheet (F2290-002-01), without the Amended Return box checked (F2290-003-01), or where the worksheet's additional tax isn't line 2 minus line 3 (F2290-018-01).",
        ],
      },
      { type: "h2", text: "How to file it here" },
      {
        type: "ol",
        items: [
          "Start a return for the business and choose Amended return.",
          "Pick the reason: Taxable gross weight increased.",
          "Enter the month of the increase, each truck's VIN, its previous category, and its new category. Several trucks can go on one amendment.",
          "The additional tax is figured from the IRS partial-period table, line by line, and the worksheet goes with the return.",
          "Pay, e-sign, and submit. The accepted amendment comes back with a [stamped Schedule 1](/blog/stamped-schedule-1-proof-of-payment).",
        ],
      },
      {
        type: "p",
        text: "For the other amendment types and how they differ from a [VIN correction](/blog/form-2290-vin-correction), see the [Form 2290 amendments guide](/blog/form-2290-amendments).",
      },
    ],
    faqs: [
      {
        q: "When is a Form 2290 weight increase amendment due?",
        a: "By the last day of the month following the month the taxable gross weight increased. If that day is a Saturday, Sunday, or legal holiday, it moves to the next business day.",
      },
      {
        q: "How do I calculate the additional tax for a weight increase?",
        a: "Look up the month of the increase in the IRS Partial-Period Tax Tables. Subtract the tax for the old category from the tax for the new category. For an increase in July after you filed, use the full-period amounts on Form 2290 page 2 instead.",
      },
      {
        q: "Which line does the additional tax go on?",
        a: "Line 3 of Form 2290, with the Amended Return box checked and the month of the increase written next to it. Don't put it on line 2.",
      },
      {
        q: "Can I get money back if my truck's weight goes down?",
        a: "No. The IRS allows no credit, lower tax, or refund for a lighter or decreased load or a changed use of the vehicle.",
      },
    ],
  },

  // CANONICAL TARGET: "2290 suspended vehicle exceeded mileage" / "category w
  // exceeded 5000 miles". suspended-vehicles-category-w stays the overview of
  // suspension; this one owns what to file when the limit is crossed.
  {
    slug: "form-2290-suspended-vehicle-over-mileage",
    category: "amendments",
    title: "Suspended Truck Went Over 5,000 Miles? How to File the Form 2290 Amendment",
    metaTitle: "2290 Suspended Vehicle Over Mileage | File the Amendment",
    metaDescription:
      "A category W truck that passes 5,000 miles (7,500 agricultural) owes Form 2290 tax. How the tax is figured, when the amended return is due, and how to file it.",
    keywords: [
      "2290 suspended vehicle exceeded mileage",
      "form 2290 amendment mileage",
      "category w exceeded 5000 miles",
      "suspended vehicle over 5000 miles 2290",
      "form 2290 mileage exceeded",
      "file amended 2290 mileage",
    ],
    excerpt:
      "Suspension only lasts while the truck stays under the mileage limit. Cross it and the tax for the period becomes due — here is how much, by when, and how to file the amendment.",
    cta: {
      heading: "File your mileage-exceeded amendment",
      text: "Pick the month the limit was passed and add the trucks. The tax is figured from the month each was first used.",
      label: "Start the amendment",
      to: "/wizard/1",
    },
    publishedAt: "2026-10-07",
    updatedAt: "2026-10-07",
    readingMinutes: 6,
    irsVerified: true,
    sources:
      "IRS Instructions for Form 2290 (Rev. July 2026) — Purpose of Form, Amended return, Line 8, Line 9 (sales), Suspended vehicles exceeding the mileage use limit, Agricultural vehicles, Recordkeeping, When To File chart; 2290 Business Rules Publication TY2026 v1.1 — F2290-009-01, F2290-010-01, F2290-033-01",
    body: [
      {
        type: "p",
        text: "A truck you expect to drive 5,000 miles or less on public highways during the period — 7,500 miles or less for agricultural vehicles — can be reported as [suspended, category W](/blog/suspended-vehicles-category-w), with no tax. The suspension is conditional. Once the truck goes over the mileage limit, the tax becomes due, and you file an amended Form 2290 to pay it.",
      },
      { type: "h2", text: "What the mileage limit counts" },
      {
        type: "ul",
        items: [
          "Use on public highways only. For agricultural vehicles, miles driven on the farm don't count toward the 7,500 — but keep accurate records of those farm miles.",
          "The total for the whole period, regardless of how many owners the truck had. A buyer inherits the miles the seller already put on it.",
        ],
      },
      { type: "h2", text: "How much tax you owe" },
      {
        type: "p",
        text: "Figure the tax on Form 2290 page 2 based on the month the truck was first used in the period — not the month it crossed the limit. A category V truck first used in July that passes 5,000 miles in February owes the full-period amount for its category:",
      },
      {
        type: "table",
        headers: ["Step", "Detail"],
        rows: [
          ["Month first used in the period", "July 2026"],
          ["Month the mileage limit was exceeded", "February 2027"],
          ["Tax (Form 2290 page 2, category V, full period)", `$${RATES.V[0].toFixed(2)}`],
          ["Amended return due", "End of March 2027 — March 31, 2027 on the IRS chart"],
        ],
      },
      {
        type: "p",
        text: "A truck first used after July pays the partial-period amount for its first-used month instead; the [Form 2290 tax calculator](/form-2290-tax-calculator) looks it up from the IRS tables.",
      },
      { type: "h2", text: "When it's due" },
      {
        type: "p",
        text: "File the amended Form 2290 and Schedule 1 by the last day of the month following the month the mileage limit was exceeded. Weekend and holiday dates move to the next business day.",
      },
      { type: "h2", text: "Filling in the return" },
      {
        type: "ul",
        items: [
          "Report the tax on line 2.",
          "Check the Amended Return box and write the month the mileage limit was exceeded next to it.",
          "Don't complete Part II unless you are also reporting other category W trucks on the same return.",
          "List the truck on Schedule 1 — the IRS rejects an amended return whose Schedule 1 has no VIN (rule F2290-033-01).",
        ],
      },
      {
        type: "cta",
        heading: "Truck passed the limit?",
        text: "Create a free account and file the amendment before the end of next month.",
        label: "Create your free account",
        to: "/login?tab=up",
      },
      { type: "h2", text: "If you only find out at the start of the next period" },
      {
        type: "p",
        text: "The new period's Form 2290 asks you to verify last period's suspended trucks. Checking box 8a confirms they stayed under the limit, except any VINs you list on line 8b. Every truck on line 8b owes the prior period's tax on a separate Form 2290 for that prior period. On an e-filed return, 8a and 8b go together: box 8a with a line 8b statement, and a line 8b statement only with box 8a (rules F2290-009-01 and F2290-010-01).",
      },
      { type: "h2", text: "If the suspended truck was sold" },
      {
        type: "p",
        text: "The seller must give the buyer a statement with the seller's name, address, and EIN; the VIN; the sale date; the odometer reading at the start of the period and at the sale; and the buyer's name, address, and EIN. The buyer attaches it to their Form 2290.",
      },
      {
        type: "p",
        text: "If the truck then goes over the limit — counting the seller's highway miles — and the seller gave that statement, the buyer owes the tax. If the seller didn't, the seller is also liable for the period's tax. Buying one? See [buying a used truck and filing Form 2290](/blog/form-2290-used-truck-purchase).",
      },
      { type: "h2", text: "How to file it here" },
      {
        type: "ol",
        items: [
          "Start a return for the business and choose Amended return.",
          "Pick the reason: Suspended vehicle went over its mileage limit.",
          "Enter the month the trucks were first used and the month the limit was exceeded, then add each truck — VIN, weight category, and logging status. A fleet can bulk-upload them.",
          "Review the tax, pay, e-sign, and submit. The accepted return comes back with a [stamped Schedule 1](/blog/stamped-schedule-1-proof-of-payment).",
        ],
      },
      {
        type: "p",
        text: "Keep the highway mileage records for every suspended truck for at least 3 years after the end of the period the suspension covers. For how this fits with the other amendment types, see the [Form 2290 amendments guide](/blog/form-2290-amendments).",
      },
    ],
    faqs: [
      {
        q: "What happens if a suspended truck goes over 5,000 miles?",
        a: "The tax becomes due. File an amended Form 2290 with the tax figured from the month the truck was first used in the period, by the last day of the month after the month it went over the limit.",
      },
      {
        q: "Do farm miles count toward the 7,500-mile limit for agricultural vehicles?",
        a: "No. Only public highway miles count. Keep accurate records of the miles driven on the farm.",
      },
      {
        q: "Does the mileage limit reset when a suspended truck is sold?",
        a: "No. The limit applies to the total highway miles in the period regardless of the number of owners, so the buyer counts the seller's miles too.",
      },
      {
        q: "I only noticed after the period ended. What do I file?",
        a: "On the new period's Form 2290, list the truck's VIN on line 8b, and file a separate Form 2290 for the prior period to report and pay that period's tax.",
      },
    ],
  },

  // CANONICAL TARGET: "form 2290 rejected" / "2290 rejection codes". Rule ids
  // and wording from the TY2026 Business Rules Publication v1.1; the plain-
  // language reading matches IrsRuleClassificationRegistry (form2290-api), which
  // also drives the in-app rejection modal (RejectionErrorModal.jsx).
  {
    slug: "form-2290-rejected-common-reasons",
    category: "basics",
    title: "Form 2290 Rejected? Common IRS Reject Codes and How to Fix and Refile",
    metaTitle: "Form 2290 Rejected? Reject Codes and How to Fix Them",
    metaDescription:
      "Form 2290 rejected by the IRS? What the most common reject codes mean — EIN and name control, duplicate VIN, payment date, month first used — and how to refile.",
    keywords: [
      "form 2290 rejected",
      "2290 rejection codes",
      "form 2290 reject code fix",
      "2290 rejected return",
      "form 2290 rejection",
      "refile form 2290",
    ],
    excerpt:
      "A rejected Form 2290 hasn't been accepted — so there's no stamped Schedule 1 yet. Most rejections come from a handful of IRS rules, and nearly all of them are fixed by correcting one field and submitting again.",
    cta: {
      heading: "Fix the rejection and refile",
      text: "Open the rejected return to see the IRS rule in plain language, the fix, and a button straight to the step that needs it.",
      label: "Go to my filings",
      to: "/dashboard",
    },
    publishedAt: "2026-10-07",
    updatedAt: "2026-10-07",
    readingMinutes: 7,
    irsVerified: true,
    sources:
      "2290 Business Rules Publication TY2026 v1.1 — R0000-922-01, F2290-017, F2290-026-01, F2290-077, F2290-068-01, FPYMT-076-02, R0000-906-03, F2290-004-01, F2290-033-01; IRS Instructions for Form 2290 (Rev. July 2026) — Employer Identification Number, Vehicle Identification Number, When To File, How To Pay the Tax, Line 5",
    body: [
      {
        type: "p",
        text: "When the IRS rejects an e-filed [Form 2290](/blog/what-is-form-2290), it hasn't accepted the return, so there is no [stamped Schedule 1](/blog/stamped-schedule-1-proof-of-payment) to take to the DMV yet. Every rejection names the IRS business rule the return broke. Below are the ones filers run into most, with the IRS rule text summarized and what to change.",
      },
      {
        type: "callout",
        tone: "info",
        text: "Looking for a specific code? The [Form 2290 reject code library](/form-2290-reject-codes) lists every active IRS rule for 2026–2027 in the IRS's own words, with a step-by-step fix for each one you can correct yourself.",
      },
      { type: "h2", text: "EIN and name control don't match (R0000-922-01)" },
      {
        type: "p",
        text: "The rule: the filer's EIN and name control must match the IRS e-file database. The name control is a short code — up to four characters — the IRS derives from the business name and ties to the EIN. If the business name or the EIN on the return doesn't match what the IRS has, the return is rejected.",
      },
      {
        type: "p",
        text: "Fix: enter the EIN exactly as issued and the name control printed on the IRS EIN letter. This is the most common rejection by far, so it has its own guide: [EIN and name control rejection](/blog/form-2290-ein-name-control-rejection).",
      },
      { type: "h2", text: "The same VIN appears twice (F2290-017)" },
      {
        type: "p",
        text: "The rule: the VINs on Schedule 1 must not contain duplicates anywhere on the form. It usually comes from a pasted spreadsheet. Fix: remove the repeated row, or correct it if it was meant to be a different truck.",
      },
      { type: "h2", text: "This VIN was already filed (F2290-026-01)" },
      {
        type: "p",
        text: "The rule: for the same EIN and tax period, the first VIN on Schedule 1 and its weight category must not match a Form 2290 the IRS has already accepted. In practice: the truck was already filed this period, and a second original return is being sent for it.",
      },
      {
        type: "p",
        text: "Fix: don't refile the same truck. If the earlier return had a mistyped VIN, file a [VIN correction](/blog/form-2290-vin-correction). If the truck's weight went up, file a [weight increase amendment](/blog/form-2290-weight-increase-amendment). If it's a different truck, check the VIN against the registration or title.",
      },
      { type: "h2", text: "Month first used is out of range (F2290-077)" },
      {
        type: "p",
        text: "The rule: the first-used date on a 2026–2027 return must be between July 2026 and June 2027. Fix: enter the month the truck was first used on a public highway in this period — the [due date calculator](/form-2290-due-date-calculator) lists them all.",
      },
      {
        type: "cta",
        heading: "Rejected return waiting?",
        text: "Correct the field the IRS flagged and submit again from your dashboard.",
        label: "Go to my filings",
        to: "/dashboard",
      },
      { type: "h2", text: "Payment problems" },
      {
        type: "table",
        headers: ["Rule", "What it means", "Fix"],
        rows: [
          ["F2290-068-01", "There's a balance due, but no payment method was indicated (EFTPS, card, or direct debit).", "Choose a payment method and resubmit."],
          ["FPYMT-076-02", "The requested direct-debit payment date must equal the date the IRS receives the return.", "Re-save the bank payment so the date is set to the received date, then resubmit."],
          ["R0000-906-03", "The bank routing number isn't in the IRS database.", "Check the 9-digit routing number with your bank."],
        ],
      },
      {
        type: "p",
        text: "More on each method in [how to pay Form 2290](/blog/how-to-pay-form-2290).",
      },
      { type: "h2", text: "Credits and amendments" },
      {
        type: "ul",
        items: [
          "F2290-004-01 — credits on line 5 can't be more than the tax on line 4. Reduce the credit; claim the excess as a refund on Form 8849. See [claiming a credit for a sold, destroyed, or stolen truck](/blog/form-2290-credit-sold-destroyed-stolen-truck).",
          "F2290-033-01 — an amended return must list at least one VIN on Schedule 1.",
        ],
      },
      { type: "h2", text: "How to fix and refile here" },
      {
        type: "ol",
        items: [
          "Open My Filings and select the rejected return.",
          "Each IRS rule on the rejection is shown with a plain-language explanation and the steps to fix it.",
          "Use the button on the error to jump straight to the step that owns the data — Business, Trucks, Credits, or Payment.",
          "Correct it, review the return, and submit again.",
        ],
      },
      {
        type: "p",
        text: "A few rules can't be fixed by editing the return — for example ones about the e-file software or transmission itself. Those are marked as such, with a prompt to contact support.",
      },
      {
        type: "p",
        text: "Haven't filed yet? Most of these are avoidable — the [Form 2290 checklist](/blog/form-2290-client-checklist) covers what to confirm before you submit. [Create your free account](/login?tab=up) to start.",
      },
    ],
    faqs: [
      {
        q: "Why was my Form 2290 rejected?",
        a: "Every rejection names the IRS business rule the return broke. The most common are an EIN and name control that don't match IRS records (R0000-922-01), a duplicate VIN (F2290-017), a VIN already filed this period (F2290-026-01), and payment details (F2290-068-01, FPYMT-076-02).",
      },
      {
        q: "Do I get a Schedule 1 if my Form 2290 is rejected?",
        a: "No. A stamped Schedule 1 only comes back once the IRS accepts the return. Fix the rejection and submit again.",
      },
      {
        q: "What does reject code F2290-026-01 mean?",
        a: "The first VIN on Schedule 1 and its weight category match a Form 2290 the IRS already accepted for the same EIN and tax period. Don't file that truck again; use a VIN correction or amendment if something changed.",
      },
      {
        q: "How do I fix rejection R0000-922-01?",
        a: "Make the EIN and name control on the return match IRS records: the EIN exactly as issued and the name control printed on your IRS EIN letter.",
      },
    ],
  },

  // CANONICAL TARGET: "ein name control mismatch 2290" / "form 2290 rejected
  // ein". Name-control rules quoted from IRS Pub 4164 (Rev. 10-2024) Exhibits
  // 1, 4 and 5 as saved in LLC/notes/irs-business-name-control-rules.md - IRS
  // examples only, no customer names. The UI field + hint is BusinessStep.jsx.
  {
    slug: "form-2290-ein-name-control-rejection",
    category: "basics",
    title: "Form 2290 Rejected for EIN or Name Control (R0000-922-01): How to Fix It",
    metaTitle: "2290 EIN Name Control Mismatch (R0000-922-01) | Fix",
    metaDescription:
      "Form 2290 rejected because the EIN and name control don't match IRS records? What a name control is, where to find yours, and how to fix and refile today.",
    keywords: [
      "ein name control mismatch 2290",
      "form 2290 rejected ein",
      "2290 name control",
      "r0000-922-01",
      "irs name control business",
      "form 2290 ein rejection",
    ],
    excerpt:
      "The single most common Form 2290 rejection is a mismatch between the EIN and the name control the IRS has on file. The fix is usually one four-letter code — here is how to find the right one.",
    cta: {
      heading: "Correct the name control and refile",
      text: "Update the business details with the code on your IRS EIN letter, then submit the return again.",
      label: "Go to my filings",
      to: "/dashboard",
    },
    publishedAt: "2026-10-07",
    updatedAt: "2026-10-07",
    readingMinutes: 5,
    irsVerified: true,
    sources:
      "2290 Business Rules Publication TY2026 v1.1 — R0000-922-01; IRS Publication 4164 (Rev. 10-2024) — name control character set, Exhibit 1, Exhibit 4 (which names Form 2290), Exhibit 5; IRS Instructions for Form 2290 (Rev. July 2026) — Employer Identification Number, Name and Address",
    body: [
      {
        type: "p",
        text: "IRS rule R0000-922-01 says the filer's EIN and name control in the return must match the IRS e-file database. When they don't, the [Form 2290](/blog/what-is-form-2290) is rejected and no stamped Schedule 1 comes back. It is a data problem, not a tax problem — fix the two values and submit again.",
      },
      { type: "h2", text: "What a name control is" },
      {
        type: "p",
        text: "A name control is a short code — up to four characters — that the IRS derives from the business name and stores with the EIN. Only letters, numbers, the hyphen (-), and the ampersand (&) are allowed; other characters in the name are dropped, and \"DBA\" is never part of it.",
      },
      {
        type: "p",
        text: "The IRS assigned one is what counts. It is printed on the letter the IRS sent when it issued the EIN (the CP 575). Use that code rather than working it out yourself.",
      },
      { type: "h2", text: "How the IRS forms it" },
      {
        type: "p",
        text: "IRS Publication 4164 has the rules, in an exhibit that names Form 2290 directly. Its own examples:",
      },
      {
        type: "table",
        headers: ["Business name", "Name control", "Rule"],
        rows: [
          ["Sumac Field Plow Inc", "SUMA", "Corporations, LLCs and similar: first four significant characters"],
          ["P&P Company", "P&PC", "The ampersand counts"],
          ["The Willow Co.", "WILL", "A leading \"The\" is dropped when more than one word follows"],
          ["The Hawthorn", "THEH", "…but kept when only one word follows"],
          ["Arthur P Aspen (sole proprietor)", "ASPE", "One-name sole proprietor: the last name"],
          ["Juan Garza Morales", "GARZ", "Two Spanish surnames: the first one"],
        ],
      },
      {
        type: "callout",
        tone: "info",
        text: "Sole proprietors are where most mismatches start, because the right surname can't be told from the name alone. If you're a sole proprietor, copy the code from your EIN letter rather than guessing.",
      },
      {
        type: "cta",
        heading: "Have your EIN letter handy?",
        text: "Enter the EIN and name control exactly as printed, and the return goes through on the next submit.",
        label: "Fix my filing",
        to: "/dashboard",
      },
      { type: "h2", text: "How to fix it" },
      {
        type: "ol",
        items: [
          "Find your IRS EIN letter. Check the EIN digit by digit and note the name control.",
          "Open the rejected return in My Filings. The rejection shows R0000-922-01 with the fix and a button to the business details.",
          "Correct the EIN and the name control. The name control field accepts only the allowed characters, up to four.",
          "Make sure the business name is the legal name the EIN was issued to.",
          "Save, review the return, and submit again.",
        ],
      },
      { type: "h2", text: "Where to get help" },
      {
        type: "p",
        text: "If you can't find the letter or aren't sure of the name control, the IRS Business and Specialty Tax line is 800-829-4933. Don't have an EIN at all? A Form 2290 can't be filed with a Social Security number — see [getting an EIN for Form 2290](/blog/ein-for-form-2290).",
      },
      {
        type: "p",
        text: "Other rejection codes are covered in [common Form 2290 reject codes](/blog/form-2290-rejected-common-reasons). Filing for the first time? [Create your free account](/login?tab=up) and enter the name control from your letter on the business step — it saves a rejection.",
      },
    ],
    faqs: [
      {
        q: "What does R0000-922-01 mean on Form 2290?",
        a: "The EIN and name control on the return don't match the IRS e-file database. Enter the EIN exactly as issued and the name control printed on your IRS EIN letter, then submit again.",
      },
      {
        q: "Where do I find my business name control?",
        a: "On the letter the IRS sent when it issued your EIN (CP 575). If you can't find it, call the IRS Business and Specialty Tax line at 800-829-4933.",
      },
      {
        q: "What characters can a name control contain?",
        a: "Up to four characters: letters A-Z, numbers 0-9, the hyphen, and the ampersand. Other characters in the name are dropped, and DBA is never included.",
      },
      {
        q: "Is a sole proprietor's name control their first name?",
        a: "IRS Publication 4164 bases a one-name sole proprietor's control on the last name, with special rules for some surnames. The safest source is the code on your EIN letter.",
      },
    ],
  },

  // CANONICAL TARGET: "how to pay form 2290" / "pay 2290 tax online". Four IRS
  // methods from i2290 How To Pay the Tax; what the app offers from PayStep.jsx
  // (EFW, EFTPS, card - no check option in the app).
  {
    slug: "how-to-pay-form-2290",
    category: "basics",
    title: "How to Pay Form 2290 Tax: EFW, EFTPS, Card, or Check",
    metaTitle: "How to Pay Form 2290 Tax Online | EFW, EFTPS, Card",
    metaDescription:
      "Four ways to pay your Form 2290 tax: direct debit when you e-file, EFTPS, credit or debit card, or check with Form 2290-V. Rules, timing, and which to choose.",
    keywords: [
      "how to pay form 2290",
      "pay 2290 tax online",
      "eftps vs efw 2290",
      "pay form 2290 credit card",
      "form 2290 payment options",
      "form 2290 direct debit",
    ],
    excerpt:
      "The IRS wants the Form 2290 tax paid in full with the return. There are four ways to do it — here is how each works, the rules that trip people up, and which ones you can use when you e-file.",
    cta: {
      heading: "File and pay in one sitting",
      text: "Choose direct debit, EFTPS, or card when you file. Nothing owed? No payment method needed.",
      label: "Start your Form 2290",
      to: "/wizard/1",
    },
    publishedAt: "2026-10-07",
    updatedAt: "2026-10-07",
    readingMinutes: 6,
    irsVerified: true,
    sources:
      "IRS Instructions for Form 2290 (Rev. July 2026) — How To Pay the Tax (EFW, EFTPS, credit or debit card, check or money order, payment voucher), Where To File; 2290 Business Rules Publication TY2026 v1.1 — F2290-068-01, FPYMT-076-02, R0000-906-03",
    body: [
      {
        type: "p",
        text: "The rule underneath every option: you must pay the tax in full with your [Form 2290](/blog/what-is-form-2290). The IRS lists four ways to do it.",
      },
      {
        type: "table",
        headers: ["Method", "How it works", "Available when you e-file here"],
        rows: [
          ["Electronic funds withdrawal (direct debit)", "You authorize the IRS to debit your bank account as part of the e-filed return.", "Yes"],
          ["EFTPS", "You pay separately through the Treasury's Electronic Federal Tax Payment System and check the EFTPS box on the return.", "Yes"],
          ["Credit or debit card", "You pay through an IRS-approved card processor at IRS.gov/PayByCard and check the card box on the return.", "Yes"],
          ["Check or money order", "You mail the payment with Form 2290-V, the payment voucher.", "No — paper payment only"],
        ],
      },
      { type: "h2", text: "Electronic funds withdrawal (direct debit)" },
      {
        type: "p",
        text: "Only available if you file electronically. You enter the bank routing number, account number, and account type (checking or savings), and the debit is part of the return itself — so don't send a payment voucher.",
      },
      {
        type: "p",
        text: "One IRS rule matters here: the requested payment date must equal the date the IRS receives the return (FPYMT-076-02). It isn't a date you pick; it's set for you when the return is sent. A routing number the IRS database doesn't recognize is rejected too (R0000-906-03), so check it against your bank.",
      },
      { type: "h2", text: "EFTPS" },
      {
        type: "ul",
        items: [
          "Voluntary, but you must enroll before you can use it — at EFTPS.gov or 800-555-4477.",
          "To be on time, submit the payment by 8:00 p.m. Eastern time the day before the due date.",
          "Check the EFTPS box on line 6 of Form 2290 and don't include a payment voucher.",
          "EFTPS doesn't process payments from international financial institutions.",
        ],
      },
      {
        type: "cta",
        heading: "Ready to file and pay?",
        text: "Pick your payment method on the payment step; the tax is shown before you commit.",
        label: "Create your free account",
        to: "/login?tab=up",
      },
      { type: "h2", text: "Credit or debit card" },
      {
        type: "p",
        text: "Pay at IRS.gov/PayByCard. The card processors charge a convenience fee — that's their fee, separate from the tax. Check the credit or debit card box on line 6 and don't include the payment voucher.",
      },
      { type: "h2", text: "Check or money order" },
      {
        type: "ul",
        items: [
          "Make it payable to \"United States Treasury\". Don't send cash.",
          "Write your name, address, EIN, \"Form 2290\", and the date from box 3 of the voucher on the payment.",
          "Complete Form 2290-V and send it with the payment; don't staple them together.",
          "If you filed electronically, don't mail Form 2290 or Schedule 1 with the voucher.",
        ],
      },
      {
        type: "p",
        text: "Where to mail it depends on how you paid — see [Form 2290 mailing addresses](/blog/form-2290-mailing-address-paper-filing).",
      },
      { type: "h2", text: "EFTPS vs EFW: which to choose" },
      {
        type: "p",
        text: "Direct debit is the shortest path if you're e-filing: the payment goes with the return, with nothing to enroll in. EFTPS suits filers who already use it for other federal taxes and want to schedule the payment themselves — just mind the 8 p.m. Eastern cut-off the day before the deadline. A card is quickest if you'd rather not share bank details, at the cost of the processor's fee.",
      },
      { type: "h2", text: "If you owe nothing" },
      {
        type: "p",
        text: "A return with no balance due — for example all [suspended category W trucks](/blog/suspended-vehicles-category-w), or credits that cover the tax — needs no payment method. But a return with a balance due and no payment method indicated is rejected (F2290-068-01).",
      },
      { type: "h2", text: "Paying when you file here" },
      {
        type: "ol",
        items: [
          "Fill in the return and check the tax on the summary — or price it first with the [Form 2290 tax calculator](/form-2290-tax-calculator).",
          "On the payment step, choose EFW, EFTPS, or credit/debit card.",
          "For EFW, enter the routing number, account number, and account type.",
          "E-sign and submit. Once the IRS accepts the return, your [stamped Schedule 1](/blog/stamped-schedule-1-proof-of-payment) is ready.",
        ],
      },
    ],
    faqs: [
      {
        q: "How do I pay my Form 2290 tax online?",
        a: "Three electronic options: electronic funds withdrawal (direct debit) when you e-file, EFTPS, or a credit or debit card through IRS.gov/PayByCard. The tax must be paid in full with the return.",
      },
      {
        q: "Can I pay Form 2290 with a credit card?",
        a: "Yes, through an IRS-approved processor at IRS.gov/PayByCard. The processor charges a convenience fee. Check the credit or debit card box on line 6 of the return.",
      },
      {
        q: "What is the EFTPS deadline for Form 2290?",
        a: "Submit the EFTPS payment by 8:00 p.m. Eastern time the day before the due date. You must be enrolled in EFTPS before you can use it.",
      },
      {
        q: "What's the difference between EFTPS and EFW for Form 2290?",
        a: "EFW is a direct debit authorized inside the e-filed return. EFTPS is a separate Treasury payment system you enroll in and pay through yourself, then check the EFTPS box on the return.",
      },
    ],
  },
// CANONICAL TARGET: "form 2290 logging truck" / "logging vehicle 2290 rate".
  // Rates via RATES[cat][1] (Form 2290 p.2 column (1)(b)); partial-period
  // logging figures are the IRS's printed Table II values (i2290 p.14), never
  // annual x months / 12 - Table II does not round that way.
  {
    slug: "form-2290-logging-trucks",
    category: "rates",
    title: "Form 2290 for Logging Trucks: Who Qualifies for the Reduced Rate and How to File",
    metaTitle: "Form 2290 Logging Truck Rates | File 2290 for Logging",
    metaDescription:
      "Logging trucks pay a reduced Form 2290 rate in every weight category. The two tests a truck must pass, the logging rates, and how to file the return.",
    keywords: [
      "form 2290 logging truck",
      "logging vehicle 2290 rate",
      "file 2290 logging",
      "form 2290 logging vehicle",
      "logging truck heavy vehicle use tax",
      "2290 logging rates",
    ],
    excerpt:
      "A truck that hauls only harvested forest products can pay a lower Heavy Vehicle Use Tax in every weight category. Here are the two tests it must pass, what the reduced rates are, and how to claim them when you file.",
    cta: {
      heading: "File your logging truck's Form 2290",
      text: "Mark each truck as logging and the return uses the IRS logging rates, annual or partial-period.",
      label: "Start a logging return",
      to: "/wizard/1",
    },
    publishedAt: "2026-10-07",
    updatedAt: "2026-10-07",
    readingMinutes: 6,
    irsVerified: true,
    sources:
      "IRS Instructions for Form 2290 (Rev. July 2026) — Who Must File (Logging vehicles), Line 2 column (1)(b) and column (2)(b), Partial-Period Tax Tables (Table II); Form 2290 page 2 tax computation table",
    body: [
      {
        type: "p",
        text: "Logging trucks file the same [Form 2290](/blog/what-is-form-2290) as every other heavy vehicle, on the same deadlines, but they pay less. The IRS taxes logging vehicles at reduced rates in every weight category, for the full period and for partial periods alike. The catch is that the truck has to qualify, and the qualification is narrower than most people expect.",
      },
      { type: "h2", text: "The two tests a logging vehicle must pass" },
      {
        type: "p",
        text: "A vehicle qualifies as a logging vehicle only if both of these are true:",
      },
      {
        type: "ol",
        items: [
          "It is used exclusively to transport products harvested from the forested site, or exclusively transports those products to and from locations on a forested site. Public highways may be used between forested-site locations.",
          "It is registered, under the laws of the state or states where it must be registered, as a highway motor vehicle used exclusively in the transportation of harvested forest products.",
        ],
      },
      {
        type: "p",
        text: "The registration test is met if the vehicle is registered that way under a state statute or legally valid regulations. No special tag or license plate identifying it as a logging vehicle is required.",
      },
      {
        type: "callout",
        tone: "info",
        text: "Products harvested from the forested site can include timber already processed for commercial use — sawn into lumber, chipped, or otherwise milled — as long as the processing happened before the products left the forested site.",
      },
      {
        type: "p",
        text: "The word that matters in both tests is exclusively. A truck that hauls logs most of the year and other freight the rest does not meet it.",
      },
      { type: "h2", text: "Logging rates for a full period" },
      {
        type: "p",
        text: "A logging truck first used in July pays the annual logging amount for its weight category, from column (1)(b) of the tax table on Form 2290, page 2. A few examples next to the standard rate:",
      },
      {
        type: "table",
        headers: ["Category", "Taxable gross weight", "Standard (annual)", "Logging (annual)"],
        rows: [
          ["A", "55,000 lb", `$${RATES.A[0].toFixed(2)}`, `$${RATES.A[1].toFixed(2)}`],
          ["K", "64,001–65,000 lb", `$${RATES.K[0].toFixed(2)}`, `$${RATES.K[1].toFixed(2)}`],
          ["U", "74,001–75,000 lb", `$${RATES.U[0].toFixed(2)}`, `$${RATES.U[1].toFixed(2)}`],
          ["V", "Over 75,000 lb", `$${RATES.V[0].toFixed(2)}`, `$${RATES.V[1].toFixed(2)}`],
        ],
      },
      {
        type: "p",
        text: "The full table for every taxable category, both columns, is on the [Form 2290 rates page](/rates). If you're unsure which category a truck falls into, start with [how weight categories work](/blog/form-2290-weight-categories-and-rates).",
      },
      { type: "h2", text: "Logging trucks first used after July" },
      {
        type: "p",
        text: "A logging truck first used later in the period pays the partial-period amount from the IRS's Table II, entered in column (2)(b). Use the IRS figures themselves rather than working out a fraction of the annual rate — Table II's published amounts don't always match a simple months-left calculation. For a category V logging truck, for example, Table II shows:",
      },
      {
        type: "table",
        headers: ["First used in", "Category V logging tax (IRS Table II)"],
        rows: [
          ["August", "$378.12"],
          ["October", "$309.37"],
          ["January", "$206.25"],
          ["April", "$103.12"],
        ],
      },
      {
        type: "p",
        text: "The [Form 2290 tax calculator](/form-2290-tax-calculator) has every Table II figure built in — tick the logging box for any weight category and month.",
      },
      {
        type: "cta",
        heading: "Price your logging trucks first",
        text: "Pick the weight category and month, tick logging, and see the IRS amount and your filing deadline.",
        label: "Open the tax calculator",
        to: "/form-2290-tax-calculator",
      },
      { type: "h2", text: "How to file Form 2290 for a logging truck" },
      {
        type: "ol",
        items: [
          "Create your account and add the business — legal name, address, and EIN. Form 2290 can't be filed with a Social Security number.",
          "Choose an original return and the month the trucks were first used on a public highway this period. The month sets the [Form 2290 due date](/form-2290-due-date-calculator).",
          "Add each truck's VIN and weight category, and mark it as a logging vehicle. The return then uses the logging column.",
          "Review the tax, pay, e-sign, and transmit. Once the IRS accepts the return, your [stamped Schedule 1](/blog/stamped-schedule-1-proof-of-payment) is ready to download.",
        ],
      },
      {
        type: "p",
        text: "A mixed fleet — some logging trucks, some general freight — goes on the same return. Each truck is marked individually, so only the qualifying ones get the reduced rate.",
      },
      {
        type: "cta",
        heading: "New to EasyFile2290?",
        text: "Set up your business once and every logging return after that starts from it.",
        label: "Create your free account",
        to: "/login?tab=up",
      },
    ],
    faqs: [
      {
        q: "What qualifies as a logging vehicle for Form 2290?",
        a: "It must be used exclusively to transport products harvested from a forested site (or exclusively between locations on the site), and be registered under state law as a highway motor vehicle used exclusively in transporting harvested forest products. No special plate is required.",
      },
      {
        q: "How much is Form 2290 for a logging truck?",
        a: `For a full period it depends on the weight category: from $${RATES.A[1].toFixed(2)} for category A to $${RATES.V[1].toFixed(2)} for category V (over 75,000 lb). Trucks first used after July pay the IRS Table II partial-period amount instead.`,
      },
      {
        q: "Can a truck that hauls logs part of the time use the logging rate?",
        a: "No. Both IRS tests require exclusive use for transporting harvested forest products. A truck that also hauls other freight pays the standard rate.",
      },
      {
        q: "Do logging trucks have a different Form 2290 deadline?",
        a: "No. The deadline is the same for every vehicle: the last day of the month after the month the truck was first used on a public highway during the period.",
      },
    ],
  },

  // CANONICAL TARGET: "form 2290 agricultural vehicle" / "farm truck 2290".
  // The general suspended/category W rules live in suspended-vehicles-category-w;
  // this owns the farm-specific definition and the 7,500-mile limit.
  {
    slug: "form-2290-agricultural-vehicles",
    category: "basics",
    title: "Form 2290 for Farm Trucks: The Agricultural Vehicle Rules and the 7,500-Mile Limit",
    metaTitle: "Form 2290 Agricultural Vehicle | Farm Truck 7,500 Miles",
    metaDescription:
      "Farm trucks get a 7,500-mile Form 2290 suspension limit instead of 5,000. What counts as an agricultural vehicle, which miles count, and how to file the return.",
    keywords: [
      "form 2290 agricultural vehicle",
      "farm truck 2290",
      "7500 mile 2290",
      "form 2290 farm vehicle",
      "agricultural vehicle heavy use tax",
      "2290 suspended farm truck",
    ],
    excerpt:
      "A heavy farm truck can run up to 7,500 miles on public highways in a period and owe no Heavy Vehicle Use Tax — but it still has to be reported. Here is what counts as an agricultural vehicle, which miles count, and how to file.",
    cta: {
      heading: "File your farm truck's Form 2290",
      text: "Report it as suspended with the 7,500-mile agricultural limit and get the stamped Schedule 1 your state needs.",
      label: "Start your return",
      to: "/wizard/1",
    },
    publishedAt: "2026-10-07",
    updatedAt: "2026-10-07",
    readingMinutes: 6,
    irsVerified: true,
    sources:
      "IRS Instructions for Form 2290 (Rev. July 2026) — Purpose of Form, Agricultural vehicles, Line 5 (vehicle used less than the mileage use limit; When to make a claim), Line 7, Suspended vehicles exceeding the mileage use limit, Recordkeeping",
    body: [
      {
        type: "p",
        text: "Most heavy trucks can have the Heavy Vehicle Use Tax suspended if they run 5,000 miles or less on public highways during the July-to-June period. Agricultural vehicles get a higher limit: 7,500 miles. Under that limit the truck owes no tax for the period — but it still goes on a [Form 2290](/blog/what-is-form-2290) and on the Schedule 1.",
      },
      { type: "h2", text: "What counts as an agricultural vehicle" },
      {
        type: "p",
        text: "An agricultural vehicle is any highway motor vehicle that is:",
      },
      {
        type: "ol",
        items: [
          "Used, or expected to be used, primarily for farming purposes; and",
          "Registered under state law as a highway motor vehicle used for farming purposes for the entire period. A special farm tag or license plate isn't required.",
        ],
      },
      {
        type: "p",
        text: "Primarily means more than half of the vehicle's use during the period, measured by mileage, is for farming purposes.",
      },
      { type: "h2", text: "What counts as farming purposes" },
      {
        type: "p",
        text: "Farming purposes means transporting any farm commodity to or from a farm, or use directly in agricultural production. A farm commodity is any agricultural or horticultural commodity, feed, seed, fertilizer, livestock, bees, poultry, fur-bearing animals, or wildlife.",
      },
      {
        type: "p",
        text: "A vehicle also counts as used for farming if it contributes to direct agricultural production or in any way to the running of a farm — cultivating soil, raising or harvesting crops, clearing land, repairing fences and farm buildings, building terraces or irrigation ditches, cleaning tools or machinery, and painting.",
      },
      {
        type: "callout",
        tone: "warn",
        text: "Processing breaks the chain. A commodity changed from its raw or natural state by processing isn't a farm commodity — the IRS's own example is juice extracted from fruits or vegetables — and use in canning, freezing, packaging, or other processing operations isn't farming use.",
      },
      { type: "h2", text: "Which miles count toward 7,500" },
      {
        type: "p",
        text: "Only public-highway miles count. Don't include miles the vehicle is driven on the farm when deciding whether the 7,500-mile limit has been passed — but keep accurate records of those farm miles anyway.",
      },
      {
        type: "p",
        text: "The limit applies to the vehicle's total highway mileage for the period, regardless of how many owners it had. If you buy a suspended farm truck partway through, the previous owner's highway miles count too.",
      },
      {
        type: "cta",
        heading: "Reporting a farm truck under the limit?",
        text: "Mark it suspended and agricultural; the return reports it under category W with no tax due.",
        label: "Start your Form 2290",
        to: "/wizard/1",
      },
      { type: "h2", text: "How to file Form 2290 for a farm truck" },
      {
        type: "ol",
        items: [
          "Create your account and add the business with its EIN. A Social Security number can't be used on Form 2290.",
          "Start an original return for the month the truck was first used on a public highway this period. That month sets the [filing deadline](/form-2290-due-date-calculator) even though no tax is due.",
          "Add the truck's VIN, mark it as suspended, and mark it as agricultural so the 7,500-mile limit applies.",
          "E-sign and submit. The truck is listed on your [stamped Schedule 1](/blog/stamped-schedule-1-proof-of-payment), the proof your state looks for at registration.",
        ],
      },
      {
        type: "p",
        text: "For the general rules on suspended trucks and category W, see [suspended vehicles and category W](/blog/suspended-vehicles-category-w).",
      },
      { type: "h2", text: "If the farm truck goes over 7,500 miles" },
      {
        type: "p",
        text: "Once a suspended vehicle passes the mileage limit, the tax becomes due. You file an amended Form 2290 reporting the tax for the period, figured from the month the truck was first used, by the last day of the month after the month the limit was exceeded. See [what to do when a suspended truck goes over its mileage limit](/blog/form-2290-suspended-vehicle-over-mileage).",
      },
      { type: "h2", text: "If you paid tax and stayed under 7,500" },
      {
        type: "p",
        text: "If you paid the tax for a period on a farm truck that ended up running 7,500 highway miles or less, you can claim it back — but only after the period ends. The credit goes on the first Form 2290 you file for the next period, or you can claim a refund on Form 8849 after June 30. More in [Form 2290 credits and refunds](/blog/form-2290-credits-and-refunds).",
      },
      { type: "h2", text: "Records to keep" },
      {
        type: "p",
        text: "For a suspended vehicle, keep a record of actual highway mileage, and for an agricultural vehicle keep accurate records of the miles driven on the farm. Keep the records for at least 3 years after the end of the period the suspension applies to.",
      },
      {
        type: "cta",
        heading: "File your farm truck in one sitting",
        text: "Set up the farm business once; next July's return starts from it.",
        label: "Create your free account",
        to: "/login?tab=up",
      },
    ],
    faqs: [
      {
        q: "Do farm trucks have to file Form 2290?",
        a: "Yes, if the truck has a taxable gross weight of 55,000 pounds or more. An agricultural vehicle expected to run 7,500 public-highway miles or less in the period is reported as suspended with no tax, but it must still be filed for and listed on Schedule 1.",
      },
      {
        q: "What is the mileage limit for agricultural vehicles on Form 2290?",
        a: "7,500 miles on public highways during the July-to-June period, instead of the usual 5,000. Miles driven on the farm don't count toward the limit.",
      },
      {
        q: "What makes a truck an agricultural vehicle for Form 2290?",
        a: "It must be used primarily for farming purposes — more than half its mileage — and registered under state law as a highway motor vehicle used for farming purposes for the entire period. A farm plate isn't required.",
      },
      {
        q: "What if my farm truck goes over 7,500 miles?",
        a: "The tax becomes due. File an amended Form 2290 by the last day of the month after the month the limit was exceeded, with the tax figured from the month the truck was first used in the period.",
      },
    ],
  },

  // CANONICAL TARGET: "form 2290 bus" / "bus heavy vehicle use tax".
  // The bus-specific weight rule (+150 lb per seat) and the exemptions a bus
  // operator is most likely to meet.
  {
    slug: "form-2290-buses",
    category: "basics",
    title: "Form 2290 for Buses: How to Figure a Bus's Taxable Gross Weight and File",
    metaTitle: "Form 2290 for Buses | Bus Heavy Vehicle Use Tax",
    metaDescription:
      "Buses owe Form 2290 tax too. How the IRS figures a bus's taxable gross weight (150 lb per seat), which bus operators are exempt, and how to file.",
    keywords: [
      "form 2290 bus",
      "bus heavy vehicle use tax",
      "2290 for buses",
      "bus taxable gross weight",
      "charter bus form 2290",
      "hvut bus",
    ],
    excerpt:
      "Buses are taxed under Form 2290 like trucks, but their taxable gross weight is figured differently: the IRS counts seats, not cargo. Here is the bus formula, who is exempt, and how to file.",
    cta: {
      heading: "File Form 2290 for your buses",
      text: "Add each bus's VIN and weight category; the tax comes from the IRS tables.",
      label: "Start your return",
      to: "/wizard/1",
    },
    publishedAt: "2026-10-07",
    updatedAt: "2026-10-07",
    readingMinutes: 5,
    irsVerified: true,
    sources:
      "IRS Instructions for Form 2290 (Rev. July 2026) — Taxable Vehicles, Exemptions, Taxable Gross Weight, Buses, Fully equipped for service, When To File",
    body: [
      {
        type: "p",
        text: "[Form 2290](/blog/what-is-form-2290) isn't only for trucks. A highway motor vehicle is any self-propelled vehicle designed to carry a load over public highways, and the IRS lists buses alongside trucks and truck tractors as examples. A bus with a taxable gross weight of 55,000 pounds or more is taxable, and its operator files the same return on the same deadlines.",
      },
      { type: "h2", text: "How a bus's taxable gross weight is figured" },
      {
        type: "p",
        text: "For trucks, taxable gross weight is the empty vehicle plus customary trailers plus the maximum load customarily carried. Buses use a different rule. The taxable gross weight of a bus is:",
      },
      {
        type: "ul",
        items: [
          "Its actual unloaded weight, fully equipped for service; plus",
          "150 pounds for each seat provided for passengers and the driver.",
        ],
      },
      {
        type: "p",
        text: "For a bus, fully equipped for service includes the equipment for accommodating passengers or others — the IRS gives air conditioning and sanitation facilities as examples — along with the body, all accessories, operating and maintenance equipment carried on the bus, and a full supply of fuel, oil, and water.",
      },
      {
        type: "callout",
        tone: "info",
        text: "Seats count whether or not they're filled. The 150 pounds is added for every seat provided, including the driver's.",
      },
      {
        type: "p",
        text: "Once you have the weight, the category follows from the [Form 2290 weight categories and rates](/blog/form-2290-weight-categories-and-rates), from A at 55,000 pounds up to V over 75,000. For the truck version of this calculation, see [taxable gross weight for Form 2290](/blog/taxable-gross-weight-form-2290).",
      },
      {
        type: "cta",
        heading: "Know the weight category?",
        text: "Enter it with the month the bus was first used and see the tax and the deadline.",
        label: "Open the tax calculator",
        to: "/form-2290-tax-calculator",
      },
      { type: "h2", text: "Bus operators who are exempt" },
      {
        type: "p",
        text: "The use of a highway motor vehicle isn't taxed — and isn't reported on Form 2290 — when the vehicle is used and actually operated by any of these:",
      },
      {
        type: "ul",
        items: [
          "The federal government, the District of Columbia, or a state or local government.",
          "A mass transportation authority created under a statute that gives it certain powers normally exercised by the state.",
          "An Indian tribal government, but only if the use involves an essential tribal government function.",
          "The American National Red Cross, or a nonprofit volunteer fire department, ambulance association, or rescue squad.",
        ],
      },
      {
        type: "p",
        text: "The exemption turns on who uses and actually operates the bus. A bus operated by a private company isn't on this list, so it is taxed by weight like any other vehicle. The full list of exemptions is in [do I need to file Form 2290](/blog/form-2290-exempt-vehicles).",
      },
      { type: "h2", text: "When a bus's Form 2290 is due" },
      {
        type: "p",
        text: "The same rule as every vehicle: by the last day of the month after the month the bus was first used on a public highway during the July-to-June period. Buses in service in July are due by August 31; a bus added in October is due by the end of November and taxed only for the months left. Check any month with the [Form 2290 due date calculator](/form-2290-due-date-calculator).",
      },
      { type: "h2", text: "How to file Form 2290 for a bus" },
      {
        type: "ol",
        items: [
          "Create your account and add the business — legal name, address, and EIN.",
          "Start an original return for the month the buses were first used.",
          "Add each bus's VIN and weight category. An operator with many buses can [import them from a spreadsheet](/blog/bulk-form-2290-filing-for-fleets).",
          "Review the tax, pay, e-sign, and submit. The accepted return gives you a [stamped Schedule 1](/blog/stamped-schedule-1-proof-of-payment) listing every VIN.",
        ],
      },
      {
        type: "p",
        text: "A bus expected to run 5,000 public-highway miles or less during the period can be reported as suspended, with no tax, under [category W](/blog/suspended-vehicles-category-w).",
      },
      {
        type: "cta",
        heading: "Running a bus company?",
        text: "Set up the business once and file every new bus from the same account.",
        label: "Create your free account",
        to: "/login?tab=up",
      },
    ],
    faqs: [
      {
        q: "Do buses have to file Form 2290?",
        a: "Yes, if the bus has a taxable gross weight of 55,000 pounds or more and isn't operated by an exempt user such as a government, a qualifying mass transportation authority, or a nonprofit volunteer fire department, ambulance association, or rescue squad.",
      },
      {
        q: "How do you calculate the taxable gross weight of a bus?",
        a: "Take the bus's actual unloaded weight fully equipped for service and add 150 pounds for each seat provided for passengers and the driver.",
      },
      {
        q: "Are school or city buses exempt from Form 2290?",
        a: "Vehicles used and actually operated by a state or local government, or by a qualifying mass transportation authority, are exempt. The test is who uses and actually operates the bus; a bus operated by a private company isn't on the exempt list.",
      },
    ],
  },

  // CANONICAL TARGET: "form 2290 canadian trucks" / "form 2290 mexican
  // carrier" / "2290 cross border". Product supports foreign (Canada/Mexico)
  // addresses: BusinessStep country select, Province/State, Postal code.
  {
    slug: "form-2290-canadian-mexican-trucks",
    category: "basics",
    title: "Form 2290 for Canadian and Mexican Trucks: Filing HVUT for Cross-Border Carriers",
    metaTitle: "Form 2290 for Canadian & Mexican Trucks | Cross-Border",
    metaDescription:
      "Canadian and Mexican heavy trucks need Form 2290 and a stamped Schedule 1 to enter the U.S. EIN, address, and payment rules for cross-border carriers.",
    keywords: [
      "form 2290 canadian trucks",
      "form 2290 mexican carrier",
      "2290 cross border",
      "form 2290 canada",
      "form 2290 mexico",
      "schedule 1 border crossing",
    ],
    excerpt:
      "A heavy truck registered in Canada or Mexico that runs on U.S. highways owes the Heavy Vehicle Use Tax — and U.S. Customs and Border Protection asks for the proof. Here is how cross-border carriers file.",
    cta: {
      heading: "File Form 2290 from Canada or Mexico",
      text: "Enter your Canadian or Mexican address, add your trucks, and get the stamped Schedule 1 for the border.",
      label: "Start your return",
      to: "/wizard/1",
    },
    publishedAt: "2026-10-07",
    updatedAt: "2026-10-07",
    readingMinutes: 6,
    irsVerified: true,
    sources:
      "IRS Instructions for Form 2290 (Rev. July 2026) — Reminders (U.S. Customs and Border Protection), Who Must File, Getting Started, EIN, Name and Address (Canadian or Mexican address), Where To File, Form 2290 Call Site, How To Pay the Tax (EFTPS, International payments), Schedule 1 proof of payment",
    body: [
      {
        type: "p",
        text: "[Form 2290](/blog/what-is-form-2290) isn't only for U.S.-plated trucks. You must file if a taxable highway motor vehicle is registered, or required to be registered, in your name under state, District of Columbia, Canadian, or Mexican law at the time of its first use during the period, and it has a taxable gross weight of 55,000 pounds or more.",
      },
      { type: "h2", text: "Why the border cares" },
      {
        type: "p",
        text: "U.S. Customs and Border Protection requires proof of payment of the Heavy Vehicle Use Tax for entering a Canadian or Mexican vehicle into the United States. That proof is the same document state DMVs use: the [stamped Schedule 1](/blog/stamped-schedule-1-proof-of-payment) the IRS returns once your Form 2290 is accepted.",
      },
      {
        type: "p",
        text: "The Schedule 1 lists the VIN of each vehicle you reported. A truck that isn't on it has no proof of payment, so check the VIN list before you submit.",
      },
      { type: "h2", text: "You need an EIN, not a foreign tax number" },
      {
        type: "p",
        text: "Form 2290 can only be filed under a U.S. employer identification number (EIN). If you don't have one, you can apply online at IRS.gov/EIN. Persons with an address in a foreign country, such as Canada, can also apply by calling 267-941-1099 (not a toll-free call), or by faxing or mailing Form SS-4. More on that in [getting an EIN for Form 2290](/blog/ein-for-form-2290).",
      },
      { type: "h2", text: "Entering a Canadian or Mexican address" },
      {
        type: "p",
        text: "For a Canadian or Mexican address, follow that country's practice for entering the postal code, and don't abbreviate the country name. When you file here, choose Canada or Mexico as the country and the form asks for the province or state and postal code instead of a U.S. state and ZIP.",
      },
      {
        type: "cta",
        heading: "Crossing with a heavy truck?",
        text: "File now and have the stamped Schedule 1 ready before you reach the border.",
        label: "Start your Form 2290",
        to: "/wizard/1",
      },
      { type: "h2", text: "Paying from outside the U.S." },
      {
        type: "p",
        text: "The tax must be paid in full with the return. Two points matter for cross-border carriers:",
      },
      {
        type: "ul",
        items: [
          "EFTPS doesn't process payments from international financial institutions.",
          "A check or money order drawn from an international financial institution goes, with a paper return, to the IRS International Accounts address in Ogden, Utah — a different address from domestic paper filings.",
        ],
      },
      {
        type: "p",
        text: "The other IRS payment methods are electronic funds withdrawal (for e-filed returns) and credit or debit card. Compare the options in [how to pay Form 2290 tax](/blog/how-to-pay-form-2290).",
      },
      { type: "h2", text: "When it's due" },
      {
        type: "p",
        text: "Same rule as U.S. carriers: by the last day of the month after the month the truck was first used on a U.S. public highway during the July-to-June period. Find your date with the [Form 2290 due date calculator](/form-2290-due-date-calculator).",
      },
      { type: "h2", text: "How to file as a Canadian or Mexican carrier" },
      {
        type: "ol",
        items: [
          "Create your account and add the business: legal name, EIN, and your Canadian or Mexican address.",
          "Start an original return for the month the trucks were first used.",
          "Add each truck's VIN and weight category — use the truck's VIN, not the trailer's.",
          "Pay, e-sign, and submit. Download the stamped Schedule 1 once the IRS accepts the return and carry it for the border crossing.",
        ],
      },
      { type: "h2", text: "Getting help from the IRS" },
      {
        type: "p",
        text: "The IRS Form 2290 call site takes calls from Canada or Mexico at 859-320-3581 (not toll free), Monday to Friday, 8:00 a.m. to 6:00 p.m. Eastern time. Spanish-speaking assistors are available.",
      },
      {
        type: "cta",
        heading: "Set up your cross-border account",
        text: "Save your business and address once; next period's return starts from it.",
        label: "Create your free account",
        to: "/login?tab=up",
      },
    ],
    faqs: [
      {
        q: "Do Canadian trucks need to file Form 2290?",
        a: "Yes, if the truck is registered (or required to be registered) in your name under Canadian law at its first use in the period and has a taxable gross weight of 55,000 pounds or more. The same applies to trucks registered under Mexican law.",
      },
      {
        q: "Why does U.S. Customs ask for Schedule 1?",
        a: "U.S. Customs and Border Protection requires proof the Heavy Vehicle Use Tax was paid before a Canadian or Mexican vehicle enters the United States. The stamped Schedule 1 is that proof.",
      },
      {
        q: "Can a Canadian company get an EIN for Form 2290 by phone?",
        a: "Yes. Persons with an address in a foreign country, such as Canada, may apply by calling 267-941-1099 (not toll free). You can also apply online at IRS.gov/EIN or by fax or mail with Form SS-4.",
      },
      {
        q: "Can I pay Form 2290 through EFTPS from a Canadian bank?",
        a: "No. EFTPS doesn't process payments from international financial institutions. The other IRS payment methods are electronic funds withdrawal, credit or debit card, and check or money order; a check drawn on an international institution goes to the IRS International Accounts address.",
      },
    ],
  },

  // CANONICAL TARGET: "do i need to file form 2290" / "form 2290 exempt
  // vehicles" / "who is exempt from 2290". Taxable-vehicle definition plus the
  // full exemption list from i2290 Taxable Vehicles and Exemptions.
  {
    slug: "form-2290-exempt-vehicles",
    category: "basics",
    title: "Do I Need to File Form 2290? Taxable Vehicles and Who Is Exempt",
    metaTitle: "Do I Need to File Form 2290? Exempt Vehicles Explained",
    metaDescription:
      "Do you need to file Form 2290? Which vehicles are taxable at 55,000 lb, who is exempt (governments, Red Cross, volunteer fire, machinery), and next steps.",
    keywords: [
      "do i need to file form 2290",
      "form 2290 exempt vehicles",
      "who is exempt from 2290",
      "who must file form 2290",
      "form 2290 exemptions",
      "is my truck subject to 2290",
    ],
    excerpt:
      "Not every heavy vehicle owes the Heavy Vehicle Use Tax, and not every exempt one is obvious. Here is the test for a taxable vehicle, the IRS's full exemption list, and what to do if you're taxable.",
    cta: {
      heading: "Taxable? File your Form 2290 now",
      text: "If your vehicle passes the test below, e-file the return and get your stamped Schedule 1.",
      label: "Start your return",
      to: "/wizard/1",
    },
    publishedAt: "2026-10-07",
    updatedAt: "2026-10-07",
    readingMinutes: 6,
    irsVerified: true,
    sources:
      "IRS Instructions for Form 2290 (Rev. July 2026) — Who Must File, Dual registration, Dealers, Taxable Vehicles, Exemptions, Qualified blood collector vehicle, Vehicles not considered highway motor vehicles",
    body: [
      {
        type: "p",
        text: "You must file [Form 2290](/blog/what-is-form-2290) for the July 1, 2026 – June 30, 2027 period if a taxable highway motor vehicle is registered, or required to be registered, in your name under state, District of Columbia, Canadian, or Mexican law at the time of its first use during the period, and it has a taxable gross weight of 55,000 pounds or more. Individuals, LLCs, corporations, partnerships, and nonprofits are all covered.",
      },
      { type: "h2", text: "Step 1: Is it a highway motor vehicle?" },
      {
        type: "p",
        text: "A highway motor vehicle is any self-propelled vehicle designed to carry a load over public highways, whether or not it's also designed for other jobs. Trucks, truck tractors, and buses are the IRS's examples. Use means use with power from its own motor on any public highway in the United States — any road that isn't a private roadway, including federal, state, county, and city roads.",
      },
      { type: "h2", text: "Step 2: Is it 55,000 pounds or more?" },
      {
        type: "p",
        text: "Only vehicles with a taxable gross weight of 55,000 pounds or more are taxable. That is why vans, pickup trucks, panel trucks, and similar trucks generally aren't subject to the tax. Taxable gross weight is not empty weight — see [how to work out taxable gross weight](/blog/taxable-gross-weight-form-2290).",
      },
      { type: "h2", text: "Step 3: Is the user exempt?" },
      {
        type: "p",
        text: "The use of a highway motor vehicle isn't taxed — and isn't reported on Form 2290 — if it is used and actually operated by:",
      },
      {
        type: "ul",
        items: [
          "The federal government.",
          "The District of Columbia.",
          "A state or local government.",
          "The American National Red Cross.",
          "A nonprofit volunteer fire department, ambulance association, or rescue squad.",
          "An Indian tribal government, but only if the use involves an essential tribal government function.",
          "A mass transportation authority created under a statute that gives it certain powers normally exercised by the state.",
        ],
      },
      {
        type: "p",
        text: "Also exempt is the use of qualified blood collector vehicles by qualified blood collector organizations — a vehicle at least 80% of whose use in the prior period was collecting, storing, or transporting blood. A vehicle first placed in service in a period qualifies if the organization certifies it reasonably expects at least 80% of its use to be for that purpose.",
      },
      {
        type: "cta",
        heading: "Passed all three steps?",
        text: "Then the vehicle is taxable. Work out the tax and the deadline in seconds.",
        label: "Open the tax calculator",
        to: "/form-2290-tax-calculator",
      },
      { type: "h2", text: "Vehicles that aren't highway motor vehicles" },
      {
        type: "p",
        text: "Two kinds of vehicles generally fall outside the tax because they aren't considered highway vehicles at all.",
      },
      { type: "h3", text: "Specially designed mobile machinery" },
      {
        type: "p",
        text: "A self-propelled vehicle isn't a highway vehicle if all of these apply:",
      },
      {
        type: "ol",
        items: [
          "The chassis has machinery or equipment permanently mounted to it for operations such as construction, manufacturing, drilling, mining, timbering, processing, or farming, unrelated to transportation on or off public highways.",
          "The chassis was specially designed to serve only as a mobile carriage and mount (and power source, if applicable) for that machinery or equipment.",
          "Because of its special design, the chassis couldn't be used as part of a vehicle designed to carry any other load without substantial structural modification.",
        ],
      },
      { type: "h3", text: "Vehicles specially designed for off-highway transportation" },
      {
        type: "p",
        text: "A vehicle designed mainly to carry a particular load somewhere other than on public highways, whose design substantially limits its ability to carry a load over a public highway, isn't treated as a highway vehicle. The IRS looks at size, licensing and safety requirements, and whether the vehicle can carry a load at a sustained speed of at least 25 miles per hour.",
      },
      { type: "h2", text: "Taxable but barely driven? You still file" },
      {
        type: "p",
        text: "A vehicle expected to run 5,000 miles or less on public highways during the period (7,500 for [agricultural vehicles](/blog/form-2290-agricultural-vehicles)) isn't exempt — it is suspended. It is reported under [category W](/blog/suspended-vehicles-category-w) with no tax due, and it still appears on Schedule 1.",
      },
      { type: "h2", text: "Who files when two names are on the registration" },
      {
        type: "ul",
        items: [
          "If a taxable vehicle is registered in the name of both the owner and another person, the owner is liable for the tax. The same rule applies to a leased vehicle with dual registration.",
          "A vehicle operated under a dealer's tag, license, or permit is considered registered in the dealer's name.",
        ],
      },
      {
        type: "cta",
        heading: "Your vehicle is taxable — file today",
        text: "Set up your business and file the return in one sitting.",
        label: "Create your free account",
        to: "/login?tab=up",
      },
    ],
    faqs: [
      {
        q: "Do I need to file Form 2290?",
        a: "Yes, if a highway motor vehicle with a taxable gross weight of 55,000 pounds or more is registered, or required to be registered, in your name at its first use during the period, and its use isn't exempt.",
      },
      {
        q: "Who is exempt from Form 2290?",
        a: "Vehicles used and actually operated by the federal government, the District of Columbia, a state or local government, the American National Red Cross, a nonprofit volunteer fire department, ambulance association, or rescue squad, certain Indian tribal government uses, and qualifying mass transportation authorities. Qualified blood collector vehicles and certain mobile machinery are also exempt.",
      },
      {
        q: "Do pickup trucks need Form 2290?",
        a: "Generally no. Vans, pickup trucks, panel trucks, and similar trucks usually have a taxable gross weight under 55,000 pounds, so the tax doesn't apply.",
      },
      {
        q: "Is a truck that barely gets driven exempt from Form 2290?",
        a: "No. A truck expected to run 5,000 public-highway miles or less (7,500 for agricultural vehicles) is suspended, not exempt. It is reported under category W with no tax due.",
      },
    ],
  },
// CANONICAL TARGET: "taxable gross weight form 2290" / "how to calculate 2290
  // weight". The weight-categories post owns the rate table; this one owns how
  // to arrive at the weight that picks the category.
  {
    slug: "taxable-gross-weight-form-2290",
    category: "rates",
    title: "Taxable Gross Weight for Form 2290: How to Work Out Your Truck's Category",
    metaTitle: "Taxable Gross Weight Form 2290 | How to Calculate It",
    metaDescription:
      "How to calculate taxable gross weight for Form 2290: the three parts the IRS adds up, what fully equipped means, and how state registration sets the floor.",
    keywords: [
      "taxable gross weight form 2290",
      "how to calculate 2290 weight",
      "2290 weight category",
      "form 2290 taxable gross weight",
      "what is taxable gross weight",
      "2290 gross weight calculation",
    ],
    excerpt:
      "Your Form 2290 tax is set by one number: taxable gross weight. It isn't the empty weight and it isn't a guess. Here is how the IRS defines it, step by step, so you pick the right category the first time.",
    cta: {
      heading: "Know your weight? Get your tax in seconds",
      text: "Pick the category and the month first used, and the calculator shows the tax and the filing deadline from the IRS tables.",
      label: "Open the tax calculator",
      to: "/form-2290-tax-calculator",
    },
    publishedAt: "2026-10-07",
    updatedAt: "2026-10-07",
    readingMinutes: 6,
    irsVerified: true,
    sources:
      "IRS Instructions for Form 2290 (Rev. July 2026) — Getting Started, Taxable Gross Weight, Buses, Determining Taxable Gross Weight (state registration by specific gross weight, by gross weight category, by actual unloaded weight; special permits)",
    body: [
      {
        type: "p",
        text: "Every truck on a [Form 2290](/blog/what-is-form-2290) is taxed by its weight category, and the category comes from one figure: taxable gross weight. Get that number wrong and the whole return is wrong — too low and you owe more later, too high and you have overpaid.",
      },
      {
        type: "p",
        text: "The IRS lists it as one of three things to have ready before you start, alongside your EIN and each VIN.",
      },
      { type: "h2", text: "The three parts of taxable gross weight" },
      {
        type: "p",
        text: "For any vehicle other than a bus, taxable gross weight is the total of:",
      },
      {
        type: "ol",
        items: [
          "The actual unloaded weight of the vehicle, fully equipped for service.",
          "The actual unloaded weight of any trailers or semitrailers, fully equipped for service, customarily used in combination with the vehicle.",
          "The weight of the maximum load customarily carried on the vehicle and on those trailers or semitrailers.",
        ],
      },
      {
        type: "p",
        text: "A trailer counts as customarily used with a vehicle if the vehicle is equipped to tow it. So a tractor set up to pull a 53-foot trailer is weighed with that trailer, even on days it runs bobtail.",
      },
      { type: "h2", text: "What fully equipped for service includes" },
      {
        type: "p",
        text: "Actual unloaded weight means the empty (tare) weight of the vehicle fully equipped for service. The IRS spells out what that covers:",
      },
      {
        type: "ul",
        items: [
          "The body, whether or not it is designed to carry cargo — a concrete mixer counts.",
          "All accessories, and all equipment attached to or carried on the vehicle for its operation or maintenance.",
          "A full supply of fuel, oil, and water.",
        ],
      },
      {
        type: "p",
        text: "And what it leaves out: the driver; any equipment (other than the body) mounted on or attached to the vehicle to handle, protect, or preserve cargo; and special equipment such as an air compressor, crane, or specialized oilfield equipment.",
      },
      {
        type: "callout",
        tone: "info",
        text: "Buses are figured differently: actual unloaded weight fully equipped for service plus 150 pounds for each seat for passengers and the driver. See [Form 2290 for buses](/blog/form-2290-buses).",
      },
      {
        type: "cta",
        heading: "Turn the weight into a tax figure",
        text: "Choose the category your weight falls in and the month the truck was first used — the calculator does the rest.",
        label: "Calculate my Form 2290 tax",
        to: "/form-2290-tax-calculator",
      },
      { type: "h2", text: "Your state registration sets the floor" },
      {
        type: "p",
        text: "The IRS warns that the weight you declare to register the truck in a state can control the taxable gross weight. Which rule applies depends on how your state registers vehicles:",
      },
      {
        type: "table",
        headers: ["How the state registers the vehicle", "What that means for Form 2290"],
        rows: [
          [
            "By a specific declared gross weight (including prorated or proportional registration)",
            "Taxable gross weight can't be less than the highest gross weight declared in any state. For a tractor-trailer or truck-trailer combination, no less than the highest combined gross weight declared.",
          ],
          [
            "By gross weight category, and not in any state that requires a specific declared weight",
            "Taxable gross weight must fall within the highest gross weight category the vehicle is registered for in that state.",
          ],
          [
            "Only on actual unloaded weight",
            "Taxable gross weight is the total of the three parts above.",
          ],
        ],
      },
      {
        type: "p",
        text: "In practice: if your IRP cab card or state registration declares 80,000 lb, that is your floor, even if the truck rarely scales that heavy.",
      },
      { type: "h2", text: "Special permits don't count — with one limit" },
      {
        type: "p",
        text: "Weights declared only to get special temporary travel permits are left out. Those are permits that let a vehicle run in a state where it isn't registered, above a state's maximum weight limit, or above the weight it is registered at in that state.",
      },
      {
        type: "p",
        text: "The exception: permits issued for your vehicle that together cover more than 60 days — or, if issued monthly, more than 2 months — during a tax year are not treated as special temporary permits. At that point the permitted weight can matter.",
      },
      {
        type: "cta",
        heading: "Ready to file with the right category?",
        text: "Set up your business once; every truck and every later return starts from it.",
        label: "Create your free account",
        to: "/login?tab=up",
      },
      { type: "h2", text: "From weight to category" },
      {
        type: "p",
        text: "Once you have the number, match it to a category on the IRS table. Category A is 55,000 lb, each category above it covers the next 1,000 lb, and category V is anything over 75,000 lb. The full table with standard and logging rates is on the [Form 2290 rates page](/rates), and the [weight categories guide](/blog/form-2290-weight-categories-and-rates) explains each band.",
      },
      {
        type: "p",
        text: "If the weight later goes up — a heavier maximum load customarily carried, for example — and the truck moves into a higher category, you file an [amendment for the weight increase](/blog/form-2290-weight-increase-amendment) and pay the difference for the rest of the period.",
      },
      { type: "h2", text: "Filing it" },
      {
        type: "p",
        text: "On the return, each truck gets its VIN and its category. Fleets can put the category in a spreadsheet column and [import every truck at once](/blog/bulk-form-2290-filing-for-fleets). New to filing? The [step-by-step guide to filing Form 2290 online](/blog/how-to-file-form-2290-online) walks through each truck in order.",
      },
    ],
    faqs: [
      {
        q: "How do I calculate taxable gross weight for Form 2290?",
        a: "Add the truck's unloaded weight fully equipped for service, the unloaded weight of trailers customarily used with it, and the maximum load customarily carried. If a state registration declares a specific gross weight, the taxable gross weight can't be lower than the highest weight declared.",
      },
      {
        q: "Is taxable gross weight the same as my registered weight?",
        a: "Often it is set by it. If you register in a state that requires a specific declared gross weight, your taxable gross weight can't be less than the highest weight you declared in any state.",
      },
      {
        q: "Does fuel count toward taxable gross weight?",
        a: "Yes. Fully equipped for service includes a full supply of fuel, oil, and water. The driver does not count.",
      },
      {
        q: "Do special permit weights change my Form 2290 category?",
        a: "Not if they are special temporary travel permits. Permits covering more than 60 days, or more than 2 months if issued monthly, in a tax year don't count as temporary.",
      },
    ],
  },

  // CANONICAL TARGET: "form 2290 extension" / "2290 extension of time".
  // Sourced only from i2290 "Extension of time to file". Steers the reader to
  // file now rather than wait on a letter.
  {
    slug: "form-2290-extension",
    category: "deadlines",
    title: "Form 2290 Extension: How to Request More Time, and Why Filing Now Is Usually Faster",
    metaTitle: "Form 2290 Extension of Time to File | How It Works",
    metaDescription:
      "Can you extend Form 2290? Yes, by letter to the IRS before the due date, but it doesn't extend time to pay. How to ask, and why e-filing now is quicker.",
    keywords: [
      "form 2290 extension",
      "2290 extension of time",
      "can i extend form 2290",
      "form 2290 extension letter",
      "irs form 2290 extension",
    ],
    excerpt:
      "There is no extension form for Form 2290 — it takes a letter to the IRS, sent before the deadline, and it still doesn't push back the tax. Here is exactly how it works, and when filing today makes more sense.",
    cta: {
      heading: "Beat the deadline instead",
      text: "Most returns take one sitting online, and the stamped Schedule 1 comes back once the IRS accepts it.",
      label: "File Form 2290 now",
      to: "/wizard/1",
    },
    publishedAt: "2026-10-07",
    updatedAt: "2026-10-07",
    readingMinutes: 5,
    irsVerified: true,
    sources:
      "IRS Instructions for Form 2290 (Rev. July 2026) — When To File (incl. Extension of time to file), How To Pay the Tax, Schedule 1 (proof of payment)",
    body: [
      {
        type: "p",
        text: "If a [Form 2290 deadline](/blog/form-2290-due-date-2026-2027) is coming and you aren't ready, the IRS does allow you to ask for more time. But the process is old-fashioned, it has to happen before the due date, and it covers less than most people assume.",
      },
      { type: "h2", text: "How to request an extension" },
      {
        type: "p",
        text: "There is no extension form. The IRS instructions say that before the due date of the return, you may request an extension of time to file by writing to:",
      },
      {
        type: "callout",
        tone: "info",
        text: "Internal Revenue Service, 7940 Kentucky Drive, Florence, KY 41042-2915",
      },
      {
        type: "ul",
        items: [
          "Send it before the due date. A request after the deadline isn't an extension request.",
          "In the letter you must fully explain the cause of the delay.",
          "Except for taxpayers abroad, the extension can be for no more than 6 months.",
        ],
      },
      { type: "h2", text: "What an extension does not do" },
      {
        type: "callout",
        tone: "warn",
        text: "An extension of time to file doesn't extend the time to pay the tax. If you want more time to pay, you must request that separately.",
      },
      {
        type: "p",
        text: "That matters because Form 2290 tax is paid in full with the return. An extension to file, on its own, leaves the payment deadline where it was.",
      },
      {
        type: "p",
        text: "It also doesn't give you a stamped Schedule 1. Your state needs that to register the truck, and you only get it once a return is filed and accepted. See [using Schedule 1 for DMV registration](/blog/schedule-1-for-dmv-registration).",
      },
      { type: "h2", text: "Check your actual deadline first" },
      {
        type: "p",
        text: "Many people searching for an extension are looking at the wrong date. Form 2290 is due by the last day of the month after the month the truck was first used on a public highway — not August 31 for everyone, and not your registration renewal date. If the date lands on a weekend or legal holiday, it moves to the next business day.",
      },
      {
        type: "p",
        text: "Use the [Form 2290 due date calculator](/form-2290-due-date-calculator) to see the deadline for your month. You may have more time than you think.",
      },
      {
        type: "cta",
        heading: "Have your EIN, VINs, and weights?",
        text: "That is all the IRS asks you to have ready. Start now and save as you go.",
        label: "Create your free account",
        to: "/login?tab=up",
      },
      { type: "h2", text: "Why filing now is usually faster than asking" },
      {
        type: "ul",
        items: [
          "An e-filed return can be done in one sitting, and the stamped Schedule 1 can be available within minutes of IRS acceptance.",
          "A letter has to reach the IRS, and you still owe the tax on the original schedule.",
          "Missing information is usually smaller than it seems. The IRS lists only three things to have ready: your EIN, each VIN, and each truck's taxable gross weight — see the [Form 2290 checklist](/blog/form-2290-client-checklist).",
        ],
      },
      {
        type: "p",
        text: "Not sure about the weight? [Taxable gross weight explained](/blog/taxable-gross-weight-form-2290) walks through it, and the [tax calculator](/form-2290-tax-calculator) shows what you owe before you start.",
      },
      { type: "h2", text: "If the deadline has already passed" },
      {
        type: "p",
        text: "File as soon as you can. If you then get a penalty and believe you had reasonable cause, the IRS says to send a letter explaining why, or see IRS.gov/PenaltyRelief — and not to attach the explanation to the return itself. More in [late Form 2290 filing](/blog/form-2290-late-filing-penalties).",
      },
    ],
    faqs: [
      {
        q: "Can I get an extension on Form 2290?",
        a: "Yes. Before the due date, write to the IRS at 7940 Kentucky Drive, Florence, KY 41042-2915 and fully explain the cause of the delay. Except for taxpayers abroad, the extension is for no more than 6 months.",
      },
      {
        q: "Does a Form 2290 extension give me more time to pay?",
        a: "No. An extension of time to file doesn't extend the time to pay the tax. A request for more time to pay has to be made separately.",
      },
      {
        q: "Is there an IRS form for a 2290 extension?",
        a: "The instructions describe a written request — a letter — sent before the due date, not a separate form.",
      },
      {
        q: "Will an extension let me register my truck?",
        a: "No. States generally require the stamped Schedule 1 as proof of payment, which you only get once a Form 2290 is filed and accepted.",
      },
    ],
  },

  // CANONICAL TARGET: "form 2290 mailing address" / "where to mail form 2290"
  // (Search Console 2026-10-07: "form 2290 mailing address" at ~67). Gives the
  // three IRS addresses exactly, then makes the e-file case.
  {
    slug: "form-2290-mailing-address-paper-filing",
    category: "basics",
    title: "Form 2290 Mailing Address: Where to Mail a Paper Return (and When to E-File Instead)",
    metaTitle: "Form 2290 Mailing Address | Where to Mail Form 2290",
    metaDescription:
      "Where to mail a paper Form 2290: the three IRS addresses by payment type, private delivery rules, and why 25+ trucks must e-file. Or e-file and skip the mail.",
    keywords: [
      "form 2290 mailing address",
      "where to mail form 2290",
      "paper form 2290",
      "form 2290 paper vs e-file",
      "irs address for form 2290",
      "mail form 2290",
    ],
    excerpt:
      "A paper Form 2290 goes to one of three IRS addresses, depending on how you pay. Here they are exactly as the IRS lists them — plus who isn't allowed to file on paper, and how much faster e-filing gets you a Schedule 1.",
    cta: {
      heading: "Skip the envelope",
      text: "E-file online and download the stamped Schedule 1 once the IRS accepts your return.",
      label: "E-file Form 2290",
      to: "/wizard/1",
    },
    publishedAt: "2026-10-07",
    updatedAt: "2026-10-07",
    readingMinutes: 5,
    irsVerified: true,
    sources:
      "IRS Instructions for Form 2290 (Rev. July 2026) — How To File, Where To File, Private Delivery Services, How To Pay the Tax (EFTPS, card, check or money order, international payments), Schedule 1",
    body: [
      {
        type: "p",
        text: "You can still file [Form 2290](/blog/what-is-form-2290) on paper. If you do, the address depends on one thing: how you are paying the tax.",
      },
      { type: "h2", text: "The three IRS mailing addresses" },
      {
        type: "table",
        headers: ["If you are mailing…", "Send it to"],
        rows: [
          [
            "Form 2290 with full payment, not drawn from an international financial institution",
            "Internal Revenue Service, P.O. Box 932500, Louisville, KY 40293-2500",
          ],
          [
            "Form 2290 with no payment due, or paid through EFTPS or by credit/debit card",
            "Department of the Treasury, Internal Revenue Service, Ogden, UT 84201-0031",
          ],
          [
            "Form 2290 with a check or money order drawn from an international financial institution",
            "Internal Revenue Service, International Accounts, 1973 Rulon White Blvd., Ogden, UT 84201-0038",
          ],
        ],
      },
      {
        type: "p",
        text: "If you pay by check or money order, include Form 2290-V, the payment voucher, and make the payment out to \"United States Treasury\". Don't send cash, and don't staple the payment to the voucher or the return. The [ways to pay Form 2290 tax](/blog/how-to-pay-form-2290) covers each method.",
      },
      { type: "h2", text: "Using FedEx, UPS, or another private carrier" },
      {
        type: "p",
        text: "Certain IRS-designated private delivery services count for the \"timely mailing as timely filing/paying\" rule. The current list is at IRS.gov/PDS. If you use one, send it to the Ogden Processing Center street address listed at IRS.gov/PDSstreetAddresses.",
      },
      {
        type: "callout",
        tone: "warn",
        text: "Private delivery services can't deliver to P.O. boxes. Anything going to an IRS P.O. box — like the Louisville address above — has to go by U.S. Postal Service.",
      },
      { type: "h2", text: "Who isn't allowed to mail it" },
      {
        type: "p",
        text: "Electronic filing is required for each return reporting and paying tax on 25 or more vehicles. Suspended category W vehicles aren't counted toward the 25, because no tax is paid on them. Above that line, a paper return isn't an option — fleets usually [upload the vehicle list from a spreadsheet](/blog/bulk-form-2290-filing-for-fleets).",
      },
      {
        type: "cta",
        heading: "Rather not wait on the mail?",
        text: "E-filing takes one sitting. Set up your business once and every later return starts from it.",
        label: "Create your free account",
        to: "/login?tab=up",
      },
      { type: "h2", text: "Paper vs. e-file: what actually changes" },
      {
        type: "table",
        headers: ["", "Paper", "E-file"],
        rows: [
          ["Allowed for 25+ taxable vehicles", "No", "Yes"],
          [
            "Stamped Schedule 1",
            "IRS stamps the second copy and returns it to you",
            "Can be available within minutes after IRS acceptance",
          ],
          [
            "Payment options",
            "Check or money order with 2290-V, EFTPS, or card",
            "Electronic funds withdrawal, EFTPS, or card",
          ],
        ],
      },
      {
        type: "p",
        text: "The Schedule 1 timing is the part that matters most, because states generally won't register a heavy vehicle without it. See [using Schedule 1 for DMV registration](/blog/schedule-1-for-dmv-registration).",
      },
      { type: "h2", text: "If you do file on paper" },
      {
        type: "ul",
        items: [
          "Complete and file both copies of Schedule 1. The second copy is the one stamped and returned to you. A return can be rejected if Schedule 1 isn't attached.",
          "Sign the return. An unsigned return isn't considered filed.",
          "If you paid through EFTPS or by card, check the matching box on line 6 and use the Ogden \"no payment due\" address.",
          "Mail it early enough to meet your deadline — the [due date calculator](/form-2290-due-date-calculator) shows it for your month.",
        ],
      },
    ],
    faqs: [
      {
        q: "Where do I mail Form 2290 with a payment?",
        a: "If you are paying with the return and the payment isn't drawn from an international financial institution: Internal Revenue Service, P.O. Box 932500, Louisville, KY 40293-2500. Include Form 2290-V with a check or money order.",
      },
      {
        q: "Where do I mail Form 2290 if I paid by EFTPS or card?",
        a: "Department of the Treasury, Internal Revenue Service, Ogden, UT 84201-0031 — the same address used when no payment is due.",
      },
      {
        q: "Can I send Form 2290 by FedEx or UPS?",
        a: "Only through an IRS-designated private delivery service, to the Ogden Processing Center street address at IRS.gov/PDSstreetAddresses. Private carriers can't deliver to P.O. boxes.",
      },
      {
        q: "Do I have to e-file Form 2290?",
        a: "Yes, if the return reports and pays tax on 25 or more vehicles. Suspended category W vehicles don't count toward the 25.",
      },
    ],
  },

  // CANONICAL TARGET: "schedule 1 for dmv registration" / "get copy of schedule
  // 1". stamped-schedule-1-proof-of-payment owns "what is a stamped Schedule
  // 1"; this one owns the registration scenarios and replacement copies.
  {
    slug: "schedule-1-for-dmv-registration",
    category: "schedule1",
    title: "Schedule 1 for DMV Registration: What Your State Will Accept, and How to Get a Copy",
    metaTitle: "Schedule 1 for DMV Registration | Form 2290 Proof",
    metaDescription:
      "What counts as Form 2290 proof at the DMV: the stamped Schedule 1, last period's copy in July–September, the 60-day bill of sale rule, and getting a copy.",
    keywords: [
      "schedule 1 for dmv registration",
      "2290 schedule 1 for registration",
      "get copy of schedule 1",
      "form 2290 proof of payment dmv",
      "schedule 1 copy",
      "2290 proof for registration",
    ],
    excerpt:
      "The DMV wants proof the heavy vehicle use tax is paid. Usually that is the stamped Schedule 1 — but there are a few situations where something else works, and a few where it doesn't.",
    cta: {
      heading: "Need a Schedule 1 for the DMV?",
      text: "File online and download the stamped Schedule 1 once the IRS accepts the return.",
      label: "File Form 2290 now",
      to: "/wizard/1",
    },
    publishedAt: "2026-10-07",
    updatedAt: "2026-10-07",
    readingMinutes: 6,
    irsVerified: true,
    sources:
      "IRS Instructions for Form 2290 (Rev. July 2026) — Schedule 1 (E-file; prior-period copy request; Proof of payment for state registration and entry into the United States; alternate proof of payment program; Consent to Disclosure); Reminders (U.S. Customs and Border Protection)",
    body: [
      {
        type: "p",
        text: "Most heavy-truck owners meet [Form 2290](/blog/what-is-form-2290) at the DMV counter. States generally require proof the tax is paid before they will register a taxable vehicle, and the standard proof is the [stamped Schedule 1](/blog/stamped-schedule-1-proof-of-payment). This guide covers the registration situations around it.",
      },
      { type: "h2", text: "The standard case: the stamped Schedule 1" },
      {
        type: "p",
        text: "Use the copy of Schedule 1 stamped and returned to you by the IRS as proof of payment when you register with the state. On an e-filed return it carries an IRS watermark and can be available within minutes of acceptance.",
      },
      {
        type: "p",
        text: "Check the VINs before the DMV does: the IRS warns that leaving out the full VIN may prevent you from registering the vehicle. A mistyped VIN is fixed with a [VIN correction](/blog/form-2290-vin-correction), not a new return.",
      },
      { type: "h2", text: "Registering in July, August, or September" },
      {
        type: "p",
        text: "The new tax period starts July 1, but the July return isn't due until August 31. To bridge that, the IRS says: if the state receives your registration application in July, August, or September, you may give it the previous tax period's approved Schedule 1 that the IRS returned to you.",
      },
      {
        type: "callout",
        tone: "info",
        text: "This only covers the registration. You still have to file the current period's Form 2290 by its due date — see the [due date calculator](/form-2290-due-date-calculator).",
      },
      { type: "h2", text: "Just bought the truck: the 60-day rule" },
      {
        type: "p",
        text: "No proof of payment is required for a recently purchased new or used vehicle if you give the state a copy of the bill of sale showing it was bought within the last 60 days. You still have to file a return and pay any tax due. Buying used? See [Form 2290 for a used truck purchase](/blog/form-2290-used-truck-purchase).",
      },
      {
        type: "cta",
        heading: "Bought a truck this month?",
        text: "Add it to a new return for the month you first drove it, and get a Schedule 1 that lists its VIN.",
        label: "Create your free account",
        to: "/login?tab=up",
      },
      { type: "h2", text: "No stamped copy?" },
      {
        type: "p",
        text: "If you don't have the stamped copy, the IRS says you may use a photocopy of the Form 2290 (with Schedule 1 attached) that you filed with the IRS, plus a photocopy of both sides of the canceled check, as proof of payment.",
      },
      { type: "h2", text: "Letting the DMV forward your return" },
      {
        type: "p",
        text: "A limited number of states take part in an alternate proof of payment program with the IRS. There, the DMV may forward your Form 2290 (with voucher and payment) to the IRS, and no further proof is needed to register. Ask your local DMV whether your state participates.",
      },
      {
        type: "callout",
        tone: "warn",
        text: "The risk is yours: if you give the DMV your return to forward, it isn't considered filed until the IRS receives it, and you are responsible for any penalties or interest if it is filed late or lost by the DMV.",
      },
      { type: "h2", text: "Crossing from Canada or Mexico" },
      {
        type: "p",
        text: "U.S. Customs and Border Protection also requires this proof of payment for a Canadian or Mexican vehicle entering the United States. See [Form 2290 for Canadian and Mexican trucks](/blog/form-2290-canadian-mexican-trucks).",
      },
      { type: "h2", text: "Getting another copy" },
      {
        type: "ul",
        items: [
          "E-filed: the IRS sends the watermarked Schedule 1 electronically to the e-file provider, so ask the provider. If you filed with us, it stays in My Filings and can be downloaded again once the return is accepted.",
          "A prior-period Schedule 1: the IRS says to send a written request to Internal Revenue Service, 7940 Kentucky Drive, Florence, KY 41042-2915.",
        ],
      },
      { type: "h2", text: "Why the IRS asks for your consent" },
      {
        type: "p",
        text: "Schedule 1 includes a Consent to Disclosure. If you sign it, the IRS can share the VINs on your Schedule 1 and confirmation that you paid the tax with the Department of Transportation, U.S. Customs and Border Protection, and state DMVs.",
      },
    ],
    faqs: [
      {
        q: "What does the DMV need from Form 2290 to register my truck?",
        a: "Generally the stamped Schedule 1 the IRS returned to you. On an e-filed return it carries an IRS watermark.",
      },
      {
        q: "Can I register in July with last year's Schedule 1?",
        a: "Yes. If the state receives your registration application in July, August, or September, you may give it the previous period's approved Schedule 1. You still have to file the current period's return by its due date.",
      },
      {
        q: "I just bought my truck. Do I need a Schedule 1 to register it?",
        a: "Not if you give the state a bill of sale showing it was bought within the last 60 days. You still have to file a Form 2290 and pay any tax due.",
      },
      {
        q: "How do I get a copy of an old Schedule 1?",
        a: "For a prior period, send a written request to Internal Revenue Service, 7940 Kentucky Drive, Florence, KY 41042-2915. For an e-filed return, ask your e-file provider for the electronic copy.",
      },
    ],
  },

  // CANONICAL TARGET: "form 2290 final return" / "stop filing 2290". IRS text
  // ("check the Final Return box, sign the return, and mail it") is quoted
  // exactly; the product offers a final-return option in the e-file flow
  // (FormTypeStep, FinalReturnInd) - see note in the post.
  {
    slug: "form-2290-final-return",
    category: "basics",
    title: "Form 2290 Final Return: What to File When You No Longer Have Trucks to Report",
    metaTitle: "Form 2290 Final Return | Stop Filing Form 2290",
    metaDescription:
      "Sold your last truck or closing your trucking business? When to file a final Form 2290, how to claim credits for trucks you sold, and which records to keep.",
    keywords: [
      "form 2290 final return",
      "closing trucking business 2290",
      "stop filing 2290",
      "final form 2290",
      "2290 out of business",
      "sold last truck form 2290",
    ],
    excerpt:
      "When you no longer have vehicles to report, the IRS asks for a final Form 2290 so it stops expecting one. Here is what the instructions say, what to claim on the way out, and what to keep.",
    cta: {
      heading: "Closing out your Form 2290?",
      text: "Mark the return as your final return, claim credits for trucks you sold, and keep a copy of everything.",
      label: "Start a final return",
      to: "/wizard/1",
    },
    publishedAt: "2026-10-07",
    updatedAt: "2026-10-07",
    readingMinutes: 5,
    irsVerified: true,
    sources:
      "IRS Instructions for Form 2290 (Rev. July 2026) — Name and Address (Final return), Signature, Line 5 (credits; information to be submitted; When to make a claim), Line 9 (sales of suspended vehicles), Recordkeeping",
    body: [
      {
        type: "p",
        text: "If you sell your last truck, park the business, or otherwise stop operating taxable heavy vehicles, you stop owing [Form 2290](/blog/what-is-form-2290) tax. But the IRS also wants to know you are done, so it doesn't keep expecting a return.",
      },
      { type: "h2", text: "What the IRS instructions say" },
      {
        type: "p",
        text: "In the IRS's words: \"If you no longer have vehicles to report, file a final return. Check the Final Return box on Form 2290, sign the return, and mail it to the IRS.\"",
      },
      {
        type: "p",
        text: "Two things in that sentence matter. It applies when you no longer have vehicles to report — not when one truck of several is sold. And the return has to be signed: the IRS says an unsigned return isn't considered filed.",
      },
      {
        type: "callout",
        tone: "info",
        text: "You don't have to mail it. The IRS e-file schema for Form 2290 has its own Final Return indicator, so a final return can be e-filed like any other Form 2290 — the instructions' \"mail it\" describes the paper route. Choose \"This is my final return\" on the return type step, and the return is transmitted with that box checked.",
      },
      { type: "h2", text: "Claim what you are owed on the way out" },
      {
        type: "p",
        text: "If you paid tax on a truck for this period and then sold it, the tax for the remaining months can come back. A vehicle sold, destroyed, or stolen before June 1 and not used for the rest of the period qualifies for a credit on the next Form 2290 you file, or a refund on Form 8849.",
      },
      {
        type: "p",
        text: "For each truck, you'll need the VIN, its weight category, the date of the sale (or destruction or theft), the credit worksheet, and — for a sale — the buyer's name and address. The worked example is in [claiming the credit for a sold, destroyed, or stolen truck](/blog/form-2290-credit-sold-destroyed-stolen-truck).",
      },
      {
        type: "p",
        text: "One limit: the credit on Form 2290 can't be more than the tax on that return. If the return has little or no tax on it, the excess is claimed as a refund on Form 8849 with Schedule 6. The [credits and refunds guide](/blog/form-2290-credits-and-refunds) covers both routes.",
      },
      {
        type: "cta",
        heading: "Sold your trucks this period?",
        text: "Add each one in the credits step; the credit is figured from the IRS partial-period table.",
        label: "Create your free account",
        to: "/login?tab=up",
      },
      { type: "h2", text: "If you sold a suspended truck" },
      {
        type: "p",
        text: "If a truck you reported as suspended (category W) is sold, you must give the buyer a statement showing your name, address, and EIN; the VIN; the sale date; the odometer reading at the start of the period and at the sale; and the buyer's name, address, and EIN. If you don't, you can remain liable for the tax if the truck later goes over the mileage limit. More in [suspended vehicles and category W](/blog/suspended-vehicles-category-w).",
      },
      { type: "h2", text: "Records to keep after you stop" },
      {
        type: "p",
        text: "Closing the business doesn't end the recordkeeping. Keep records for every taxable vehicle registered in your name for at least 3 years after the tax is due or paid, whichever is later — even if a vehicle was in your name for only part of a period. For suspended vehicles, keep the records at least 3 years after the end of the period the suspension covers. For each truck, the records should show how and when you disposed of it, including the buyer's name and address if it was sold.",
      },
      { type: "h2", text: "Starting again later" },
      {
        type: "p",
        text: "If you put a taxable truck back on the road, you file again for the month it is first used — the [Form 2290 due date calculator](/form-2290-due-date-calculator) shows the deadline.",
      },
    ],
    faqs: [
      {
        q: "When do I file a final Form 2290?",
        a: "When you no longer have vehicles to report. Check the Final Return box and sign the return. On paper you mail it to the IRS; the IRS e-file format also carries a Final Return indicator, so it can be e-filed instead.",
      },
      {
        q: "Can I get money back for trucks I sold before closing?",
        a: "Yes, if a truck was sold before June 1 and not used for the rest of the period. Claim a credit on your next Form 2290 or a refund on Form 8849, with the VIN, weight category, sale date, worksheet, and the buyer's name and address.",
      },
      {
        q: "How long do I keep Form 2290 records after closing my trucking business?",
        a: "At least 3 years after the tax is due or paid, whichever is later, and for suspended vehicles at least 3 years after the end of the period the suspension covers.",
      },
      {
        q: "Do I need to file a final return if I sold just one truck?",
        a: "No. A final return is for when you no longer have vehicles to report. Selling one truck of several is handled with a credit on your next return.",
      },
    ],
  },
];

export const getPost = (slug) => BLOG_POSTS.find((p) => p.slug === slug);


// Newest first, which is also the order the blog index renders.
export const sortedPosts = () =>
  [...BLOG_POSTS].sort((a, b) => (a.publishedAt < b.publishedAt ? 1 : -1));

// Same category, excluding self. Internal linking is a ranking factor and the
// publish checklist in SEO_CMS_SPEC.md warns on posts with fewer than 2 internal
// links - this guarantees every post has some.
export const relatedPosts = (slug, limit = 3) => {
  const post = getPost(slug);
  if (!post) return [];
  const sameCategory = BLOG_POSTS.filter((p) => p.slug !== slug && p.category === post.category);
  const others = BLOG_POSTS.filter((p) => p.slug !== slug && p.category !== post.category);
  return [...sameCategory, ...others].slice(0, limit);
};
